import React, { useState, useEffect, useRef } from 'react';
import * as XLSX from 'xlsx';
import { collection, query, where, getDocs, addDoc, doc, getDoc, setDoc, updateDoc, deleteDoc, deleteField, Timestamp, runTransaction, orderBy, serverTimestamp } from 'firebase/firestore';
import { auth, db } from '../firebase/config';
import { createNotification } from '../services/NotificationSystem';
import FlowBar from './common/FlowBar';
import AgentStatusBanner from './common/AgentStatusBanner';
import { Search, Edit2, Trash2, Plus, Save, Printer, FileDown, X, Download, Upload, Send, FileText } from 'lucide-react';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { ko } from 'date-fns/locale';
import {
    getTimestamp, normKey, findPriceMatch, normalizePriceItems,
    generateStatementNumber, buildItemsFromOrders, serializeStatementItems,
    priceItemCategory, canonKey, remakeOfLabel, cleanRemakeOf, remakeNoteKeyOf, REMAKE_LIABILITIES,
} from '../utils/statementItems';
import { displayPatientName } from '../utils/patientName';
import { fetchDomesticBank } from '../utils/domesticBank';
import { normMaterialToken, isMaterialSubset } from '../utils/shared/material-alias';
// 케이스 통합 뷰 — 1단계는 섀도 비교 전용(화면 출력 불변). docs/기획서_케이스중심통일_2026-08.md
import { mergeCases, ordersOf, diffItems, logShadowDiff } from '../utils/caseView';
import { loadCasesForClinic } from '../utils/caseViewLoad';
import { getFeatures, isCaseUnifyRead, isCaseUnifyDisplay, isCaseUnifyWrite } from '../utils/features';
import PriceListModal from './PriceListModal';
import { designSrcOf, designStateOf, designBadgeFromSaved, DesignBadge } from '../utils/designState';   // 디자인 완료/전 배지 (2026-09-06)
// 거래명세서 1단계 (2026-10-23) — 청구 마감일·받을 돈·전월 미수 (utils는 dev A 소유, 여기서는 import만)
import { getLabBilling, partnerKeyOf as ledgerPartnerKeyOf, lastClosedPeriod, isMemberPartnerKey } from '../utils/billingCycle';   // 로컬 partnerKeyOf(이름 정규화)와 뜻이 달라 별칭
import { loadReceivables, computeCarryOver, invalidateReceivables } from '../utils/receivables';
import { restoreCasesDroppedFromStatement } from '../utils/caseRestore';   // 저장·발행 때 줄에서 빠진 케이스 되돌림 (2026-10-24 재점검 P1-2)
import { invalidateSummaryCache } from './statement/SummaryCards';   // 발행 뒤 목록 카드 다시 계산
import { isLabStaff, isClinicUser } from '../utils/excelExport';
import RemakeLinkModal from './statement/RemakeLinkModal';   // 리메이크 원 건 연결 창 (2026-10-24 2단계 ④)

// 날짜 계산 헬퍼 함수들
const formatDateHelper = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
};

const getToday = () => {
    const today = new Date();
    return {
        start: formatDateHelper(today),
        end: formatDateHelper(today)
    };
};

const getThisWeek = () => {
    const today = new Date();
    const dayOfWeek = today.getDay();
    const monday = new Date(today);
    monday.setDate(today.getDate() - (dayOfWeek === 0 ? 6 : dayOfWeek - 1));
    const sunday = new Date(monday);
    sunday.setDate(monday.getDate() + 6);
    return {
        start: formatDateHelper(monday),
        end: formatDateHelper(sunday)
    };
};

const getThisMonth = () => {
    const today = new Date();
    const firstDay = new Date(today.getFullYear(), today.getMonth(), 1);
    const lastDay = new Date(today.getFullYear(), today.getMonth() + 1, 0);
    return {
        start: formatDateHelper(firstDay),
        end: formatDateHelper(lastDay)
    };
};

const getLastMonth = () => {
    const today = new Date();
    const firstDay = new Date(today.getFullYear(), today.getMonth() - 1, 1);
    const lastDay = new Date(today.getFullYear(), today.getMonth(), 0);
    return {
        start: formatDateHelper(firstDay),
        end: formatDateHelper(lastDay)
    };
};

const getLast7Days = () => {
    const today = new Date();
    const sevenDaysAgo = new Date(today);
    sevenDaysAgo.setDate(today.getDate() - 6);
    return {
        start: formatDateHelper(sevenDaysAgo),
        end: formatDateHelper(today)
    };
};

const getLast30Days = () => {
    const today = new Date();
    const thirtyDaysAgo = new Date(today);
    thirtyDaysAgo.setDate(today.getDate() - 29);
    return {
        start: formatDateHelper(thirtyDaysAgo),
        end: formatDateHelper(today)
    };
};

// 법무 P0 — 외주(kind='design_outsource') 명세서 표시 품목명에 "CAD 디자인" 접두 강제.
// 완제품 보철물명 단독 표기 금지(의료기사법 방어). 표시 필드(toothInfo)에만 붙이고 매칭 키(serviceName)는 손대지 않는다.
// 멱등 — 이미 접두가 있으면(재저장·이어쓰기) 중복으로 붙이지 않는다.
const CAD_DESIGN_PREFIX = 'CAD 디자인 - ';
const withCadDesignPrefix = (toothInfo) => {
    const t = (toothInfo || '').trim();
    if (!t) return 'CAD 디자인';   // 치식 없는 라인도 서비스 성격 명시
    if (t.startsWith('CAD 디자인')) return t;   // 이미 붙음(멱등)
    return CAD_DESIGN_PREFIX + t;
};

// ── 골드(금값) 파생 행 (2026-08-20 대표 확정 — 기획서 docs/기획서_명세서_골드입력_개선_2026-08.md 2부) ──
// 금값 = 부모 품목(공임) 행 아래 별도 파생 행(itemType 'gold'). 개수1 × 단가(중량×시세)라서
// 기존 합계·앱·세금계산서 계산식(개수×개당가격)을 전부 무수정 통과한다.
// 대표 확정: 시세 단위 원/g / 금값=면세(기공소↔치과) / 합금 종류·치과 제공 금은 메모(비고)에 텍스트 기재.
const GOLD_RATE_LS_KEY = 'dcGoldRateLast';   // 최근 입력 시세 기억 (기기 로컬)
const loadLastGoldRate = () => {
    try {
        const v = JSON.parse(localStorage.getItem(GOLD_RATE_LS_KEY) || 'null');
        return v && Number(v.pricePerGram) > 0 ? Number(v.pricePerGram) : 0;
    } catch { return 0; }
};
const saveLastGoldRate = (pricePerGram) => {
    try { localStorage.setItem(GOLD_RATE_LS_KEY, JSON.stringify({ pricePerGram, updatedAt: Date.now() })); } catch { /* 로컬 기억 실패는 무시 (다음 입력만 수동) */ }
};
const goldLineLabel = (weight, rate) =>
    `금값 (${Number(weight) || 0}g × ${(Number(rate) || 0).toLocaleString('ko-KR')}원/g)`;
const goldLineAmount = (weight, rate) => Math.round((Number(weight) || 0) * (Number(rate) || 0));

// 치아 정보에서 치아 개수 파싱
// "#" 뒤의 숫자만 치아로 인식 — "4.5mm", "3본" 같은 규격 숫자가 개수에 섞이는 것 방지
const parseToothCount = (toothInfo) => {
    if (!toothInfo) return 0;

    const hashPart = toothInfo.match(/#\s*([\d,\-~.\s]+)/);
    let source = hashPart ? hashPart[1] : toothInfo;

    // 범위 표기 전개: "11-13" → "11,12,13" (같은 분면 내에서만)
    source = source.replace(/(\d{2})\s*[-~]\s*(\d{2})/g, (m, a, b) => {
        const from = parseInt(a, 10), to = parseInt(b, 10);
        if (Math.floor(from / 10) !== Math.floor(to / 10) || from > to) return m;
        const expanded = [];
        for (let n = from; n <= to; n++) expanded.push(n);
        return expanded.join(',');
    });

    const matches = source.match(/\d{1,2}/g);
    if (!matches) return 0;

    // 중복 제거 후 개수 반환
    const uniqueTeeth = [...new Set(matches)];
    return uniqueTeeth.length;
};

// 단가표 매칭/항목 생성 로직은 ../utils/statementItems.js 공용 모듈 사용

// ── 청구 키: 같은 거래처 안에서 "같은 환자 · 같은 치식 · 같은 종류(보철/임플란트)"를 한 건으로 보는 열쇠 (2026-09-09 대표 승인)
//   치과가는날 8월분 실측: 7월에 웹 의뢰서로 청구한 송명진 #36이 8월 수집분으로 다시 청구됨 — 문서 id(orderId·parsedCaseId)가
//   달라 기존 '이미 발행됨' 표시가 못 잡았다. 이름은 [리메이크]·(리메이크)·숫자·공백을 벗긴 뒤 비교, 치식 없는 행은 키 없음.
const billKeyOf = (it) => {
    const name = String(it?.patientName || '')
        .replace(/\[.*?\]|\(.*?\)/g, '')
        .replace(/리메이크|재제작|수리|remake/gi, '')
        .replace(/[\s\d\-_.()[\]]/g, '').toLowerCase();
    const teeth = [...new Set(String(it?.toothInfo || '').match(/\d{2}/g) || [])].sort().join(',');
    if (!name || !teeth) return null;
    const kind = /임플|어버트|abut|implant/i.test(`${it?.serviceName || ''} ${it?.toothInfo || ''}`) ? 'imp' : 'pros';
    return `${name}|${teeth}|${kind}`;
};
const partnerKeyOf = (idOrName) => String(idOrName || '').replace(/\s|\(.*?\)/g, '').replace(/치과의원|치과병원|치과$/g, '').toLowerCase();

// autoDraft (2026-10-23 1단계 §3-3, §2-3): 목록에서 넘어온 자동 초안 지시 — 화면을 연 뒤 [담기]와 같은 경로로 수집분을 담고 바로 임시저장한다.
//   { mode: 'closing'|'unbilled', partnerKey, partnerName, from, to, label?, closeDay?, collectFrom?, collectTo? }
//   closing = 마감 초안(문서에 closingDraft 표식) / unbilled = 청구 안 된 작업 [이번 초안에 넣기](표식 없음). 발행은 자동으로 하지 않는다.
function CreateTransactionStatement({ user, onBack, statementId, initialClinicId, initialFrom, initialTo, initialOpenPrice, correctFromId, autoDraft }) {
    // 기존 문서에서 시작하는 진입 (이어쓰기·발행본 수정 = statementId / 정정 발행 = correctFromId, 2026-10-23)
    const startsFromDoc = !!(statementId || correctFromId);
    // 검색 조건 (마감 현황 보드/수집함에서 넘어온 거래처·기간은 미리 선택 — draft 이어쓰기는 draft에서 로드)
    const [selectedClinic, setSelectedClinic] = useState(startsFromDoc ? '' : (initialClinicId || ''));
    const [manualClinicName, setManualClinicName] = useState('');
    const [startDate, setStartDate] = useState(startsFromDoc ? '' : (initialFrom || ''));
    const [endDate, setEndDate] = useState(startsFromDoc ? '' : (initialTo || ''));

    // 거래처 목록
    const [clinics, setClinics] = useState([]);

    // 조회된 의뢰서들
    const [loading, setLoading] = useState(false);

    // 편집 가능한 항목들 (보철물별로 분리)
    const [items, setItems] = useState([]);

    // 총 할인
    const [totalDiscount, setTotalDiscount] = useState(0);
    const [discountType, setDiscountType] = useState('amount');

    // 메모
    const [notes, setNotes] = useState('');

    // 항상 편집 가능 (별도 편집 모드 토글 제거)
    const [isEditMode, setIsEditMode] = useState(true);

    // 임시저장 이어쓰기: 기존 draft 문서 ID (있으면 저장 시 업데이트)
    const [currentStatementId, setCurrentStatementId] = useState(statementId || null);
    const loadedClinicRef = React.useRef(null);          // 거래처 변경 감지 (draft 덮어쓰기 방지)
    // (2026-08-05 대표 재현 버그 수정) 이어쓰기 재로드 병합이 "지운 행"을 되살리던 문제:
    // 병합(2026-07-30)이 "현재 항목에 없는 기간 내 의뢰서 전부"를 새 수집분으로 판단해
    // 사용자가 삭제한 행까지 다시 추가했다. draft가 한 번이라도 품었던 의뢰서 id를 기억해 두고
    // (저장 시 seenOrderIds로 영속) 그 안에 없는 진짜 신규만 추가한다.
    const seenOrderIdsRef = React.useRef(new Set());
    // 이 draft에서 [수집 취소]한 수집 건 — 재로드 시 자동등록으로 새 의뢰서 id가 생겨도 다시 안 붙게
    const canceledParsedCaseIdsRef = React.useRef(new Set());
    const autoRegisterInFlight = React.useRef(false);    // 자동등록 중복 실행 가드 (이중 청구 방지)
    const [savedStatementNumber, setSavedStatementNumber] = useState(null);

    // 정렬 기준 ('createdAt' 또는 'dueDate')
    const [sortBy, setSortBy] = useState('createdAt');

    // 조회 날짜 기준 ('createdAt' 작성일 / 'dueDate' 마감일)
    const [dateBasis, setDateBasis] = useState('design');   // 10-31: 늘 디자인 완성일 기준(고르기 없앰). 옛 명세서를 열면 저장값이 들어올 수 있으나 조회는 디자인 완성일로만 한다
    // 기간 프리셋 (리디자인 2026-07-19): 칩 클릭 = 기간 설정 + 즉시 조회
    // 마감 초안 자동 진입이면 '청구 마감 기간' 칩, [이번 초안에 넣기]면 직접 지정(그 달)으로 시작 (2026-10-23)
    const [activePreset, setActivePreset] = useState(autoDraft?.mode === 'closing' ? 'closing' : (autoDraft && !statementId ? 'custom' : 'thisMonth'));
    // 기간 칩 '더 보기' 펼침 — 이번 주·오늘·7일·30일을 접어 둠 (2026-10-23 §5-2, UI만)
    const [moreChipsOpen, setMoreChipsOpen] = useState(false);

    // 저장 중 상태
    const [saving, setSaving] = useState(false);

    // 단가표 관련 state
    const [priceList, setPriceList] = useState([]);

    // 에이전트 자동 수집 항목 (parsedCases, status=pending)
    const [parsedCases, setParsedCases] = useState([]);
    const [parsedChecked, setParsedChecked] = useState({});   // id -> bool
    const [parsedFrom, setParsedFrom] = useState('');          // 수집 항목 날짜 필터 (fileMtime 기준)
    const [parsedTo, setParsedTo] = useState('');
    const [showPriceListModal, setShowPriceListModal] = useState(false);
    const [priceLoadedClinic, setPriceLoadedClinic] = useState('');   // 단가표 로드 완료된 거래처 (자동 열기 신호)
    const priceAutoOpened = useRef(false);
    const [searchedOrders, setSearchedOrders] = useState([]);   // 조회된 원본 의뢰서 (의뢰서 확인 모달용)
    const [orderModal, setOrderModal] = useState(null);         // 열려 있는 의뢰서 상세

    // 종이 의뢰서 대조 체크 (2026-07-30 사장 요청) — item.id -> bool.
    // 화면에서만 쓰는 임시 표시라 서버에 저장하지 않는다 (새로고침하면 풀림)
    const [verifiedPatients, setVerifiedPatients] = useState({});
    // 거래 내역 환자명 검색 (2026-08-05 대표 요청) — 화면 표시 필터일 뿐, 합계·저장·발행은 전체 항목 기준
    const [patientSearch, setPatientSearch] = useState('');

    // 이어쓰기 병합 안내 (2026-07-30) — draft 저장 후 새로 명세화된 수집분을 추가했을 때 표시
    const [mergeNotice, setMergeNotice] = useState('');

    // ── 디자인 완료/전 배지 (2026-09-06 대표 지시) ──
    // 근거 우선순위: 항목에 붙은 _designSrc(buildItemsFromOrders가 로드 객체에서 붙임) → 조회된 의뢰서(searchedOrders) 맵
    // → 이어쓰기 복원 항목 등 어디에도 없는 건만 배치 getDoc(최대 100, 1회) → 저장된 designState → '확인 불가'
    const [extraDesignSrc, setExtraDesignSrc] = useState({});   // { [orderId|parsedCaseId]: src|null }
    const designFetchedRef = useRef(new Set());
    const designSrcMap = React.useMemo(() => {
        const m = {};
        (searchedOrders || []).forEach(o => {
            const src = designSrcOf(o);
            if (!src) return;
            [o.id, o.parsedCaseId, o.caseId].filter(Boolean).forEach(k => { if (!m[k]) m[k] = src; });
        });
        return m;
    }, [searchedOrders]);
    const resolveDesign = (item) => {
        if (!item || item.itemType === 'gold') return null;
        const oid = item.orderId ? String(item.orderId) : '';
        const keys = [oid, item.parsedCaseId, oid.startsWith('case:') ? oid.slice(5) : ''].filter(Boolean);
        const src = item._designSrc || keys.map(k => designSrcMap[k] || extraDesignSrc[k]).find(Boolean) || null;
        if (src) return designStateOf(src);
        // 근거 없음(수기·연결 안 됨·아직 조회 전) — 저장된 값이 있으면 그것, 없으면 '확인 불가'
        return designBadgeFromSaved(item.designState) || designStateOf(null);
    };
    // 저장 직전 항목에 designState 3값 기록 (금값 행 제외 — 직렬화가 키를 쓰지 않음)
    const withDesignState = (list) => list.map(it => it.itemType === 'gold' ? it : { ...it, designState: (resolveDesign(it) || {}).state || 'unknown' });
    useEffect(() => {
        // 근거가 없는 항목(이어쓰기 복원·발행본 편집 등)만 원문서에서 1회 읽는다 — 최대 100건, 실패는 null로 기록해 재시도 폭주 방지
        const want = [];
        for (const it of items) {
            if (it.itemType === 'gold' || it._designSrc || it.source === 'manual') continue;
            const oid = it.orderId ? String(it.orderId) : '';
            const cands = [];
            if (oid && !oid.startsWith('case:')) cands.push({ col: 'workOrders', id: oid });
            const cid = it.parsedCaseId || (oid.startsWith('case:') ? oid.slice(5) : '');
            if (cid) cands.push({ col: 'parsedCases', id: cid });
            if (cands.some(c => designSrcMap[c.id])) continue;
            cands.forEach(c => {
                if (extraDesignSrc[c.id] !== undefined || designFetchedRef.current.has(c.id)) return;
                designFetchedRef.current.add(c.id);
                want.push(c);
            });
            if (want.length >= 100) break;
        }
        if (!want.length) return;
        let alive = true;
        (async () => {
            const out = {};
            await Promise.all(want.slice(0, 100).map(async (c) => {
                try {
                    const snap = await getDoc(doc(db, c.col, c.id));
                    out[c.id] = snap.exists()
                        ? designSrcOf(c.col === 'parsedCases' ? { id: `case:${c.id}`, _case: snap.data() } : { id: c.id, ...snap.data() })
                        : null;
                } catch (e) {
                    console.warn('[디자인 배지] 근거 문서 읽기 실패:', c.col, c.id, e?.code || e);   // 조용한 실패 금지
                    out[c.id] = null;
                }
            }));
            if (alive) setExtraDesignSrc(prev => ({ ...prev, ...out }));
        })();
        return () => { alive = false; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [items, designSrcMap]);

    // 이미 다른 명세서에 들어간 의뢰서 (2026-07-30 사장 요청)
    // orderId -> { number, status } — 작성란 환자명 옆에 작게 표시해 중복 청구를 눈으로 잡게 한다
    // (2026-08-26) 원본 항목(usedOrderEntries)을 두고, 지금 편집 중인 문서만 useMemo로 제외
    const [usedOrderEntries, setUsedOrderEntries] = useState([]);
    // 수집함 통합 (2026-07-30 대표 승인): 조회 기간의 수집 현황 요약 + 제외 목록
    const [collectSummary, setCollectSummary] = useState(null);   // {total, included, excludedRows, pendingIds}
    const [showExcluded, setShowExcluded] = useState(false);
    // [📥 수집분 불러오기] (2026-08-31 대표 확정 — 수집분 합산은 버튼으로만)
    const [collectPanelOpen, setCollectPanelOpen] = useState(false);
    const [collectFrom, setCollectFrom] = useState('');
    const [collectTo, setCollectTo] = useState('');
    const [collectLoading, setCollectLoading] = useState(false);

    // ── 거래명세서 1단계 (2026-10-23) ──
    // 조회(handleSearch) 완료 횟수 — 자동 초안이 "첫 조회가 끝났는지" 기다리는 신호
    const [searchTick, setSearchTick] = useState(0);
    const [usedLoaded, setUsedLoaded] = useState(null);   // 기존 명세서 사용 이력: null(로딩)|'ok'|'error'
    // 자동 초안 진행 단계: null(없음) → 'waitSearch' → 'collecting' → 'save' → 'saving' → 'done' | 'error'
    const [autoStage, setAutoStage] = useState(autoDraft ? 'waitSearch' : null);
    const [autoMsg, setAutoMsg] = useState(null);   // { kind: 'info'|'ok'|'error', text }
    // 청구 마감일(labBilling)·받을 돈(원장) — 기공소 대표만 (직원은 rules상 못 읽음)
    const [labBilling, setLabBilling] = useState(null);       // { partners } | null
    const [recvByPartner, setRecvByPartner] = useState(null); // loadReceivables().byPartner | null
    // 전월 미수 (명세 §4-5 · 계약 §1): 기본 꺼짐, 0 이하면 숨김, 발행할 때 다시 계산해 고정
    const [carryOverPreview, setCarryOverPreview] = useState(null);   // { amount, asOf } | null
    const [includeCarryOver, setIncludeCarryOver] = useState(false);

    // 상대 업체 보철물/임플란트 프리셋 + 단축어별 재료 맵
    const [partnerMaterialMap, setPartnerMaterialMap] = useState({});
    // 표준 명칭 (2026-07-30 대표 확정) — 의뢰서 선택기와 동일 어휘, 임플란트는 종류 무관 단일가
    const DEFAULT_PROSTHESIS_CHIPS = ['지르코니아 크라운', '이맥스 크라운', 'PFM 크라운', '골드 크라운', '지르코니아 인레이', '골드 인레이'];
    const DEFAULT_IMPLANT_CHIPS = ['임플란트'];
    const [partnerProsthesisPresets, setPartnerProsthesisPresets] = useState(DEFAULT_PROSTHESIS_CHIPS);
    const [partnerImplantPresets, setPartnerImplantPresets] = useState(DEFAULT_IMPLANT_CHIPS);

    useEffect(() => {
        fetchClinics();
    }, []);

    // 내 다른 명세서들이 이미 가져다 쓴 의뢰서 목록 (지금 편집 중인 문서는 제외)
    // (2026-08-26 성능) 전량 스캔 → 최근 6개월 createdAt 범위로 1회만 조회. currentStatementId가 바뀌면
    // 재조회하지 않고 아래 useMemo에서 해당 문서 항목만 제외한다.
    useEffect(() => {
        let alive = true;
        (async () => {
            try {
                const uid = user?.companyId || auth.currentUser?.uid;
                if (!uid) return;
                const sixMonthsAgo = new Date(Date.now() - 183 * 24 * 3600 * 1000);
                const snap = await getDocs(query(collection(db, 'transactionStatements'),
                    where('fromUserId', '==', uid),
                    where('createdAt', '>=', sixMonthsAgo),
                    orderBy('createdAt', 'desc')));
                const entries = [];
                snap.docs.forEach(d => {
                    const s = d.data();
                    // 살아 있는 발행본 (2026-10-23 1단계) — 자동 초안이 이미 청구된 id를 다시 담지 않게
                    const live = ['confirmed', 'published'].includes(s.status) && s.deletedBySender !== true;
                    (s.items || []).forEach(it => {
                        // 리메이크 연결 창용 원 건 요약 (2026-10-24 ④) — 이미 읽은 문서에서 날짜·단가·치식만 더 담는다(추가 조회 없음, 환자명 X)
                        const meta = { itDate: getTimestamp(it.createdAt) || 0, unitPrice: Number(it.unitPrice ?? it.pricePerTooth ?? 0) || 0,
                            toothInfo: it.toothInfo || '', isRemake: !!it.isRemake, itemType: it.itemType || '' };
                        // 케이스 통일 2단계: 키를 orderId ∪ parsedCaseId로 넓힌다 —
                        // 수집 취소로 orderId가 바뀌거나 복사 주문 없이(케이스 직접) 들어간 건도 추적된다.
                        const keys = [it.orderId, it.parsedCaseId].filter(Boolean);
                        for (const k of keys) {
                            entries.push({ stmtId: d.id, key: k, number: s.statementNumber || '', status: s.status || 'draft', live, meta });
                            if (String(k).startsWith('case:')) entries.push({ stmtId: d.id, key: String(k).slice(5), number: s.statementNumber || '', status: s.status || 'draft', live, idOnly: true, meta });
                        }
                        // (2026-09-09) 환자·치식 키 — 문서 id가 달라도(웹 의뢰서 ↔ 수집분, 월 넘김) 같은 거래처의 같은 건을 잡는다
                        const bk = billKeyOf(it);
                        if (bk) {
                            entries.push({ stmtId: d.id, key: `pt:${bk}`, number: s.statementNumber || '', status: s.status || 'draft',
                                partnerId: s.toUserId || null, partnerName: partnerKeyOf(s.toUserName), live, meta });
                        }
                    });
                });
                if (alive) { setUsedOrderEntries(entries); setUsedLoaded('ok'); }
            } catch (e) {
                console.warn('기존 명세서 사용 이력 조회 실패:', e);   // 표시용 부가 정보 — 실패해도 작성은 계속
                if (alive) setUsedLoaded('error');   // 자동 초안은 이 확인 없이는 진행하지 않는다
            }
        })();
        return () => { alive = false; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [user]);

    // 정정 발행 (2026-10-23): 이 명세서가 고치는 원본 id·번호 — 발행 성공 시 원본에 '정정됨' 표시
    // (아래 usedOrderMap이 참조하므로 그보다 먼저 선언)
    const [correctsId, setCorrectsId] = useState(correctFromId || null);
    const [correctsNumber, setCorrectsNumber] = useState('');

    // 표시용 맵: 지금 편집 중인 문서(currentStatementId) 항목만 제외 — 재조회 없이 즉시 반영 (2026-08-26)
    // 정정 화면이면 원본(correctsId)도 제외 — 원본은 발행 시 '정정됨' 처리되므로 중복이 아니다 (2026-10-23)
    // 살아 있는 발행본에 이미 들어간 id (지금 문서·정정 원본 제외) — 자동 초안 이중 청구 방지 (2026-10-23)
    const liveBilledIds = React.useMemo(() => {
        const set = new Set();
        usedOrderEntries.forEach(en => {
            if (!en.live || en.stmtId === currentStatementId || (correctsId && en.stmtId === correctsId)) return;
            if (!String(en.key).startsWith('pt:')) set.add(en.key);
        });
        return set;
    }, [usedOrderEntries, currentStatementId, correctsId]);
    const usedOrderMap = React.useMemo(() => {
        const map = {};
        usedOrderEntries.forEach(en => {
            if (en.idOnly) return;   // case: 접두를 벗긴 보조 키는 자동 초안 거름 전용
            if (en.stmtId === currentStatementId) return;          // 지금 문서는 "중복"이 아니다
            if (correctsId && en.stmtId === correctsId) return;    // 정정 원본도 "중복"이 아니다
            // 발행본이 임시저장보다 우선 표시 (같은 건이 여러 곳에 있으면 확정된 쪽을 알린다)
            if (map[en.key] && map[en.key].status === 'confirmed') return;
            map[en.key] = { number: en.number, status: en.status };
        });
        return map;
    }, [usedOrderEntries, currentStatementId, correctsId]);

    // (2026-09-09 대표 승인) 같은 거래처의 다른 명세서에 이미 있는 환자·치식 — 키 'pt:…' 항목만, 거래처가 같아야 한다
    // (환자명은 치과마다 겹칠 수 있어 거래처 불일치는 절대 표시하지 않는다). 발행본 우선.
    const usedPatientMap = React.useMemo(() => {
        const map = {};
        const pid = selectedClinic && selectedClinic !== '__manual__' ? selectedClinic : null;
        const pname = pid ? null : partnerKeyOf(manualClinicName);
        usedOrderEntries.forEach(en => {
            if (!en.key.startsWith('pt:') || en.stmtId === currentStatementId) return;
            if (correctsId && en.stmtId === correctsId) return;    // 정정 원본 제외 (2026-10-23)
            const samePartner = pid ? en.partnerId === pid : (!!pname && en.partnerName === pname);
            if (!samePartner) return;
            if (map[en.key] && map[en.key].status === 'confirmed') return;
            map[en.key] = { number: en.number, status: en.status };
        });
        return map;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [usedOrderEntries, currentStatementId, correctsId, selectedClinic, manualClinicName]);
    const priorBilledOf = (item) => {
        if ((item.orderId && usedOrderMap[item.orderId]) || (item.parsedCaseId && usedOrderMap[item.parsedCaseId])) return null;   // id로 이미 잡힘
        const bk = billKeyOf(item);
        return bk ? (usedPatientMap[`pt:${bk}`] || null) : null;
    };

    // (2026-09-09 대표 승인) 같은 명세서 안에서 같은 환자·치식·종류가 서로 다른 건(orderId)으로 2회 이상 → '리메이크 예상'
    //   리메이크 표시가 없는 행만 대상(표시된 행은 이미 [리메이크] 꼬리표가 있다). 치과가는날 8월분: 허승호 #47·한대성 #26 같은 짝.
    const remakeSuspectSet = React.useMemo(() => {
        const groups = {};
        items.forEach(it => {
            if (it.itemType === 'gold' || it.isRemake) return;
            const bk = billKeyOf(it); if (!bk) return;
            const src = it.orderId || it.parsedCaseId || it.id;
            (groups[bk] = groups[bk] || new Map()).set(src, true);
        });
        const set = new Set();
        items.forEach(it => {
            if (it.itemType === 'gold' || it.isRemake) return;
            const bk = billKeyOf(it);
            if (bk && groups[bk] && groups[bk].size >= 2) set.add(it.id);
        });
        return set;
    }, [items]);

    // ── 리메이크 원 건 연결 (2026-10-24 2단계 ④ — 계약 §1·§5) ──
    // 원 건 찾는 순서(명세 §2-2): ① id(remakeOfCaseId·remakeOfOrderId)가 이 명세서 줄·지난 명세서 줄(최근 6개월, 이미 읽은 usedOrderEntries)과 일치
    //   ② id가 없거나 못 찾으면 같은 거래처·환자·치식 키로 '추정'(사람이 [이 건으로 연결]을 눌러야 연결) ③ 못 찾음.
    // 책임 선택은 기공소 계정(대표·직원)만 — 치과 계정에는 연결 창 자체를 띄우지 않는다.
    const canLinkRemake = !isClinicUser(user);
    const [remakeModalItemId, setRemakeModalItemId] = useState(null);
    // 키 → 대표 원 건 줄(발행본 우선). 지금 문서·정정 원본은 제외(그 줄은 지금 items 안에서 찾는다)
    const originEntryMap = React.useMemo(() => {
        const map = new Map();
        const pid = selectedClinic && selectedClinic !== '__manual__' ? selectedClinic : null;
        const pname = pid ? null : partnerKeyOf(manualClinicName);
        usedOrderEntries.forEach(en => {
            if (!en.meta || en.meta.itemType === 'gold') return;
            if (en.stmtId === currentStatementId || (correctsId && en.stmtId === correctsId)) return;
            const isPt = String(en.key).startsWith('pt:');
            if (isPt) {
                if (en.meta.isRemake) return;   // 추정은 '처음 만든 건'만 — 다른 재제작 줄과 짝짓지 않는다
                const samePartner = pid ? en.partnerId === pid : (!!pname && en.partnerName === pname);
                if (!samePartner) return;
            }
            const prev = map.get(en.key);
            const rank = (x) => (x.live ? 2 : 0) + (x.status === 'confirmed' ? 1 : 0);
            // 같은 등급이면 더 이른 작업일(처음 만든 쪽)
            if (!prev || rank(en) > rank(prev) || (rank(en) === rank(prev) && (en.meta.itDate || Infinity) < (prev.meta.itDate || Infinity))) map.set(en.key, en);
        });
        return map;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [usedOrderEntries, currentStatementId, correctsId, selectedClinic, manualClinicName]);
    const originFromEntry = (en, linkedBy) => ({
        linkedBy, where: 'statement', number: en.number || '', status: en.status || '',
        date: en.meta.itDate || 0, unitPrice: en.meta.unitPrice || 0, toothInfo: en.meta.toothInfo || '',
    });
    // id로 원 건 찾기 (연결 창의 늦은 조회 결과에도 다시 쓴다)
    const findRemakeOriginById = (selfId, ids) => {
        const want = new Set();
        ids.filter(Boolean).forEach(x => { want.add(String(x)); want.add(`case:${x}`); });
        if (want.size === 0) return null;
        const inCur = items.find(x => x.id !== selfId && x.itemType !== 'gold' && !x.isRemake
            && ((x.parsedCaseId && want.has(String(x.parsedCaseId))) || (x.orderId && want.has(String(x.orderId)))));
        if (inCur) return { linkedBy: 'id', where: 'current', number: savedStatementNumber || '', status: '',
            date: getTimestamp(inCur.createdAt) || 0, unitPrice: Number(inCur.unitPrice) || 0, toothInfo: inCur.toothInfo || '' };
        for (const k of want) {
            const en = originEntryMap.get(k);
            if (en) return originFromEntry(en, 'id');
        }
        return null;
    };
    const remakeOriginOf = (item) => {
        if (!item || !item.isRemake || item.itemType === 'gold') return null;
        const byId = findRemakeOriginById(item.id, [item.remakeOfCaseId, item.remakeOfOrderId]);
        if (byId) return byId;
        const bk = billKeyOf(item);
        if (bk) {
            const cur = items.find(x => x.id !== item.id && x.itemType !== 'gold' && !x.isRemake && billKeyOf(x) === bk);
            if (cur) return { linkedBy: 'guess', where: 'current', number: savedStatementNumber || '', status: '',
                date: getTimestamp(cur.createdAt) || 0, unitPrice: Number(cur.unitPrice) || 0, toothInfo: cur.toothInfo || '' };
            const en = originEntryMap.get(`pt:${bk}`);
            if (en) return originFromEntry(en, 'guess');
        }
        return null;
    };
    // 화면·인쇄 표기에 쓸 remakeOf: 저장된 값 우선, 없으면 id로 확실히 찾은 원 건 (추정은 사람이 연결해야 쓴다)
    const effectiveRemakeOf = (item) => {
        if (item.remakeUnlinked) return null;   // 사람이 연결을 끊은 줄 (검수 P2-8)
        const saved = cleanRemakeOf(item.remakeOf);
        if (saved) return saved;
        if (!item.isRemake) return null;
        const o = findRemakeOriginById(item.id, [item.remakeOfCaseId, item.remakeOfOrderId]);
        if (!o || !o.date) return null;
        return cleanRemakeOf({ date: formatDateHelper(new Date(o.date)), statementNumber: o.number || '' });
    };
    // 저장 직전: id로 확실히 찾은 원 건의 날짜·번호를 줄에 고정 (발행본·인쇄·공유 스냅샷이 같은 글자를 쓴다)
    const withRemakeOf = (list) => list.map(it => {
        if (!it.isRemake || it.itemType === 'gold' || it.remakeUnlinked || cleanRemakeOf(it.remakeOf)) return it;
        const r = effectiveRemakeOf(it);
        return r ? { ...it, remakeOf: r } : it;
    });
    // 한 줄만 고치기 (연결 창 전용 — 늦게 끝나는 조회와 겹쳐도 최신 items 기준)
    const patchItem = (itemId, patch) => {
        markDirty();
        setItems(prev => prev.map(it => (it.id === itemId ? { ...it, ...patch } : it)));
    };
    // 연결 창의 [N원 적용] — 사람이 눌렀을 때만 단가에 들어간다(자동 적용 금지, 계약 §1)
    const applyRemakePrice = (itemId, price) => {
        const unitPrice = Math.max(0, Math.round(Number(price) || 0));
        markDirty();
        setItems(prev => prev.map(it => (it.id === itemId ? {
            ...it, unitPrice, priceSource: 'manual', needsPriceCheck: false,
            ...(unitPrice > 0 && !(it.toothCount > 0) && !it.countManual ? { toothCount: 1 } : {}),
        } : it)));
    };

    // ── 리메이크 책임 기록 (검수 P2-7, 2026-10-24) — statementRemakeNotes/{statementId} = { labId, items: {줄키해시: 책임}, updatedAt }
    //   명세서 문서(치과도 읽음)에는 책임을 넣지 않는다. 기공소 회사만 읽고 쓰는 별도 문서(rules = S 담당).
    const [remakeNoteWarn, setRemakeNoteWarn] = useState('');
    const remakeNoteExistsRef = useRef(false);   // 불러올 때 문서가 있었는지 — 전부 지웠으면 빈 items로 덮어쓴다
    const loadRemakeNotes = async (stmtId) => {
        if (!stmtId) return;
        try {
            const snap = await getDoc(doc(db, 'statementRemakeNotes', stmtId));
            remakeNoteExistsRef.current = snap.exists();
            if (!snap.exists()) return;
            const map = snap.data().items || {};
            setItems(prev => prev.map(it => {
                const v = map[remakeNoteKeyOf(it)];
                return REMAKE_LIABILITIES.includes(v) ? { ...it, remakeLiability: v } : it;
            }));
        } catch (e) {
            // 문서가 없을 때 rules가 읽기를 거부할 수도 있다 — 작성은 계속, 사유만 기록 (환자 정보 X)
            console.warn('[리메이크 책임] 기록 읽기 실패:', e?.code || e?.message || e);
        }
    };
    // savedItems = 이번에 저장한 직렬화 줄(items와 같은 순서) — 키는 저장된 글자 기준으로 만든다(외주 접두 등 반영)
    const saveRemakeNotes = async (stmtId, savedItems) => {
        if (!stmtId) return;
        const map = {};
        items.forEach((it, i) => {
            if (!it.isRemake || !REMAKE_LIABILITIES.includes(it.remakeLiability)) return;
            map[remakeNoteKeyOf((savedItems && savedItems[i]) || it)] = it.remakeLiability;
        });
        if (Object.keys(map).length === 0 && !remakeNoteExistsRef.current) return;   // 쓸 것도 지울 것도 없음
        try {
            await setDoc(doc(db, 'statementRemakeNotes', stmtId), {
                labId: user?.companyId || auth.currentUser?.uid || '',
                items: map,
                updatedAt: serverTimestamp(),
            });
            remakeNoteExistsRef.current = true;
            setRemakeNoteWarn('');
        } catch (e) {
            // 명세서 저장은 이미 끝났다 — 책임 기록만 실패. 화면에 알린다 (조용한 실패 금지)
            console.warn('[리메이크 책임] 기록 저장 실패:', e?.code || e?.message || e);
            setRemakeNoteWarn('명세서는 저장됐지만 리메이크 책임 기록(기공소 내부)을 저장하지 못했습니다. 잠시 후 다시 저장해 주세요.');
        }
    };

    // 불러온 문서의 상태 — 'confirmed'면 발행본 수정 모드 (저장 시 치과에 재확인 요청)
    const [loadedStatus, setLoadedStatus] = useState(null);
    const isEditingPublished = loadedStatus === 'confirmed';
    // 세금계산서 발행 상태 (2026-08-13 검토자 P1-5) — 'issued'면 금액 변경 저장 차단
    const [loadedTaxInvoiceStatus, setLoadedTaxInvoiceStatus] = useState(null);
    const [loadedTaxInvoiceId, setLoadedTaxInvoiceId] = useState(null);
    // 정산 상태 (2026-10-23 대표 확정) — 'settled'(치과가 정산 확인)면 금액·항목 수정 불가 → [고쳐서 다시 발행]
    const [loadedSettlementStatus, setLoadedSettlementStatus] = useState(null);
    // 단가표 읽기 실패 (문서 없음이 아닌 오류) — 빈 단가표로 학습해 기존 단가표를 덮어쓰는 사고 방지
    const [priceListLoadFailed, setPriceListLoadFailed] = useState(false);
    const originalTotalRef = React.useRef(0);   // 로드 시점 총액 (발행 후 금액 변경 감지용)
    // 손본 흔적 감지 (2026-08-13 P0-1) — 단가 수정·삭제·추가·할인 등 사용자 편집이 있으면 true.
    // 기간 칩 재클릭·재조회로 내용이 통째로 교체되기 전에 확인창을 띄우는 근거.
    const dirtyRef = React.useRef(false);
    const markDirty = () => { dirtyRef.current = true; };
    // 손본 내용 재조회 확인 (2026-08-13 P0-1) — false면 취소(재조회 중단)
    const confirmDiscardIfDirty = () => {
        if (dirtyRef.current && items.length > 0) {
            return window.confirm('작성 중인 내용이 있습니다. 다시 불러오면 손보신 단가·삭제·추가가 사라집니다. 계속할까요?');
        }
        return true;
    };

    // 거래처 변경 전 확인 (2026-10-23 P1) — 손본 내용이 있으면 화면에서 지워짐을 알린다. false면 변경 취소
    const confirmClinicSwitch = (nextClinic) => {
        if (!selectedClinic || nextClinic === selectedClinic) return true;
        if (!(dirtyRef.current && items.length > 0)) return true;
        const prevName = selectedClinic === '__manual__'
            ? (manualClinicName.trim() || '직접 입력 거래처')
            : (clinics.find(c => c.id === selectedClinic)?.name || '이전 거래처');
        return window.confirm(`작성 중인 ${prevName} 내용이 화면에서 지워집니다(저장된 작성 중 명세서는 남아 있습니다). 바꿀까요?`);
    };

    // 문서 데이터 → 편집 화면 상태 반영 (statementId 진입 + 거래처 선택 시 기존 draft 이어쓰기 공용, 2026-08-26)
    // opts.asCorrection(2026-10-23): 정정 발행 — 원본 내용으로 채우되 새 문서(번호·계산서·공유·정산 정보는 안 가져옴)
    const applyLoadedStatement = async (docId, data, opts = {}) => {
                const asCorrection = !!opts.asCorrection;
                setCurrentStatementId(asCorrection ? null : docId);
                setLoadedStatus(asCorrection ? 'draft' : (data.status || 'draft'));
                setSavedStatementNumber(asCorrection ? null : (data.statementNumber || null));
                setLoadedSettlementStatus(asCorrection ? null : (data.settlementStatus || null));
                if (asCorrection) {
                    setCorrectsId(docId);
                    setCorrectsNumber(data.statementNumber || '');
                } else {
                    // 정정 draft 이어쓰기 — 저장된 correctsId 복원 + 안내줄용 원본 번호 조회
                    setCorrectsId(data.correctsId || null);
                    setCorrectsNumber('');
                    if (data.correctsId) {
                        getDoc(doc(db, 'transactionStatements', data.correctsId))
                            .then(s0 => { if (s0.exists()) setCorrectsNumber(s0.data().statementNumber || ''); })
                            .catch(e => console.warn('정정 원본 번호 조회 실패:', e?.code || e?.message));
                    }
                }
                setSelectedClinic(data.isManual ? '__manual__' : (data.toUserId || ''));
                setManualClinicName(data.isManual ? (data.toUserName || '') : '');
                if (data.startDate) {
                    const s = formatDateHelper(new Date(getTimestamp(data.startDate)));
                    setStartDate(s); setParsedFrom(s);   // 이어 편집 시 자동수집 박스도 이 기간으로 바로 표시
                }
                if (data.endDate) {
                    const e = formatDateHelper(new Date(getTimestamp(data.endDate)));
                    setEndDate(e); setParsedTo(e);
                }
                if (data.dateBasis) setDateBasis(data.dateBasis);
                setTotalDiscount(data.totalDiscount || 0);
                setDiscountType(data.discountType || 'amount');
                setNotes(data.notes || '');
                // 세금계산서 발행 여부 — 발행 후 금액 수정을 막기 위해 함께 읽어둔다 (2026-08-13 검토자 P1-5)
                setLoadedTaxInvoiceStatus(asCorrection ? null : (data.taxInvoiceStatus || null));
                setLoadedTaxInvoiceId(asCorrection ? null : (data.taxInvoiceId || null));
                originalTotalRef.current = asCorrection ? 0 : (data.totalAmount || 0);
                // (2026-08-31 대표 지시) 이어쓰기 복원도 전체 날짜순 정렬로 통일 — 출처별로 뭉치지 않게.
                // sortItems는 그룹(연속 블록)·금값 행을 묶음째 옮기므로 저장본 그룹이 해체되지 않는다.
                // 발행된 문서 "열람"(ViewTransactionStatement)은 별도 컴포넌트 — 여기 영향 없음.
                setItems(sortItems((data.items || []).map((item, i) => {
                    const up = item.unitPrice ?? item.pricePerTooth ?? 0;
                    return {
                        ...item,
                        id: `saved-${i}-${Date.now()}`,
                        orderId: item.orderId ?? null,
                        unitPrice: up,
                        // 재제작 무상확인 복원 (2026-08-13 P1-4): 저장분에 플래그가 없어도(기존 177건)
                        // 리메이크인데 단가가 비어 있으면 '무상 여부 확인'을 되살린다.
                        needsPriceCheck: item.needsPriceCheck ?? (!!item.isRemake && !up),
                        suggestedUnitPrice: item.suggestedUnitPrice ?? null,
                        _sortTimestamp: getTimestamp(item.designDate || item.createdAt) || Date.now(),   // 10-31 디자인 완성일 우선
                    };
                }), data.sortBy || 'createdAt'));
                // 리메이크 책임(기공소 내부 기록) — 이 문서의 별도 기록에서 (정정이면 원본 기록을 이어받아 새 문서에 저장)
                await loadRemakeNotes(docId);

                // 이어쓰기 병합 (2026-07-30): draft로 다시 열 때 그 기간의 새 수집분을
                // 기존 항목 뒤에 추가한다 — 이어쓰기가 저장본만 보여줘서 새 수집분이
                // 영영 안 보이던 문제의 해결. 발행본(confirmed) 편집은 건드리지 않는다.
                // 이 draft가 과거에 품었던 의뢰서 id 복원 (구 draft는 seenOrderIds가 없음 — 현재 항목으로 폴백)
                seenOrderIdsRef.current = new Set([
                    ...(data.seenOrderIds || []),
                    ...((data.items || []).map(it => it.orderId).filter(Boolean)),
                ]);
                canceledParsedCaseIdsRef.current = new Set(data.canceledParsedCaseIds || []);

                if ((data.status || 'draft') === 'draft' && !data.isManual && data.toUserId
                    && data.startDate && data.endDate) {
                    const sd0 = formatDateHelper(new Date(getTimestamp(data.startDate)));
                    const ed0 = formatDateHelper(new Date(getTimestamp(data.endDate)));
                    const clinicSel0 = data.toUserId;
                    const pl0 = await loadPriceList(clinicSel0);
                    handleSearch(sd0, ed0, pl0 || [], {
                        merge: true,
                        clinic: clinicSel0,
                        clinicName: data.toUserName || '',
                        // 지운 행 부활 방지 — 저장 항목만이 아니라 "한 번이라도 봤던" id 전체를 기준으로
                        existingOrderIds: seenOrderIdsRef.current,
                    });
                }
    };

    // 임시저장 이어쓰기 / 발행본 수정: 기존 문서 불러오기 (편집 화면 단일화 2026-07-19)
    useEffect(() => {
        if (!statementId) return;
        (async () => {
            try {
                const snap = await getDoc(doc(db, 'transactionStatements', statementId));
                if (!snap.exists()) { alert('명세서를 찾을 수 없습니다.'); onBack && onBack(); return; }
                // 정산 완료본은 편집 진입 차단 (2026-10-23 대표 확정 — rules도 막음)
                if (snap.data().status === 'confirmed' && snap.data().settlementStatus === 'settled') {
                    alert('치과가 정산을 확인한 명세서는 고칠 수 없습니다. 명세서 화면의 [고쳐서 다시 발행]을 이용해주세요.');
                    onBack && onBack();
                    return;
                }
                await applyLoadedStatement(statementId, snap.data());
            } catch (e) {
                console.error('명세서 로드 실패:', e);
                // 로드 실패 시 화면에 남으면 자동저장이 발행본을 draft로 강등시킬 수 있음 — 목록으로 복귀
                alert('명세서를 불러오지 못했습니다. 다시 시도해주세요.');
                onBack && onBack();
            }
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [statementId]);

    // 정정 발행 (2026-10-23): 원본을 읽어 거래처·기간·항목·할인·메모를 채운 새 명세서로 시작
    useEffect(() => {
        if (!correctFromId || statementId) return;
        (async () => {
            try {
                const snap = await getDoc(doc(db, 'transactionStatements', correctFromId));
                if (!snap.exists()) { alert('정정할 원본 명세서를 찾을 수 없습니다.'); onBack && onBack(); return; }
                await applyLoadedStatement(correctFromId, snap.data(), { asCorrection: true });
            } catch (e) {
                console.error('정정 원본 로드 실패:', e);
                alert('정정할 원본 명세서를 불러오지 못했습니다. 다시 시도해주세요.');
                onBack && onBack();
            }
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [correctFromId]);

    // 거래처 선택 시 그 거래처의 기존 draft 감지 (유령 draft 방지 — 거래처당 작성 중 1개 원칙, 2026-08-26)
    const findExistingClinicDraft = async (clinicSel) => {
        try {
            const uid = user?.companyId || auth.currentUser?.uid;
            if (!uid || !clinicSel || clinicSel === '__manual__') return null;
            const snap = await getDocs(query(collection(db, 'transactionStatements'),
                where('fromUserId', '==', uid),
                where('toUserId', '==', clinicSel),
                where('status', '==', 'draft')));
            const list = snap.docs.map(d => ({ id: d.id, data: d.data() }));
            if (!list.length) return null;
            // 여러 개면 가장 최근에 만진 draft를 기준으로
            list.sort((a, b) => (getTimestamp(b.data.updatedAt || b.data.createdAt) || 0) - (getTimestamp(a.data.updatedAt || a.data.createdAt) || 0));
            return list[0];
        } catch (e) {
            console.warn('기존 작성 중 명세서 조회 실패:', e);   // 감지 실패해도 작성은 계속 (새 문서 생성)
            return null;
        }
    };

    // 거래처 변경 시 단가표 + 프리셋 로드
    useEffect(() => {
        if (selectedClinic) {
            // 거래처 변경 시 이어쓰기 대상 초기화 — 이전 치과의 draft를 새 치과 내용으로 덮어쓰는 사고 방지
            // (statementId로 직접 진입한 이어쓰기 편집은 selectedClinic이 로드 시 1회만 설정되므로 영향 최소)
            let switchedClinic = false;
            if (!startsFromDoc || selectedClinic !== loadedClinicRef.current) {
                if (loadedClinicRef.current && selectedClinic !== loadedClinicRef.current) {
                    setCurrentStatementId(null);
                    switchedClinic = true;
                    // (2026-08-31 검수 P1-1) 이전 거래처의 수집 요약·안내줄·불러오기 패널 잔상 제거 —
                    // A 치과 안내줄이 B 화면에 남아 [불러오기]로 A 항목+B 수집분이 섞이는 사고 경로 차단
                    setCollectSummary(null);
                    setCollectPanelOpen(false);
                    // (2026-10-23 P1) 이전 거래처의 항목·번호·발행 상태도 비운다 — A 항목이 B 명세서로 저장·발행되던 경로 차단.
                    // 손본 내용 확인창은 선택 변경 직전(changeClinic)에서 이미 받았다.
                    setItems([]);
                    setSavedStatementNumber(null);
                    setLoadedStatus(null);
                    setLoadedTaxInvoiceStatus(null);
                    setLoadedTaxInvoiceId(null);
                    setLoadedSettlementStatus(null);
                    setCorrectsId(null);          // 다른 거래처로 바꾸면 정정 대상이 아님 — 원본을 잘못 '정정됨' 처리하지 않게
                    setCorrectsNumber('');
                    setTotalDiscount(0);
                    setDiscountType('amount');
                    setNotes('');
                    setMergeNotice('');
                    originalTotalRef.current = 0;
                    seenOrderIdsRef.current = new Set();
                    canceledParsedCaseIdsRef.current = new Set();
                    dirtyRef.current = false;
                }
                loadedClinicRef.current = selectedClinic;
            }
            // 유령 draft 방지 (2026-08-26): 같은 거래처의 기존 draft가 있으면 이어쓰기/재사용을 물어본다.
            // statementId 직접 진입(이미 그 draft 편집 중)은 첫 설정에서 감지하지 않는다.
            const shouldDetectDraft = selectedClinic !== '__manual__' && (!startsFromDoc || switchedClinic);
            if (shouldDetectDraft) {
                (async () => {
                    // (P1-2, 2026-08-26) 세대 가드: await 중 거래처가 또 바뀌었으면 이 감지 결과를 버린다
                    // (타 거래처 draft를 현재 선택에 덮어쓰는 경합 차단 — loadedClinicRef가 최신 선택을 추적)
                    const clinicAtStart = selectedClinic;
                    const found = await findExistingClinicDraft(clinicAtStart);
                    if (loadedClinicRef.current !== clinicAtStart) return;
                    if (found) {
                        const clinicName0 = clinics.find(c => c.id === selectedClinic)?.name || found.data.toUserName || '거래처';
                        // (2026-10-23 P1) [취소] 한 번에 저장본이 덮어써지던 구조 → 두 단계. 애매하면 이어쓰기(안전한 쪽).
                        let resume = window.confirm(
                            `"${clinicName0}"에 작성 중인 명세서가 있습니다.\n(${found.data.statementNumber || '번호 없음'} · ${(found.data.items || []).length}건)\n\n` +
                            `이어서 쓸까요?`);
                        if (!resume) {
                            resume = !window.confirm(
                                '기존 작성 내용을 지우고 새로 쓸까요?\n\n' +
                                '[확인] = 지우고 새로 쓰기(직접 고친 단가·추가한 줄이 모두 사라지며 되돌릴 수 없습니다)\n' +
                                '[취소] = 이어서 쓰기');
                        }
                        // 이어쓰기·새로 작성 모두 같은 문서를 재사용 — 유령 draft가 늘어나지 않는다 (앱과 동일 방식)
                        setCurrentStatementId(found.id);
                        setSavedStatementNumber(found.data.statementNumber || null);
                        if (resume) {
                            try { await applyLoadedStatement(found.id, found.data); return; }
                            catch (e) {
                                console.error('작성 중 명세서 불러오기 실패:', e);
                                alert('작성 중 명세서를 불러오지 못했습니다. 새로 작성으로 진행합니다.');
                            }
                        }
                    }
                    proceedClinicSetup({ fresh: switchedClinic });
                })();
                return;
            }
            proceedClinicSetup({ fresh: switchedClinic });
        } else {
            setPriceList([]);
            setPriceListLoadFailed(false);
            setPartnerProsthesisPresets(DEFAULT_PROSTHESIS_CHIPS);
            setPartnerImplantPresets(DEFAULT_IMPLANT_CHIPS);
            setPartnerMaterialMap({});
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedClinic]);

    // 거래처 선택 후속 처리 (기존 effect 본문 분리 — 단가표·수집분 로드 + 자동 조회)
    // fresh: 거래처를 바꿔 항목을 방금 비웠음 — 클로저의 items는 이전 거래처 것이라 길이 조건 대신 이 값으로 재조회
    const proceedClinicSetup = ({ fresh = false } = {}) => {
        {
            const noItems = fresh || items.length === 0;
            const clinic = clinics.find(c => c.id === selectedClinic);
            const isEmailSource = clinic?.source === 'email' || selectedClinic.startsWith('email_');
            // 미연결 치과(에이전트 수집으로만 존재) — 단가표/조회는 실 거래처가 아니므로 스킵, 수집 항목만 로드
            const isAgentSource = clinic?.source === 'agent' || selectedClinic.startsWith('agent_');
            // 에이전트 자동 수집 항목 로드 (덴클 회원 거래처 + 미연결 치과 + 이메일 거래처)
            // 2026-07-30: 이메일 거래처도 포함 — 제외돼 있어 수집분이 안 보이던 원인
            if (selectedClinic !== '__manual__') {
                loadParsedCases(selectedClinic);
            } else {
                setParsedCases([]);
            }
            // 미연결 치과·이메일 거래처 — 2026-07-30 B안: 이름 키 단가표도 로드해 자동 단가 적용
            if (isAgentSource || isEmailSource) {
                if (noItems) {
                    let sd = startDate, ed = endDate;
                    if (!sd || !ed) {
                        const now = new Date();
                        sd = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-01`;
                        ed = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
                        setStartDate(sd); setEndDate(ed);
                    }
                    (async () => {
                        const pl = await loadPriceList(selectedClinic);
                        handleSearch(sd, ed, pl);
                    })();
                } else {
                    loadPriceList(selectedClinic);
                }
                return;
            }
            // 자동 구성: 항목이 비어 있으면 즉시 조회 (2026-07-19)
            // - 새 명세서: 이번 달 1일~오늘로 기간을 잡고 조회
            // - 임시저장 이어쓰기(항목 없음): 저장된 기간으로 조회 (기간도 없으면 이번 달 1일~오늘)
            // 단가표를 먼저 로드해 넘긴다 — state의 priceList는 이전 거래처 것일 수 있음
            if (!isEmailSource && selectedClinic !== '__manual__' && noItems) {
                let sd = startDate;
                let ed = endDate;
                if (!sd || !ed) {
                    const now = new Date();
                    sd = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-01`;
                    ed = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
                    setStartDate(sd);
                    setEndDate(ed);
                }
                (async () => {
                    const pl = await loadPriceList(selectedClinic);
                    handleSearch(sd, ed, pl);
                })();
                loadPartnerPresets(selectedClinic);
            } else if (!isEmailSource) {
                loadPriceList(selectedClinic);
                loadPartnerPresets(selectedClinic);
            } else {
                setPriceList([]);
            }
        }
    };

    // 보드에서 진입 시 거래처 목록이 늦게 로드되면 의뢰처명 유사 매칭이 비어 실행됨 — 목록 도착 후 재시도
    useEffect(() => {
        if (clinics.length > 0 && selectedClinic && selectedClinic !== '__manual__' && !selectedClinic.startsWith('email_')) {
            loadParsedCases(selectedClinic);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [clinics]);

    // 빠른 날짜 선택 핸들러
    const handleQuickDateSelect = (start, end) => {
        setStartDate(start);
        setEndDate(end);
    };

    // 거래처 목록 가져오기
    const fetchClinics = async () => {
        try {
            const partnersMap = new Map();
            // 내 연결만 조회 (전체 스캔 방지) + 직원 계정은 회사 ID 기준
            const myId = user?.companyId || auth.currentUser.uid;
            const [sentSnap, recvSnap] = await Promise.all([
                getDocs(query(collection(db, 'connections'), where('requesterId', '==', myId), where('status', '==', 'accepted'))),
                getDocs(query(collection(db, 'connections'), where('receiverId', '==', myId), where('status', '==', 'accepted'))),
            ]);
            const connectionsSnapshot = { docs: [...sentSnap.docs, ...recvSnap.docs] };

            for (const docSnap of connectionsSnapshot.docs) {
                const connection = docSnap.data();

                // CAD 디자인 외주로 연결된 거래처(상대=기공소)는 태그를 덧입혀 목록에서 구분·명세서에 도장(통합 기획 2장)
                const outsourceTag = connection.source === 'design_outsource'
                    ? { source: 'design_outsource', counterpartyType: 'lab', designOutsourceRequestId: connection.designOutsourceRequestId || '' }
                    : {};

                if (connection.requesterId === myId && connection.receiverId) {
                    try {
                        const userDoc = await getDoc(doc(db, 'users', connection.receiverId));
                        if (userDoc.exists()) {
                            const userData = userDoc.data();
                            partnersMap.set(connection.receiverId, {
                                id: connection.receiverId,
                                name: userData.businessName || userData.name || '이름 없음',
                                ...outsourceTag,
                            });
                        }
                    } catch (error) {
                        console.error('사용자 정보 조회 실패:', error);
                    }
                }

                if (connection.receiverId === myId && connection.requesterId) {
                    try {
                        const userDoc = await getDoc(doc(db, 'users', connection.requesterId));
                        if (userDoc.exists()) {
                            const userData = userDoc.data();
                            partnersMap.set(connection.requesterId, {
                                id: connection.requesterId,
                                name: userData.businessName || userData.name || '이름 없음',
                                ...outsourceTag,
                            });
                        }
                    } catch (error) {
                        console.error('사용자 정보 조회 실패:', error);
                    }
                }
            }

            // 이메일 매핑(labSettings.clinicEmailMapping)에서 거래처 추가
            try {
                // companyId 우선 — 직원 계정도 회사 labSettings를 읽어야 이메일 거래처가 보임
                const uid = user?.companyId || auth.currentUser.uid;
                const labSettingsDoc = await getDoc(doc(db, 'labSettings', uid));
                if (labSettingsDoc.exists()) {
                    const clinicMapping = labSettingsDoc.data()?.clinicEmailMapping || {};
                    for (const [clinicId, data] of Object.entries(clinicMapping)) {
                        const clinicName = data.clinicName || '';
                        if (!clinicName) continue;

                        // 이미 connections에 있으면 스킵
                        if (partnersMap.has(clinicId)) continue;

                        // clinicId가 실제 유저인 경우
                        try {
                            const userDoc2 = await getDoc(doc(db, 'users', clinicId));
                            if (userDoc2.exists()) {
                                const userData2 = userDoc2.data();
                                partnersMap.set(clinicId, {
                                    id: clinicId,
                                    name: userData2.businessName || clinicName,
                                    source: 'email'
                                });
                                continue;
                            }
                        } catch (e) { /* ignore */ }

                        // 유저 문서 없음 — clinicName만으로 추가
                        const key = `email_${clinicName}`;
                        if (!partnersMap.has(key)) {
                            partnersMap.set(key, {
                                id: key,
                                name: clinicName,
                                source: 'email'
                            });
                        }
                    }
                }
            } catch (emailError) {
                console.error('이메일 매핑 거래처 조회 실패:', emailError);
            }

            // 에이전트 자동 수집(parsedCases) 중 거래처 연결이 안 된 치과도 목록에 추가
            // — 연결 없이도 그 치과 케이스로 거래명세서를 작성할 수 있게
            try {
                const uid = user?.companyId || auth.currentUser.uid;
                const normBiz = (n) => (n || '').replace(/\s|\(.*?\)/g, '').replace(/치과의원|치과병원|치과$/g, '').toLowerCase();
                const knownNorms = new Set(Array.from(partnersMap.values()).map(p => normBiz(p.name)));
                // pending + imported 모두 이름 소스로 사용 — 수집함에서 전량 명세서화(imported)한 뒤에도
                // 해당 비회원 치과가 거래처 목록에서 사라지지 않게 함
                const parsedSnap = await getDocs(query(
                    collection(db, 'parsedCases'),
                    where('labId', '==', uid),
                    where('status', 'in', ['pending', 'imported'])
                ));
                const unlinkedNames = new Map();   // 정규화명 -> 표시명
                parsedSnap.docs.forEach(d => {
                    const r = d.data();
                    if (r.sourceType === 'scan_only') return; // 스캔만 있는 폴더(명세서용 아님) 제외 (2026-08-05)
                    if (r.partnerId) return;                 // 이미 거래처로 매칭된 건 제외
                    const name = (r.clientNameInFile || '').trim();
                    const n = normBiz(name);
                    if (!name || !n || knownNorms.has(n)) return;   // 연결 거래처와 겹치면 제외
                    if (!unlinkedNames.has(n)) unlinkedNames.set(n, name);
                });
                for (const [, name] of unlinkedNames) {
                    const key = `agent_${name}`;
                    if (!partnersMap.has(key)) partnersMap.set(key, { id: key, name, source: 'agent' });
                }
            } catch (e) {
                console.warn('미연결 치과 거래처 추가 실패:', e);
            }

            setClinics(Array.from(partnersMap.values()));
        } catch (error) {
            console.error('거래처 목록 조회 실패:', error);
        }
    };

    // 상대 업체 보철물/임플란트 프리셋 로드
    const loadPartnerPresets = async (partnerId) => {
        try {
            if (partnerId.startsWith('email_')) return;
            const userDoc = await getDoc(doc(db, 'users', partnerId));
            if (!userDoc.exists()) return;
            const data = userDoc.data();

            const prosthesisSet = new Set(DEFAULT_PROSTHESIS_CHIPS);
            if (data.customProsthesisOptions) {
                data.customProsthesisOptions.forEach(opt => {
                    const label = typeof opt === 'string' ? opt : (opt.label || opt.value || '');
                    if (label) prosthesisSet.add(label);
                });
            }
            setPartnerProsthesisPresets(Array.from(prosthesisSet));
            setPartnerMaterialMap(data.customMaterialMap || {});

            const implantSet = new Set(DEFAULT_IMPLANT_CHIPS);
            if (data.savedImplantOptions) {
                data.savedImplantOptions.forEach(opt => {
                    const label = typeof opt === 'string' ? opt : (opt.label || opt.value || '');
                    if (label) implantSet.add(label);
                });
            }
            if (data.customImplantOptions) {
                data.customImplantOptions.forEach(opt => {
                    const label = typeof opt === 'string' ? opt : (opt.label || opt.value || '');
                    if (label) implantSet.add(label);
                });
            }
            setPartnerImplantPresets(Array.from(implantSet));
        } catch (e) {
            console.log('프리셋 로드 실패:', e);
        }
    };


    // 단가표 로드
    // ── 에이전트 자동 수집 (parsedCases) ──
    const loadParsedCases = async (partnerId) => {
        try {
            const uid = user?.companyId || auth.currentUser.uid;
            const snap = await getDocs(query(
                collection(db, 'parsedCases'),
                where('labId', '==', uid),
                where('status', '==', 'pending')
            ));
            // 정확 매칭(partnerId) + 미매칭이지만 파일의 의뢰처명이 이 거래처와 유사한 항목
            const normBiz = (n) => (n || '').replace(/\s|\(.*?\)/g, '').replace(/치과의원|치과병원|치과$/g, '').toLowerCase();
            const partnerName = clinics.find(c => c.id === partnerId)?.name || '';
            const pn = normBiz(partnerName);
            const rows = snap.docs.map(d => ({ id: d.id, ...d.data() }))
                .filter(r => r.sourceType !== 'scan_only')   // 스캔만 있는 폴더(치식 없음, 명세서용 아님) 제외 (2026-08-05)
                .filter(r => {
                    if (r.partnerId === partnerId) return true;
                    if (r.partnerId) return false;   // 다른 거래처로 매칭된 항목 제외
                    const cn = normBiz(r.clientNameInFile);
                    return pn && cn && (pn === cn || pn.includes(cn) || cn.includes(pn));
                })
                .sort((a, b) => (b.fileMtime || '').localeCompare(a.fileMtime || ''));
            setParsedCases(rows);
            setParsedChecked(Object.fromEntries(rows.map(r => [r.id, true])));
        } catch (e) {
            console.warn('자동 수집 항목 로드 실패:', e);
            setParsedCases([]);
        }
    };

    const importParsedCases = async (visibleList) => {
        // 날짜 필터로 표시 중인 항목 안에서만 가져오기 (숨겨진 체크 항목은 제외)
        const pool = Array.isArray(visibleList) ? visibleList : parsedCases;
        const picked = pool.filter(r => parsedChecked[r.id]);
        if (picked.length === 0) { alert('가져올 항목을 선택해주세요.'); return; }
        const labId = user?.companyId || auth.currentUser.uid;
        const labName = user?.businessName || user?.companyName || user?.name || '기공소';
        const partner = clinics.find(c => c.id === selectedClinic);
        const partnerName = partner?.name || picked[0]?.partnerName || '치과';
        let ok = 0;
        for (const r of picked) {
            try {
                const fileDate = r.fileMtime ? new Date(r.fileMtime) : new Date();
                // 로컬(KST) 날짜 기준 — toISOString은 UTC라 오전 9시 이전 작업이 전날 번호로 밀림 (2026-08-26)
                const dateStr = formatDateHelper(fileDate).replace(/-/g, '');
                const orderNumber = `AGT-${dateStr}-${Math.random().toString(36).substring(2, 7).toUpperCase()}`;
                const indication = (r.indications || [])[0] || '';
                // 미연결 치과(agent_)는 실제 uid가 아니므로 fromUserId를 비우고 이름만 기록
                const realPartnerId = r.partnerId || (String(selectedClinic).startsWith('agent_') ? '' : selectedClinic);
                const orderRef = await addDoc(collection(db, 'workOrders'), {
                    orderNumber,
                    fromUserId: realPartnerId || null,
                    fromUserName: partnerName,
                    toUserId: labId,
                    toUserName: labName,
                    participants: [realPartnerId, labId].filter(Boolean),
                    patientName: displayPatientName(r).name || '환자미상',
                    patientNameRaw: r.patientNameRaw || r.patientName || '',
                    items: (r.teethDetails && r.teethDetails.length)
                        ? r.teethDetails.map(d => ({
                            toothNumber: d.tooth, prosthesis: d.prosthesis || indication, material: d.material || r.material || '',
                            shade: d.shade || '', implant: d.implant || '', ...(d.implantSystem ? { implantSystem: d.implantSystem } : {}), isPontic: !!d.isPontic, isBridge: !!d.isBridge,
                        }))
                        : (r.teeth || []).map(t => ({
                            toothNumber: t,
                            prosthesis: indication,
                            material: r.material || '',
                            shade: '', implant: '', isPontic: false, isBridge: false,
                        })),
                    prosthesisGroups: [], bridgeGroups: [], ponticGroups: [], implantGroups: [],
                    // CAD 파일 날짜 ≈ 작업(마감) 시점 — 마감일 기준 조회에서도 보이도록 채움
                    dueDate: Timestamp.fromDate(fileDate),
                    specialNotes: `에이전트 자동 수집 (${r.sourceType} · ${r.sourceFile || ''})${r.isRemake ? ' · 리메이크' : ''}`,
                    imageUrls: [],
                    isRemake: !!r.isRemake, remakeType: '', remakeReason: r.isRemake ? '에이전트 감지 (재제작 파일)' : '',
                    // 원 건 id 보존 (2026-10-24 ④): 에이전트가 적은 parsedCases.remakeOfId → 명세서 줄 remakeOfCaseId 로 이어진다
                    ...(r.isRemake && r.remakeOfId ? { remakeOfCaseId: r.remakeOfId } : {}),
                    autoCollected: true,                 // 규칙 예외 플래그 (수신 기공소 생성 허용)
                    source: 'agent_auto',
                    parsedCaseId: r.id,
                    readByReceiver: true,
                    createdAt: Timestamp.fromDate(fileDate),
                    updatedAt: new Date(),
                });
                // (2026-10-24 재점검 P1-3) 여기서는 status 를 바꾸지 않는다 — orderId 만 적고, 'imported' 전환은 저장(markCasesInStatement)에서만.
                //   저장 없이 떠나도 수집 대기(pending)로 남아 다시 보인다.
                await updateDoc(doc(db, 'parsedCases', r.id), { orderId: orderRef.id, importedAt: new Date() });
                ok++;
            } catch (e) { console.error('의뢰서 등록 실패:', e); }
        }
        setParsedCases(prev => prev.filter(r => !parsedChecked[r.id]));
        // 등록 직후 현재 기간으로 재조회 → 거래 내역에 바로 통합 표시 (웹 우선 + 중복 제거 + 색 구분)
        if (startDate && endDate) {
            await handleSearch(startDate, endDate, priceList);
        }
        alert(`${ok}건을 등록해 거래 내역에 통합했습니다. (연보라 = 자동 수집 항목)\n등록한 파일 날짜가 조회 기간 밖이면 기간을 맞춰 [조회]하세요.`);
    };

    // ── 수집함 통합 (2026-07-30): 기간 수집 현황 · 제외 복원 · 수집 취소 ──
    const collectClinicMatch = (r, clinicSel, partnerName) => {
        const normBiz = (n) => (n || '').replace(/\s|\(.*?\)/g, '').replace(/치과의원|치과병원|치과$/g, '').toLowerCase();
        if (r.partnerId && r.partnerId === clinicSel) return true;
        if (r.partnerId) return false;
        const pn = normBiz(partnerName); const cn = normBiz(r.clientNameInFile);
        return !!(pn && cn && (pn === cn || pn.includes(cn) || cn.includes(pn)));
    };

    const loadCollectSummary = async (sd, ed, clinicSel, nameOverride) => {
        try {
            const uid = user?.companyId || auth.currentUser.uid;
            const partnerName = nameOverride
                || clinics.find(c => c.id === clinicSel)?.name
                || (String(clinicSel).startsWith('agent_') ? String(clinicSel).replace('agent_', '') : '')
                || (String(clinicSel).startsWith('email_') ? String(clinicSel).replace('email_', '') : '');
            const snap = await getDocs(query(collection(db, 'parsedCases'),
                where('labId', '==', uid), where('status', 'in', ['pending', 'imported', 'excluded'])));
            const rows = snap.docs.map(d => ({ id: d.id, ...d.data() })).filter(r => {
                if (r.sourceType === 'scan_only') return false;   // 스캔만 있는 폴더(명세서용 아님) — 수집 현황 요약·제외목록에서도 제외 (2026-08-08)
                if (!collectClinicMatch(r, clinicSel, partnerName)) return false;
                const dd = (r.fileMtime || '').slice(0, 10);
                if (sd && dd < sd) return false;
                if (ed && dd > ed) return false;
                return true;
            });
            const excludedRows = rows.filter(r => r.status === 'excluded');
            setCollectSummary({
                total: rows.length,
                included: rows.filter(r => r.status === 'imported').length,
                excludedRows,
                // 안내줄 N 계산용 (2026-08-31): 대기(pending) 건 id — 화면에 이미 담긴 건은 표시 시점에 제외한다
                // (통합 뷰에선 저장 전까지 DB status가 pending이라, 이 제외 없이는 과대집계)
                pendingIds: rows.filter(r => r.status === 'pending').map(r => r.id),
            });
            setShowExcluded(false);
        } catch (e) {
            console.warn('수집 현황 요약 실패:', e);
            setCollectSummary(null);
        }
    };

    // 제외 복원 — 대기 상태로 되돌림. [수집분 불러오기]를 누르면 항목에 다시 들어온다 (2026-08-31 버튼 체계)
    const restoreParsedCase = async (row) => {
        try {
            await updateDoc(doc(db, 'parsedCases', row.id), { status: 'pending', excludedAt: deleteField() });
            setCollectSummary(prev => prev ? {
                ...prev,
                excludedRows: prev.excludedRows.filter(r => r.id !== row.id),
                pendingIds: [...(prev.pendingIds || []), row.id],
            } : prev);
            alert('복원했습니다. [담기]를 누르면 항목에 포함됩니다.');
        } catch (e) {
            console.error('복원 실패:', e);
            alert('복원에 실패했습니다.');
        }
    };

    // 수집 취소 (구 수집함 [되돌리기]) — 연결 의뢰서 삭제 + 수집 대기 복귀
    const cancelCollectedItem = async (item) => {
        const used = (item.orderId && usedOrderMap[item.orderId])
            || (item.parsedCaseId && usedOrderMap[item.parsedCaseId]);   // 키 = orderId ∪ parsedCaseId (2단계)
        const warn = used && used.status === 'confirmed'
            ? `\n\n⚠️ 이 건은 이미 발행된 명세서(${used.number || ''})에 포함돼 있습니다. 발행본 금액과 달라질 수 있으니 확인 후 진행하세요.` : '';
        if (!window.confirm(`${item.patientName || '이 건'}을 수집 대기로 되돌릴까요?\n연결된 의뢰서가 삭제되고 이 명세서에서 빠집니다.${warn}`)) return;
        try {
            const uid = user?.companyId || auth.currentUser.uid;
            // 통합 뷰(B) 표시에서 온 가상 id (case:<caseId>) — 삭제할 주문 문서가 없다 (2026-08-17)
            const virtualCaseId = String(item.orderId || '').startsWith('case:')
                ? String(item.orderId).slice(5) : null;
            // 연결 수집 건 찾기: 가상 id면 그 자체, 아니면 의뢰서의 parsedCaseId 우선, 없으면(구버전) orderId 역참조
            let caseId = virtualCaseId;
            if (!caseId) {
                try {
                    const od = await getDoc(doc(db, 'workOrders', item.orderId));
                    if (od.exists()) caseId = od.data().parsedCaseId || null;
                } catch (_) { /* 의뢰서가 이미 없어도 진행 */ }
            }
            if (!caseId) {
                const cs = await getDocs(query(collection(db, 'parsedCases'),
                    where('labId', '==', uid), where('orderId', '==', item.orderId)));
                if (!cs.empty) caseId = cs.docs[0].id;
            }
            if (!virtualCaseId) {
                try { await deleteDoc(doc(db, 'workOrders', item.orderId)); } catch (e) { console.warn('의뢰서 삭제 실패(이미 없음 가능):', e); }
            }
            // statementId 도 지운다 — 남겨 두면 저장 때 되돌림(P1-2)·수집함 배지가 이 명세서를 계속 가리킨다 (2026-10-24 재점검)
            if (caseId) await updateDoc(doc(db, 'parsedCases', caseId), { status: 'pending', orderId: null, importedAt: null, statementId: deleteField() });
            // 재로드 시 자동등록이 이 건을 새 의뢰서로 다시 만들어도 병합에서 제외되도록 기억 (2026-08-05)
            if (caseId) canceledParsedCaseIdsRef.current.add(caseId);
            setItems(prev => {
                // 금값 자식 동반 삭제 (검수 P1-A, 2026-08-20): 부모(수집 행)의 goldLineId를 모아
                // goldForItemId가 거기 속한 gold 행도 함께 제거 — 고아 금값이 합계에 남는 유령 금액 방지
                const goldKeys = new Set(prev.filter(i => i.orderId === item.orderId && i.goldLineId).map(i => i.goldLineId));
                return prev.filter(i => i.orderId !== item.orderId && !(i.itemType === 'gold' && goldKeys.has(i.goldForItemId)));
            });
            setCollectSummary(prev => prev ? { ...prev, included: Math.max(0, prev.included - 1) } : prev);
            if (!caseId) alert('의뢰서는 삭제했지만 연결된 수집 건을 찾지 못했습니다. 수집 대기 복귀는 되지 않았을 수 있습니다.');
        } catch (e) {
            console.error('수집 취소 실패:', e);
            alert('수집 취소에 실패했습니다.');
        }
    };

    const excludeParsedCase = async (id) => {
        // (2026-10-23 1단계 rules) 수집 건 [제외]는 기공소 대표만 — 직원 계정은 거부되므로 미리 안내
        if (isLabStaff(user)) { alert('수집 건 제외는 대표 계정에서만 할 수 있습니다.\n(청구 누락을 막기 위한 제한입니다)'); return; }
        if (!window.confirm('이 수집 건을 명세서 대상에서 제외할까요? (제외 목록에서 복원 가능)')) return;
        try {
            await updateDoc(doc(db, 'parsedCases', id), { status: 'excluded', excludedAt: new Date() });
            setParsedCases(prev => prev.filter(r => r.id !== id));   // 성공 시에만 목록에서 제거
        } catch (e) {
            console.error('제외 처리 실패:', e?.code || e?.message);
            alert(e?.code === 'permission-denied'
                ? '제외 권한이 없습니다. 수집 건 제외는 대표 계정에서만 할 수 있습니다.'
                : '제외 처리에 실패했습니다. 잠시 후 다시 시도해주세요.');
        }
    };

    // (2026-07-24) [CAD] 버튼 제거 — 수집 건은 이미 CAD 작업이 끝난 파일이라 무의미.
    // CAD로 보내기는 치과 발신 의뢰서(주문목록 수신 행)에 배치.

    // ── [📥 수집분 불러오기] (2026-08-31 대표 확정) ─────────────────────────
    // 수집분은 자동 조회에서 항목에 넣지 않는다 — 이 버튼을 눌렀을 때만 현재 항목에 병합.
    // 안내줄 N: 기간 내 대기(pending) 수집 건에서 "화면에 이미 담긴 건"을 뺀 수.
    // (통합 뷰에선 저장 전까지 DB status가 pending으로 남아, 이 제외 없이는 과대집계 — 검증 P1)
    const pendingCollectCount = React.useMemo(() => {
        const ids = collectSummary?.pendingIds || [];
        if (!ids.length) return 0;
        const inScreen = new Set(items.map(i => i.parsedCaseId).filter(Boolean));
        items.forEach(i => {
            const k = String(i.orderId || '');
            if (k.startsWith('case:')) inScreen.add(k.slice(5));   // 통합 뷰 가상 id → caseId
        });
        // 한 번이라도 담았던 case: 매핑 + 이 draft에서 [수집 취소]한 건(부활 금지)도 제외
        seenOrderIdsRef.current.forEach(k => {
            const s = String(k || '');
            if (s.startsWith('case:')) inScreen.add(s.slice(5));
        });
        canceledParsedCaseIdsRef.current.forEach(id => inScreen.add(id));
        return ids.filter(id => !inScreen.has(id)).length;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [collectSummary, items]);

    // ── 임플란트 케이스 힌트 (2026-09-01 대표 승인) ──────────────────────────
    // 실측상 임플란트 케이스의 ~25%가 CAD 파일에 임플 표시가 없어 수집분이 일반 크라운으로 청구됨.
    // 웹 의뢰서(치과 발신)에는 치과가 임플란트를 표시하므로, 그 환자명 집합을 만들어
    // 수집 크라운 행에 "임플란트일 수 있음" 배지로 단가 확인을 유도한다. 추가 쿼리 없음(searchedOrders 재사용).
    const implantPatientSet = React.useMemo(() => {
        const norm = (n) => (n || '').replace(/[\s\d\-_.()[\]]/g, '').toLowerCase();
        const pat = /임플|implant|abut|어버트/i;
        const hasImplantMark = (o) =>
            (o.implantGroups || []).length > 0
            || (o.teethDetails || []).some(d => (d.implant || '').trim() || pat.test(`${d.prosthesis || ''} ${d.type || ''}`))
            || (o.items || []).some(it => (it.implant || '').trim() || pat.test(`${it.prosthesis || ''} ${it.type || ''}`));
        const set = new Set();
        searchedOrders.forEach(o => {
            if (o.source === 'agent_auto') return;   // 웹 의뢰서만 근거로 사용 (수집분끼리는 근거 아님)
            if (!hasImplantMark(o)) return;
            const n = norm(o.patientName);
            if (n) set.add(n);
        });
        return set;
    }, [searchedOrders]);

    // 버튼 실행: 지정 기간의 수집분을 현재 항목에 즉시 병합 (미리보기 없음 — 대표 확정).
    // 플래그 상태와 무관하게 성립 — write=true(구 방식)면 handleSearch 안에서 자동등록이 돌고,
    // display=true(통합 뷰)면 mergeCases 경로가 케이스를 직접 읽는다. 안전장치(재합산 금지·
    // 취소 건 부활 금지·agentDeduped2 병합)는 기존 handleSearch 병합 규칙 그대로.
    // extraOpts(2026-10-23 자동 초안 전용): { onlyCaseIds:Set, skipIds:Set } — handleSearch 병합 거름으로 전달.
    // 반환: { ok, added, skippedBilled } (ok=false면 담기 실패 — 자동 초안이 '만들어졌습니다' 대신 실패 안내)
    const loadCollectedIntoItems = async (sdArg, edArg, extraOpts = null) => {
        const result = { ok: false, added: 0, skippedBilled: 0 };
        if (!selectedClinic || isManualMode) { alert('거래처를 먼저 선택해주세요.'); return result; }
        const sd = sdArg || collectFrom || startDate;
        const ed = edArg || collectTo || endDate;
        if (!sd || !ed) { alert('불러올 기간을 선택해주세요.'); return result; }
        if (collectLoading) return result;
        try {
            setCollectLoading(true);
            // (2026-08-31 검수 P2) 단가표 로드 완료 전에 버튼을 누르면 state priceList가 비어
            // 수집 행이 0원으로 들어감 — 자동 조회처럼 최신 단가표를 직접 확보해 넘긴다.
            // (loadPriceList는 실패 시 []를 반환 — 빈 결과면 현행 state 폴백)
            let freshPl;
            try { freshPl = await loadPriceList(selectedClinic); } catch (e) { console.warn('수집분 불러오기 단가표 로드 실패 — 현행 단가표로 진행:', e); }
            await handleSearch(sd, ed, (Array.isArray(freshPl) && freshPl.length > 0) ? freshPl : undefined, {
                merge: true,
                includeCollected: true,
                clinicName: clinics.find(c => c.id === selectedClinic)?.name || '',
                existingOrderIds: seenOrderIdsRef.current,
                ...(extraOpts || {}),
                result,
            });
        } finally {
            setCollectLoading(false);
        }
        return result;
    };

    // 로드한 단가표를 반환도 함 — 자동 조회가 state 반영 전에 최신 단가표를 쓸 수 있도록
    // 2026-07-30 B안(대표 승인): 미가입 치과(agent_/email_ 이름 키)도 단가표 저장·사용 가능.
    // 정확 키가 없으면 치과 이름으로 폴백 매칭, 실 거래처 키로 조회 시 이름 키 단가표가 있으면 자동 승계 복사.
    const loadPriceList = async (partnerId) => {
        try {
            const uid = user?.companyId || auth.currentUser.uid;
            const isPseudo = partnerId.startsWith('email_') || partnerId.startsWith('agent_');
            const docRef = doc(db, 'priceLists', `${uid}_${partnerId}`);
            let docSnap = null;
            try { docSnap = await getDoc(docRef); } catch (e0) {
                // 미생성 문서 read 거부(permission-denied) = 없음. 그 외(네트워크 등)는 진짜 실패 — 빈 단가표로 취급하지 않는다 (2026-10-23 P1)
                if (e0?.code !== 'permission-denied') throw e0;
            }
            if (docSnap && docSnap.exists()) {
                const normalized = normalizePriceItems(docSnap.data().items);
                setPriceList(normalized);
                setPriceLoadedClinic(partnerId);
                setPriceListLoadFailed(false);
                return normalized;
            }
            // 이름 폴백: 같은 치과가 다른 키(agent_↔email_↔실ID)로 저장해둔 단가표 찾기
            const nb = (n) => (n || '').replace(/\s|\(.*?\)/g, '').replace(/치과의원|치과병원|치과$/g, '').toLowerCase();
            const myName = clinics.find(c => c.id === partnerId)?.name
                || partnerId.replace(/^agent_|^email_/, '');
            if (nb(myName)) {
                const q0 = await getDocs(query(collection(db, 'priceLists'), where('userId', '==', uid)));
                const hit = q0.docs.find(d0 => {
                    const x = d0.data();
                    return (x.items || []).length && nb(x.partnerName) === nb(myName);
                });
                if (hit) {
                    const normalized = normalizePriceItems(hit.data().items);
                    // 가입 시 자동 승계: 실 거래처 키로 조회 중인데 이름 키 단가표만 있으면 실 키로 복사
                    if (!isPseudo) {
                        setDoc(docRef, {
                            ...hit.data(), userId: uid, partnerId,
                            partnerName: myName || hit.data().partnerName || '',
                            migratedFrom: hit.id, updatedAt: Timestamp.now(),
                        }).catch((e) => console.warn('단가표 승계 복사 실패:', e));
                    }
                    setPriceList(normalized);
                    setPriceLoadedClinic(partnerId);
                    setPriceListLoadFailed(false);
                    return normalized;
                }
            }
            setPriceList([]);
            setPriceLoadedClinic(partnerId);
            setPriceListLoadFailed(false);
            return [];
        } catch (e) {
            console.error('단가표 로드 실패:', e);
            setPriceList([]);
            setPriceListLoadFailed(true);   // 화면 빨간 안내줄 + 단가 학습 중단 (빈 단가표로 기존 단가표 덮어쓰기 방지)
            return [];
        }
    };

    // 단가표 모달 열기 — 행 구성은 PriceListModal이 스스로 로드 (2026-07-30 통일)
    const openPriceListModal = () => {
        if (!selectedClinic) {
            alert('거래처를 먼저 선택해주세요.');
            return;
        }
        setShowPriceListModal(true);
    };

    // 거래처 현황에서 [단가표]로 진입 시 — 단가표 로드가 끝난 뒤 자동으로 관리 모달 열기 (2026-07-30)
    // (로드 전에 열면 기존 단가가 빈 표로 보여 저장 시 날아가는 사고 방지 — 로드 완료 신호 대기)
    useEffect(() => {
        if (!initialOpenPrice || priceAutoOpened.current) return;
        if (!selectedClinic || selectedClinic !== initialClinicId) return;
        priceAutoOpened.current = true;
        setShowPriceListModal(true);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [initialOpenPrice, selectedClinic]);

    // 단가표 일괄 적용
    const applyPriceList = () => {
        if (priceList.length === 0) {
            alert('저장된 단가표가 없습니다. 단가표를 먼저 등록해주세요.');
            return;
        }
        let matchCount = 0;
        let remakeSkipped = 0;
        const updated = items.map(item => {
            if (item.itemType === 'gold') return item;   // 금값 파생 행은 중량×시세 고정 — 단가표 일괄 적용에서 제외
            const match = findPriceMatch(priceList, item.toothInfo, item.serviceName, item.material);
            // 재제작(리메이크) 행은 일괄 적용에서 제외 (2026-09-09 대표 승인 — 치과가는날 8월분 실측:
            //   단가 0+'무상 여부 확인'으로 들어온 리메이크 5건을 이 버튼이 덮어써 35,000~40,000원으로 발행됨).
            //   무상/유상은 사람이 정한다(2026-08-11 b안) — 단가표 값은 '유상이면 N원' 제안으로만 남긴다.
            if (item.isRemake || item.needsPriceCheck) {
                remakeSkipped++;
                return match && !item.suggestedUnitPrice ? { ...item, suggestedUnitPrice: match.unitPrice } : item;
            }
            if (match) {
                matchCount++;
                return { ...item, unitPrice: match.unitPrice, priceSource: 'auto' };
            }
            return item;
        });
        setItems(updated);
        alert(`${items.length}건 중 ${matchCount}건에 단가가 적용되었습니다.` +
            (remakeSkipped > 0 ? `\n재제작 ${remakeSkipped}건은 자동 적용하지 않았습니다 — 무상이면 0원 그대로, 유상이면 직접 입력하세요.` : ''));
    };

    // 단가 학습: 수동 입력한 단가를 "현재 거래처의 단가표"에만 반영 (치과마다 단가가 다름 — 타 거래처 전파 금지)
    const learnPricesToPriceList = async () => {
        try {
            if (!selectedClinic || selectedClinic === '__manual__') return;   // 2026-07-30 B안: 미가입(이름 키) 거래처도 학습 허용
            if (priceListLoadFailed) { console.warn('단가표 로드 실패 상태 — 단가 학습 건너뜀'); return; }

            // 수동 입력(priceSource==='manual') + 단축어 파악됨 + 0원 초과 항목만 후보
            const seen = new Set();
            const candidates = [];
            items.forEach(item => {
                if (item.priceSource !== 'manual' || item.isRemake || item.needsPriceCheck) return;   // 리메이크 반값이 기본 단가로 학습되지 않게 (2026-10-23 P2)
                const sn = (item.serviceName || '').trim();
                const price = item.unitPrice || 0;
                if (!sn || price <= 0) return;
                const mat = (item.material || '').trim();
                const key = `${normKey(sn)}|${normKey(mat)}`;
                if (seen.has(key)) return;
                seen.add(key);
                const existing = priceList.find(p => normKey(p.serviceName) === normKey(sn) && normKey(p.material) === normKey(mat));
                if (existing && (existing.unitPrice || 0) === price) return; // 변화 없음
                candidates.push({ serviceName: sn, material: mat, unitPrice: price, prev: existing ? (existing.unitPrice || 0) : null });
            });
            if (candidates.length === 0) return;

            const partnerName = clinics.find(c => c.id === selectedClinic)?.name || '거래처';
            const lines = candidates.map(c =>
                `· ${c.serviceName}${c.material ? ` (${c.material})` : ''}: ${c.prev != null ? `${c.prev.toLocaleString()}원 → ` : ''}${c.unitPrice.toLocaleString()}원`
            ).join('\n');
            if (!window.confirm(`직접 입력한 단가 ${candidates.length}건을 "${partnerName}" 단가표에 저장할까요?\n(이 거래처 단가표에만 반영되며, 다른 거래처에는 영향이 없습니다)\n\n${lines}`)) return;

            // (2026-10-23 P1) 저장 직전 최신 단가표를 다시 읽어 그 위에 합친다 — 다른 화면·기기에서 바꾼 단가를 덮어쓰지 않게.
            // 미생성 문서(permission-denied)는 화면 단가표(이름 폴백 포함) 기준, 그 외 읽기 실패면 학습 중단.
            const uid = user?.companyId || auth.currentUser.uid;
            const plRef = doc(db, 'priceLists', `${uid}_${selectedClinic}`);
            let baseList = priceList;
            try {
                const freshPl = await getDoc(plRef);
                if (freshPl.exists()) baseList = normalizePriceItems(freshPl.data().items);
            } catch (e1) {
                if (e1?.code !== 'permission-denied') {
                    console.error('단가 학습 전 단가표 재조회 실패 — 학습 중단:', e1);
                    alert('단가표를 다시 읽지 못해 단가표 저장을 건너뜁니다. 명세서 저장에는 영향이 없습니다.');
                    return;
                }
            }
            const merged = baseList.map(p => ({ ...p }));
            candidates.forEach(c => {
                const idx = merged.findIndex(p => normKey(p.serviceName) === normKey(c.serviceName) && normKey(p.material) === normKey(c.material));
                const row = {
                    serviceName: c.serviceName, unitPrice: c.unitPrice, material: c.material,
                    source: 'learned', name: c.serviceName, price: c.unitPrice,
                };
                if (idx >= 0) merged[idx] = { ...merged[idx], ...row };
                else merged.push(row);
            });

            await setDoc(plRef, {
                userId: uid,
                partnerId: selectedClinic,
                partnerName,
                items: merged,
                updatedAt: Timestamp.now(),
            }, { merge: true });
            setPriceList(merged);
            // 반영된 항목은 auto로 전환해 다음 저장 때 중복 제안 방지
            setItems(prev => prev.map(it => it.priceSource === 'manual' ? { ...it, priceSource: 'auto' } : it));
        } catch (e) {
            console.warn('단가 학습 저장 실패:', e); // 학습 실패는 명세서 저장 자체에 영향 없음
        }
    };

    // 직접 입력 모드인지 확인
    const isManualMode = selectedClinic === '__manual__';
    // 파일에서 이름만 읽어온 미가입 치과를 고른 상태 (덴클 계정이 없어 알림·단가표가 없음)
    const isAgentClinicSelected = String(selectedClinic || '').startsWith('agent_')
        || clinics.find(c => c.id === selectedClinic)?.source === 'agent';
    // CAD 디자인 외주 기공소를 고른 상태 — 명세서에 외주 도장(kind)이 찍히고 4.5% 수수료 대상이 된다
    const selectedOutsourcePartner = clinics.find(c => c.id === selectedClinic && c.source === 'design_outsource') || null;

    // ── 거래명세서 1단계 (2026-10-23): 거래처 키·청구 마감일·받을 돈·전월 미수 ──
    // 받을 돈은 기공소 대표 화면에서만(직원 X). 이 화면은 /transactions(PIN 구역) 안에서 열린다.
    const isLabOwner = ['lab', 'outsource'].includes(user?.businessType || user?.companyBusinessType || '') && !isLabStaff(user);
    const carryLabId = user?.companyId || auth.currentUser?.uid || '';
    const selectedClinicName = clinics.find(c => c.id === selectedClinic)?.name || '';
    // 직접 입력·외주 거래처는 원장 대상 아님(명세 §4-4)
    const selectedPartnerKey = (!selectedClinic || isManualMode || selectedOutsourcePartner)
        ? '' : ledgerPartnerKeyOf(selectedClinic, selectedClinicName);
    const selectedClosingDay = selectedPartnerKey ? (labBilling?.partners?.[selectedPartnerKey]?.closingDay ?? null) : null;
    const selectedClosingPeriod = selectedClosingDay ? lastClosedPeriod(selectedClosingDay) : null;
    const selectedRecv = selectedPartnerKey && recvByPartner ? recvByPartner[selectedPartnerKey] : null;
    // 전월 미수 줄·발행 반영 조건: 대표 · 원장 대상 거래처 · 발행본 수정 아님 · 미리 계산한 금액 > 0
    const carryOverAllowed = isLabOwner && !!selectedPartnerKey && !isEditingPublished
        && !!carryOverPreview && carryOverPreview.amount > 0;
    const isNonMemberPartner = !!selectedPartnerKey && !isMemberPartnerKey(selectedPartnerKey);

    // parsedCase 1건 → workOrder items (임플란트·쉐이드·브릿지 포함)
    const buildItemsFromParsed = (r) => {
        const indication = (r.indications || [])[0] || '';
        return (r.teethDetails && r.teethDetails.length)
            ? r.teethDetails.map(d => ({
                toothNumber: d.tooth, prosthesis: d.prosthesis || indication, material: d.material || r.material || '',
                shade: d.shade || '', implant: d.implant || '', ...(d.implantSystem ? { implantSystem: d.implantSystem } : {}), isPontic: !!d.isPontic, isBridge: !!d.isBridge,
            }))
            : (r.teeth || []).map(t => ({
                toothNumber: t, prosthesis: indication, material: r.material || '',
                shade: '', implant: '', isPontic: false, isBridge: false,
            }));
    };

    // 조회 시 자동 실행 — 이 치과·기간의 대기(pending) 파싱분을 의뢰서로 자동 등록
    // ("명세서 만들기" 단계 없이 조회만 하면 거래 내역에 뜨도록. 이미 등록된 건 중복 안 됨)
    // clinicOverride/nameOverride (2026-07-30): 이어쓰기 병합처럼 state가 아직 안 잡힌 시점에 명시적으로 지정
    const autoRegisterParsedForClinic = async (sd, ed, clinicOverride, nameOverride) => {
        // 병렬 실행 차단 — 기간 칩 연타 시 같은 수집 건이 두 번 등록(이중 청구)되는 것 방지
        if (autoRegisterInFlight.current) {
            // (2026-08-13 P0-6) 조용한 early-return에 로그 — 두번째 트리거가 언제 겹쳤는지 흔적을 남긴다
            console.log('자동수집 등록 중복 트리거 skip (선행 실행 진행 중):', sd, ed, clinicOverride || selectedClinic);
            return;
        }
        autoRegisterInFlight.current = true;
        try {
            // ── 케이스 통일 2단계 (2026-08-17 대표 승인·조기 착수): 복사 주문 생성 중단 ──
            // write=false면 수집 케이스를 의뢰서(agent_auto)로 복제하지 않는다. 통합 뷰(display)가
            // parsedCases를 직접 읽어 명세서 내용은 동일하고, imported 전환은 저장 시 markCasesInStatement가 한다.
            // 롤백 = config/features.caseUnify.write=true (배포 불필요).
            try {
                if (!isCaseUnifyWrite(await getFeatures())) {
                    console.log('[케이스통일 2단계] 자동등록(복사 주문) 생성 중단 — 통합 뷰가 케이스를 직접 읽음');
                    return 0;
                }
            } catch (_) { /* 플래그 조회 실패 시 현행(생성) 유지 */ }
            const clinicSel = clinicOverride || selectedClinic;
            const uid = user?.companyId || auth.currentUser.uid;
            const labName = user?.businessName || user?.companyName || user?.name || '기공소';
            const snap = await getDocs(query(collection(db, 'parsedCases'),
                where('labId', '==', uid), where('status', '==', 'pending')));
            const normBiz = (n) => (n || '').replace(/\s|\(.*?\)/g, '').replace(/치과의원|치과병원|치과$/g, '').toLowerCase();
            const partnerName = nameOverride
                || clinics.find(c => c.id === clinicSel)?.name
                || (clinicSel.startsWith('agent_') ? clinicSel.replace('agent_', '') : '')
                || (clinicSel.startsWith('email_') ? clinicSel.replace('email_', '') : '');
            const pn = normBiz(partnerName);
            const rows = snap.docs.map(d => ({ id: d.id, ...d.data() })).filter(r => {
                if (r.sourceType === 'scan_only') return false; // 스캔만 있는 폴더(명세서용 아님) — 의뢰서 자동등록 제외 (2026-08-05)
                // (2026-08-13 P0-2) 이 draft에서 [수집 취소]한 건은 자동 재등록 금지 — 되살아나 이중청구되는 것 방지.
                // (parsedCase는 취소 시 pending으로 되돌려 두므로, 기억해 둔 id로 걸러야 재등록을 막을 수 있다)
                if (canceledParsedCaseIdsRef.current.has(r.id)) return false;
                // (2026-10-24 재점검 P1-3) 등록만 되고 저장 전인 건(pending + orderId)은 의뢰서가 이미 있다 — 다시 만들지 않는다
                if (r.orderId) return false;
                // 치과 매칭: 정확 partnerId 또는 미매칭 건의 이름 유사
                let match = false;
                if (r.partnerId && r.partnerId === clinicSel) match = true;
                else if (!r.partnerId) { const cn = normBiz(r.clientNameInFile); match = !!(pn && cn && (pn === cn || pn.includes(cn) || cn.includes(pn))); }
                if (!match) return false;
                // 기간 매칭 (파일 작업일)
                const d = (r.fileMtime || '').slice(0, 10);
                if (sd && d < sd) return false;
                if (ed && d > ed) return false;
                return true;
            });
            let registered = 0;
            for (const r of rows) {
                try {
                    const fileDate = r.fileMtime ? new Date(r.fileMtime) : new Date();
                    // 로컬(KST) 날짜 기준 — toISOString은 UTC라 오전 9시 이전 작업이 전날 번호로 밀림 (2026-08-26)
                    const dateStr = formatDateHelper(fileDate).replace(/-/g, '');
                    const orderNumber = `AGT-${dateStr}-${Math.random().toString(36).substring(2, 7).toUpperCase()}`;
                    // 실제 uid가 아닌 가짜 키(agent_·email_)는 fromUserId로 쓰지 않는다 —
                    // 넣으면 이름 기반 재조회에서 빠져 다시는 안 잡힌다 (2026-07-30 email_ 추가)
                    const isPseudoKey = String(clinicSel).startsWith('agent_') || String(clinicSel).startsWith('email_');
                    const realPartnerId = r.partnerId || (isPseudoKey ? null : clinicSel);
                    // (2026-08-13 P0-6) pending→imported 선점을 트랜잭션으로 — 다른 화면/탭이 동시에
                    //   같은 수집 건을 등록하는 교차 이중등록을 막는다. 선점 실패(이미 imported) 건은 건너뛴다.
                    const caseRef = doc(db, 'parsedCases', r.id);
                    const orderRef = doc(collection(db, 'workOrders'));
                    const claimed = await runTransaction(db, async (tx) => {
                        const cs = await tx.get(caseRef);
                        if (!cs.exists()) return false;
                        if ((cs.data().status || '') !== 'pending') return false;   // 이미 다른 트리거가 선점
                        if (cs.data().orderId) return false;   // 등록만 된 건(저장 전) — 의뢰서 이중 생성 방지 (P1-3)
                        tx.set(orderRef, {
                            orderNumber,
                            fromUserId: realPartnerId || null,
                            fromUserName: partnerName || r.partnerName || r.clientNameInFile || '치과',
                            toUserId: uid, toUserName: labName,
                            participants: [realPartnerId, uid].filter(Boolean),
                            patientName: displayPatientName(r).name || '환자미상',
                            patientNameRaw: r.patientNameRaw || r.patientName || '',
                            items: buildItemsFromParsed(r),
                            prosthesisGroups: [], bridgeGroups: [], ponticGroups: [], implantGroups: [],
                            dueDate: Timestamp.fromDate(fileDate),
                            specialNotes: `에이전트 자동 수집 (${r.sourceType} · ${r.sourceFile || ''})${r.isRemake ? ' · 리메이크' : ''}`,
                            imageUrls: [],
                            isRemake: !!r.isRemake, remakeType: '', remakeReason: r.isRemake ? '에이전트 감지 (재제작 파일)' : '',
                            ...(r.isRemake && r.remakeOfId ? { remakeOfCaseId: r.remakeOfId } : {}),   // 원 건 id 보존 (2026-10-24 ④)
                            autoCollected: true, source: 'agent_auto', parsedCaseId: r.id,
                            readByReceiver: true,
                            createdAt: Timestamp.fromDate(fileDate), updatedAt: new Date(),
                        });
                        // (P1-3) status 는 pending 그대로 — 'imported' 전환은 저장(markCasesInStatement)에서만. 선점 표식은 orderId.
                        tx.update(caseRef, { orderId: orderRef.id, importedAt: new Date() });
                        return true;
                    });
                    if (claimed) registered++;
                    else console.log('자동수집 선점 실패(이미 등록됨) skip:', r.id);   // 조용한 실패 금지 — 흔적 남김
                } catch (e) { console.error('자동수집 등록 실패:', e); }
            }
            return registered;
        } catch (e) { console.warn('자동수집 자동등록 실패:', e); return 0; }
        finally { autoRegisterInFlight.current = false; }
    };

    // ── 케이스 통일 섀도 비교 (1단계, 2026-08-09) ────────────────────────────
    // 현행 결과(A: agent_auto 주문 기반)와 신규 통합 뷰 결과(B: parsedCases 직접 읽기)를 둘 다 계산해
    // **B는 화면에 쓰지 않고** 차이만 콘솔에 남긴다. 플래그 config/features.caseUnify.read=true일 때만 동작.
    // 어떤 실패도 조회 흐름을 막지 않는다(경고 로그로 남김 — 조용한 실패 금지).
    const runCaseUnifyShadow = async ({ rawOrders, sd, ed, clinicSel, clinicName, currentItems, pl, uid }) => {
        try {
            const features = await getFeatures();
            if (!isCaseUnifyRead(features)) return;
            const partnerName = clinicName
                || clinics.find(c => c.id === clinicSel)?.name
                || (String(clinicSel).startsWith('agent_') ? String(clinicSel).replace('agent_', '') : '')
                || (String(clinicSel).startsWith('email_') ? String(clinicSel).replace('email_', '') : '');
            const labName = user?.businessName || user?.companyName || user?.name || '기공소';
            const cases = await loadCasesForClinic({ labId: uid, clinicSel, partnerName, from: sd, to: ed });
            const rows = mergeCases({
                webOrders: rawOrders.filter(o => o.source !== 'agent_auto'),
                legacyAgentOrders: rawOrders.filter(o => o.source === 'agent_auto'),
                cases, basis: dateBasis, partnerName, labId: uid, labName,
            });
            const nextItems = buildItemsFromOrders(ordersOf(rows), { priceList: pl });
            logShadowDiff(`명세서 작성 · ${partnerName} · ${sd}~${ed}`, diffItems(currentItems, nextItems), {
                주문: rawOrders.length, 수집케이스: cases.length, 통합행: rows.length,
                출처: rows.reduce((a, r) => { a[r.origin] = (a[r.origin] || 0) + 1; return a; }, {}),
            });
        } catch (e) {
            console.warn('[케이스통일 섀도] 비교 실패:', e);
        }
    };

    // 의뢰서 조회
    // opts.merge (2026-07-30): 이어쓰기(draft) 진입 시 기존 항목을 유지한 채 새 수집분만 추가.
    // opts.clinic / opts.clinicName: draft 로드 직후 state가 아직 안 잡혀 있어 명시적으로 넘긴다.
    const handleSearch = async (sdOverride, edOverride, plOverride, opts = {}) => {
        // 자동 조회에서 넘어온 날짜/단가표가 있으면 상태보다 우선 사용 (stale state 방지)
        const sd = typeof sdOverride === 'string' ? sdOverride : startDate;
        const ed = typeof edOverride === 'string' ? edOverride : endDate;
        const activePriceList = Array.isArray(plOverride) ? plOverride : priceList;
        const clinicSel = opts.clinic || selectedClinic;
        // (2026-08-31 대표 확정) 수집분 합산은 [📥 수집분 불러오기] 버튼에서만 (opts.includeCollected=true).
        // 자동 조회(거래처 선택·기간 칩·[조회]·이어쓰기·statementId 진입)는 수집 유입 3종을 전부 차단하고
        // 웹 의뢰서(치과 발신)만 현행대로 자동 합산한다.
        // 예외(2026-08-31 대표 지시): 미가입 치과(agent_/email_)는 내역이 전부 수집분이라 버튼만으로는
        // 늘 빈 목록으로 시작 — 이 거래처들만 종전대로 자동 합산 유지.
        const isUnregisteredClinic = String(clinicSel).startsWith('agent_') || String(clinicSel).startsWith('email_');
        const includeCollected = !!opts.includeCollected || isUnregisteredClinic;
        if (!clinicSel) {
            alert('업체를 선택해주세요.');
            return;
        }
        if (!opts.merge && isManualMode && !manualClinicName.trim()) {
            alert('업체명을 입력해주세요.');
            return;
        }

        // 직접 입력 모드: 날짜 검색 없이 곧바로 빈 항목으로 시작
        if (!opts.merge && isManualMode) {
            setItems([]);
            handleAddItem();
            return;
        }

        if (!sd || !ed) {
            alert('날짜 범위를 선택해주세요.');
            return;
        }

        try {
            setLoading(true);
            const ordersRef = collection(db, 'workOrders');
            const uid = user?.companyId || auth.currentUser.uid;
            const isEmailPartner = clinicSel.startsWith('email_');
            const isAgentPartner = clinicSel.startsWith('agent_');
            // CAD 외주 거래처 — 내역이 workOrders가 아니라 designOutsourceRequests에 있다.
            // 08-14 통합 때 거래처 선택·kind 태그만 붙고 이 연결이 빠져 목록이 늘 비었음 (대표 발견 2026-08-22)
            const isOutsourceSel = !!clinics.find(c => c.id === clinicSel && c.source === 'design_outsource');

            // 이 치과·기간의 대기 파싱분을 의뢰서로 자동 등록 → 아래 조회에 함께 잡힘
            // (2026-07-30: 이메일 거래처도 포함 — 수집분을 못 불러오던 원인이었음)
            // 외주 거래처는 수집분(치과 케이스)과 무관하므로 건너뜀
            // (2026-08-31 차단①-보조) 자동 조회에서는 자동등록을 호출하지 않는다 —
            // [수집분 불러오기] 버튼(includeCollected)에서만 실행 (함수는 보존, 플래그 write=true 대비)
            if (!isOutsourceSel) {
                if (includeCollected) await autoRegisterParsedForClinic(sd, ed, clinicSel, opts.clinicName);
                loadCollectSummary(sd, ed, clinicSel, opts.clinicName);   // 수집 현황 요약줄·안내줄 N (비동기, 실패해도 조회는 계속)
            } else {
                setCollectSummary(null);   // 직전 거래처의 요약이 외주 화면에 남지 않게 (2026-08-31)
            }

            let allOrders = [];

            // (2026-08-26 성능) 이름 매칭용 전량 스캔에 조회 기간 ±40일 createdAt 범위 제한
            // (자동수집 의뢰서는 createdAt=파일 작업일이라 마감일 기준 조회도 이 여유면 안전)
            const SCAN_MARGIN_MS = 40 * 24 * 3600 * 1000;
            const scanLo = new Date(new Date(`${sd}T00:00:00`).getTime() - SCAN_MARGIN_MS);
            const scanHi = new Date(new Date(`${ed}T23:59:59`).getTime() + SCAN_MARGIN_MS);

            if (isOutsourceSel) {
                // 수주한 외주 의뢰 — 나=toLabId(공급자), 거래처=fromLabId(발주랩). 수락 이후 건만 청구 대상.
                const doSnap = await getDocs(query(collection(db, 'designOutsourceRequests'),
                    where('toLabId', '==', uid), where('fromLabId', '==', clinicSel)));
                allOrders = doSnap.docs
                    .map(d => ({ id: d.id, ...d.data() }))
                    .filter(r => !['requested', 'rejected', 'cancelled'].includes(r.status))
                    .map(r => {
                        let rowItems = (r.teethDetails || []).length
                            ? r.teethDetails.map(t => ({
                                toothNumber: t.tooth, prosthesis: t.prosthesis || r.prosthesisType || '',
                                material: t.material || '', shade: t.shade || '', implant: t.implant || '',
                                isPontic: !!t.isPontic, isBridge: !!t.isBridge,
                            }))
                            : String(r.teeth || '').split(/[,\s]+/).map(s => s.replace(/^#/, '').trim()).filter(Boolean)
                                .map(t => ({ toothNumber: t, prosthesis: r.prosthesisType || '', material: '', shade: '', implant: '', isPontic: false, isBridge: false }));
                        // 치식 정보가 전혀 없어도 보철 종류가 있으면 1행은 만든다 (빈 의뢰서 필터에 걸려 사라지는 것 방지)
                        if (!rowItems.length && r.prosthesisType) {
                            rowItems = [{ toothNumber: '', prosthesis: r.prosthesisType, material: '', shade: '', implant: '', isPontic: false, isBridge: false }];
                        }
                        return {
                            id: r.id,
                            source: 'design_outsource',
                            patientName: r.patientName || r.caseNo,   // 마스킹 저장본(김O수) — 실명은 애초에 문서에 없음
                            caseNo: r.caseNo,
                            createdAt: r.requestedAt || r.createdAt,
                            dueDate: r.dueDate || '',
                            items: rowItems,
                        };
                    });
            } else if (isAgentPartner) {
                // 미연결 치과 — fromUserId가 없으므로 내 수신 의뢰서 중 치과명이 일치하는 것만 필터
                const clinicName = clinicSel.replace("agent_", "");
                const norm = (s) => (s || '').replace(/\s|\(.*?\)/g, '').replace(/치과의원|치과병원|치과$/g, '').toLowerCase();
                const target = norm(clinicName);
                const snap = await getDocs(query(ordersRef, where('toUserId', '==', uid),
                    where('createdAt', '>=', scanLo), where('createdAt', '<=', scanHi), orderBy('createdAt', 'desc')));
                allOrders = snap.docs.map(d => ({ id: d.id, ...d.data() }))
                    .filter(o => !o.fromUserId && norm(o.fromUserName) === target);
            } else if (isEmailPartner) {
                // 이메일 파싱 거래처 — clinicEmailMapping에서 원래 clinicId 찾아서 조회
                const emailClinicName = clinicSel.replace("email_", "");
                console.log('📧 이메일 거래처 조회 시작');   // 이름·uid는 로그에 남기지 않는다 (2026-08-26)

                // labSettings에서 해당 치과의 원래 clinicId 찾기
                const labSettingsDoc2 = await getDoc(doc(db, 'labSettings', uid));
                const mapping = labSettingsDoc2.exists() ? (labSettingsDoc2.data()?.clinicEmailMapping || {}) : {};

                // clinicName이 매칭되는 모든 clinicId로 조회
                const matchingClinicIds = Object.entries(mapping)
                    .filter(([, data]) => (data.clinicName || '').trim() === emailClinicName)
                    .map(([id]) => id);

                console.log('📧 매칭된 clinicId 수:', matchingClinicIds.length);   // id 값은 로그 생략 (2026-08-26)

                for (const cId of matchingClinicIds) {
                    // fromUserId = clinicId로 조회 (Firestore 보안 규칙 충족)
                    const emailQuery = query(
                        ordersRef,
                        where('toUserId', '==', uid),
                        where('fromUserId', '==', cId)
                    );
                    const emailSnapshot = await getDocs(emailQuery);
                    allOrders.push(...emailSnapshot.docs.map(d => ({ id: d.id, ...d.data() })));
                }

                // fromUserId가 null인 이메일 주문도 시도 (participants 기반)
                if (matchingClinicIds.length === 0) {
                    // 매핑이 없는 경우 — 직접 입력 모드로 처리
                    console.log('📧 매핑된 clinicId 없음, 직접 입력 모드');
                }
                // [2026-07-30] 에이전트 수집분 합류 — 수집분 의뢰서는 fromUserId가 비어 있어
                // 위의 clinicId 조회로는 절대 잡히지 않는다. 이메일 거래처와 이름이 같은
                // 무소속(fromUserId 없음) 의뢰서를 이름으로 함께 끌어온다.
                // (이 처리가 없으면 우리미소·연세문처럼 이메일 거래처로 잡힌 치과는
                //  수집함에서 명세화한 건을 명세서 작성 화면에서 영영 불러오지 못한다)
                {
                    const norm2 = (s) => (s || '').replace(/\s|\(.*?\)/g, '').replace(/치과의원|치과병원|치과$/g, '').toLowerCase();
                    const target2 = norm2(emailClinicName);
                    const allSnap = await getDocs(query(ordersRef, where('toUserId', '==', uid),
                        where('createdAt', '>=', scanLo), where('createdAt', '<=', scanHi), orderBy('createdAt', 'desc')));
                    const orphans = allSnap.docs.map(d => ({ id: d.id, ...d.data() }))
                        .filter(o => !o.fromUserId && target2 && norm2(o.fromUserName) === target2);
                    allOrders.push(...orphans);
                }
                console.log('📧 조회된 주문 수:', allOrders.length);
            } else {
                // 일반 거래처 — 기존 방식 (toUserId/fromUserId)
                const receivedQuery = query(
                    ordersRef,
                    where('toUserId', '==', uid),
                    where("fromUserId", "==", clinicSel)
                );
                const receivedSnapshot = await getDocs(receivedQuery);
                allOrders = receivedSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
            }

            // 날짜 필터링
            const start = new Date(sd);
            start.setHours(0, 0, 0, 0); // UTC 파싱으로 시작일 오전 주문이 누락되는 버그 방지
            const end = new Date(ed);
            end.setHours(23, 59, 59, 999);

            // 날짜 기준 = 디자인 완성일 (대표 10-31 "작성일·마감일 선택해서 가져오지 말고 디자인 완성일 기준")
            //   수집분(agent_auto) = CAD 파일 날짜(createdAt 에 들어 있음) / 웹 의뢰서 = 같은 환자·치식 수집분의 날짜, 짝이 없으면 의뢰서 작성일(대표 확정)
            {
                const normD = (n) => (n || '').replace(/[\s\d\-_.()[\]]/g, '').toLowerCase();
                const teethD = (o) => {
                    const t = [];
                    (o.items || []).forEach(it => { if (it.toothNumber) t.push(String(it.toothNumber)); });
                    (o.prosthesisGroups || []).forEach(g => (g.teeth || []).forEach(x => t.push(String(x))));
                    (o.bridgeGroups || []).forEach(g => (g.teeth || []).forEach(x => t.push(String(x))));
                    return [...new Set(t)].sort().join(',');
                };
                const keyD = (o) => `${teethD(o)}|${normD(o.patientName)}`;
                const agentAt = new Map();
                allOrders.forEach(o => {
                    if (o.source !== 'agent_auto') return;
                    const t = getTimestamp(o.createdAt);
                    const k = keyD(o);
                    if (t && (!agentAt.has(k) || t < agentAt.get(k))) agentAt.set(k, t);
                });
                allOrders = allOrders.map(o => ({
                    ...o,
                    designDate: o.source === 'agent_auto' ? o.createdAt : (agentAt.has(keyD(o)) ? new Date(agentAt.get(keyD(o))) : o.createdAt),
                }));
            }
            const filteredOrders = allOrders.filter(order => {
                const basisValue = order.designDate;
                if (!basisValue) return false;
                const orderDate = basisValue.toDate ? basisValue.toDate() : new Date(basisValue);
                return orderDate >= start && orderDate <= end;
            });

            // 중복 의뢰서 제거 (문서 id 기준)
            const uniqueOrdersMap = new Map(filteredOrders.map(order => [order.id, order]));
            let uniqueOrders = Array.from(uniqueOrdersMap.values());
            const rawOrdersForShadow = Array.from(uniqueOrdersMap.values());   // 중복제거 전 원본 (섀도 비교용)

            // 웹 의뢰서 우선 통합 — 같은 치식이면서 환자명이 사실상 같으면 웹 것만 남김(파싱분 제외)
            const teethOf = (o) => {
                const teeth = [];
                (o.items || []).forEach(it => { if (it.toothNumber) teeth.push(String(it.toothNumber)); });
                (o.prosthesisGroups || []).forEach(g => (g.teeth || []).forEach(t => teeth.push(String(t))));
                (o.bridgeGroups || []).forEach(g => (g.teeth || []).forEach(t => teeth.push(String(t))));
                return [...new Set(teeth)].sort().join(',');
            };
            // 환자명 정규화 — 공백·숫자·특수문자 제거 ("3한수영" → "한수영")
            const normName = (n) => (n || '').replace(/[\s\d\-_.()[\]]/g, '').toLowerCase();
            // 두 이름이 같은 환자로 볼 수 있는가 — 정규화(숫자·공백·특수문자 제거) 후 완전 일치만 (2026-08-26 대표 확정)
            // 종전의 2글자 포함관계 허용("이수"⊂"이수민")은 다른 환자를 병합하는 오병합 원인이라 제거
            const sameName = (a, b) => {
                if (!a || !b) return false;
                return a === b;
            };
            const webOrders = uniqueOrders.filter(o => o.source !== 'agent_auto');
            const agentOrders = uniqueOrders.filter(o => o.source === 'agent_auto');
            const webCases = webOrders.map(o => ({ n: normName(o.patientName), t: teethOf(o) }));
            const dedupedAgent = agentOrders.filter(ao => {
                const an = normName(ao.patientName), at = teethOf(ao);
                return !webCases.some(w => w.t === at && sameName(w.n, an));
            });
            // 내용 없는 의뢰서 제외 (2026-07-26): 항목·보철 정보가 전혀 없는 빈 의뢰서(테스트 제출 등)는
            // 명세서에 '-' 빈 행만 만들므로 생성 대상에서 뺀다 (의뢰서 자체는 주문 목록에 그대로 남음)
            uniqueOrders = uniqueOrders.filter(o =>
                (o.items || []).length > 0 || (o.prosthesisGroups || []).length > 0 ||
                (o.bridgeGroups || []).length > 0 || (o.ponticGroups || []).length > 0 ||
                (o.implantGroups || []).length > 0 || (o.teethDetails || []).length > 0);
            const webOrders2 = uniqueOrders.filter(o => o.source !== 'agent_auto');
            const agentOrders2 = uniqueOrders.filter(o => o.source === 'agent_auto');
            const webCases2 = webOrders2.map(o => ({ n: normName(o.patientName), t: teethOf(o) }));
            const dedupedAgentB = agentOrders2.filter(ao => {
                const an = normName(ao.patientName), at = teethOf(ao);
                return !webCases2.some(w => w.t === at && sameName(w.n, an));
            });

            // 수집분끼리 중복 제거 (2026-07-26): 같은 케이스가 exocad와 3shape 양쪽 파일로 수집되면
            // 환자명+치식이 같은 건이 두 번 잡힘 — 리메이크 표기가 없는 한 1건만 남김(항목 많은 쪽 우선)
            // (2026-08-05 대표 확정) 병합 키 확장: 작업월·재료가 다르면 별건 —
            // 같은 달 같은 치아라도 PMMA(임시)→지르코니아(최종)처럼 재료가 다르면 각각 청구 대상.
            // 재료 미상(빈값)은 병합 허용. 에이전트 수집 dedupId(치과|환자|치식|작업월|재료) 기준과 동일.
            const monthOf2 = (o) => {
                const t = getTimestamp(o.createdAt);
                if (!t) return '';
                const d = new Date(t);
                return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
            };
            // 재료 별칭 정규화·부분집합 판정 = 공통 정본(shared/material-alias.js, 에이전트 dedupId와 동일 사전 — 2026-08-06 통일)
            const matSetOf2 = (o) => [...new Set((o.items || []).map(it => normMaterialToken(it.material)).filter(Boolean))];
            // 재료 호환 판정 (2026-08-05 보완, 대표 확정): 완전일치 대신 "부분집합 허용" —
            // 임플란트 케이스는 파일에 따라 어버트(Ti) 포함 여부만 달라 같은 케이스가 zirconia vs ti+zirconia로 갈라짐.
            // 한쪽이 다른 쪽에 다 포함되면 같은 케이스로 병합(정보 많은 쪽 유지), 겹치지 않는 재료(pmma↔zirconia 등)는 별건.
            // (빈 집합=미상은 부분집합 판정상 항상 병합 허용 — 기존 유지)
            const matCompat2 = (a, b) => isMaterialSubset(a, b);
            const agentDeduped2 = [];
            for (const ao of dedupedAgentB) {
                const an = normName(ao.patientName), at = teethOf(ao);
                const am = monthOf2(ao), as = matSetOf2(ao);
                // 월 일치 or 작업일 7일 이내 (2026-08-05 대표 확정): 같은 케이스 파일 두 개가
                // 월말·월초로 걸치면(예: 7/30↔8/3) 월 기준만으로는 별건이 되므로 근접일 예외 허용
                const closeInTime2 = (x) => {
                    if (monthOf2(x) === am) return true;
                    const tx = getTimestamp(x.createdAt), ta = getTimestamp(ao.createdAt);
                    return !!(tx && ta && Math.abs(tx - ta) <= 7 * 86400000);
                };
                const dupIdx = agentDeduped2.findIndex(x =>
                    sameName(normName(x.patientName), an) && teethOf(x) === at &&
                    closeInTime2(x) &&
                    matCompat2(matSetOf2(x), as) &&
                    !x.isRemake && !ao.isRemake);
                if (dupIdx >= 0) {
                    // 항목 수가 많은(정보가 풍부한) 쪽을 남기되, 작업일 표시는 빠른 쪽 유지 (대표 확정)
                    const prev = agentDeduped2[dupIdx];
                    const winner = (ao.items || []).length > (prev.items || []).length ? ao : prev;
                    const tPrev = getTimestamp(prev.createdAt), tAo = getTimestamp(ao.createdAt);
                    const earlier = Math.min(tPrev || Infinity, tAo || Infinity);
                    agentDeduped2[dupIdx] = (isFinite(earlier) && earlier !== getTimestamp(winner.createdAt))
                        ? { ...winner, createdAt: new Date(earlier), designDate: new Date(earlier) }
                        : winner;
                } else {
                    agentDeduped2.push(ao);
                }
            }
            // 웹 의뢰서 먼저, 그 다음 에이전트 수집분
            uniqueOrders = [...webOrders2, ...agentDeduped2];

            // 날짜순 정렬 (웹 의뢰서·수집분 구분 없이 빠른 날짜부터 섞어서)
            uniqueOrders.sort((a, b) => getTimestamp(a.designDate || a.createdAt) - getTimestamp(b.designDate || b.createdAt));   // 디자인 완성일순(10-31)

            // ── 케이스 통일 표시 전환 (1단계 마무리, 2026-08-17 대표 확정 "신규가 옳다") ──
            // 플래그 ON이면 항목 생성을 통합 뷰(B: parsedCases 최신 파싱)로 바꾼다. 위 현행(A) 계산은
            // 그대로 남겨 플래그를 내리면 즉시 복귀한다. 케이스에 대응하는 자동등록 주문이 있으면
            // 그 '문서 id·참조'는 유지하고 내용(항목·환자명·작업일)만 최신 파싱으로 쓴다 —
            // 수집 취소·이어쓰기·중복 경고가 전부 실제 주문 id로 계속 동작하게 (참조 재설계는 2단계).
            let unifiedDisplay = false;
            try {
                const feats = await getFeatures();
                unifiedDisplay = isCaseUnifyDisplay(feats);
                if (isOutsourceSel) unifiedDisplay = false;   // 외주 내역은 parsedCases 통합 뷰 대상 아님
                // (2026-08-31 차단①) 통합 뷰 합산(mergeCases)은 [수집분 불러오기] 버튼에서만 —
                // 자동 조회는 아래 현행(A) 결과에서 수집 행을 걸러 웹 의뢰서만 남긴다
                if (!includeCollected) unifiedDisplay = false;
                if (unifiedDisplay) {
                    const partnerName2 = opts.clinicName
                        || clinics.find(c => c.id === clinicSel)?.name
                        || (String(clinicSel).startsWith('agent_') ? String(clinicSel).replace('agent_', '') : '')
                        || (String(clinicSel).startsWith('email_') ? String(clinicSel).replace('email_', '') : '');
                    const labName2 = user?.businessName || user?.companyName || user?.name || '기공소';
                    const cases2 = await loadCasesForClinic({ labId: uid, clinicSel, partnerName: partnerName2, from: sd, to: ed });
                    const rows2 = mergeCases({
                        webOrders: rawOrdersForShadow.filter(o => o.source !== 'agent_auto'),
                        legacyAgentOrders: rawOrdersForShadow.filter(o => o.source === 'agent_auto'),
                        cases: cases2, basis: dateBasis, partnerName: partnerName2, labId: uid, labName: labName2,
                    });
                    const orderByCaseId = new Map(rawOrdersForShadow
                        .filter(o => o.source === 'agent_auto' && o.parsedCaseId)
                        .map(o => [o.parsedCaseId, o]));
                    uniqueOrders = rows2.map(r => {
                        if (r.webOrder) return r.order;
                        const legacy = r.caseId ? orderByCaseId.get(r.caseId) : null;
                        return legacy
                            ? { ...legacy, items: r.order.items, patientName: r.order.patientName, isRemake: r.order.isRemake, createdAt: r.order.createdAt, dueDate: r.order.dueDate,
                                // 원 건 id (2026-10-24 ④): 옛 복사 의뢰서엔 없으므로 케이스 원본(remakeOfId)에서 보충
                                remakeOfCaseId: legacy.remakeOfCaseId || r.order.remakeOfCaseId || r.order._case?.remakeOfId || null }
                            : r.order;   // 주문 문서가 없는 케이스 = case: 가상 id (수집 취소는 case: 분기가 받는다)
                    });
                    console.log(`[케이스통일] 표시=통합 뷰(B) · 행 ${rows2.length} (웹 ${rows2.filter(r => r.origin === 'web').length} / 수집 ${rows2.filter(r => r.origin === 'collected').length} / 웹+수집 ${rows2.filter(r => r.origin === 'both').length})`);
                }
            } catch (e) {
                // 전환 실패는 조회를 막지 않는다 — 현행(A) 결과로 계속 (조용한 실패 금지: 사유 기록)
                console.warn('[케이스통일] 표시 전환 실패 — 현행(A) 결과로 계속:', e);
                unifiedDisplay = false;
            }

            console.log('📊 필터링된 의뢰서:', uniqueOrders.length, '개');   // 환자명은 로그에 남기지 않는다 (2026-08-26)

            // (2026-08-31 차단②) 자동 조회에서는 항목 합산 입력에서 수집 행을 제외 —
            // 과거 생성된 agent_auto 의뢰서 재유입 + 통합 뷰 case: 가상 행을 함께 거른다(버튼이 재포함).
            // searchedOrders(의뢰서 확인 모달)는 종전대로 uniqueOrders 전체를 유지한다.
            const ordersForItems = includeCollected
                ? uniqueOrders
                : uniqueOrders.filter(o => o.source !== 'agent_auto' && !String(o.id || '').startsWith('case:'));

            // 항목 생성 + 단가표 자동 매칭 — 공용 로직 (utils/statementItems.js)
            const newItems = buildItemsFromOrders(ordersForItems, { priceList: activePriceList });

            // 섀도 비교 (플래그 OFF면 즉시 반환 · 화면 결과에는 영향 없음). 표시가 이미 B면 생략.
            // (2026-08-31) 자동 조회는 수집 행을 뺀 결과라 A/B 비교가 무의미 + 매 조회 추가 read — 버튼 실행에서만
            if (includeCollected && !unifiedDisplay && !isOutsourceSel) runCaseUnifyShadow({
                rawOrders: rawOrdersForShadow, sd, ed, clinicSel, clinicName: opts.clinicName,
                currentItems: newItems, pl: activePriceList, uid,
            });

            // ── 병합 모드 (2026-07-30): 이어쓰기(draft)로 열었을 때 —
            // 저장된 항목은 그대로 두고, draft 저장 이후 명세화된 수집분만 뒤에 추가한다.
            // (이어쓰기는 재조회를 안 해서 새 수집분이 영영 안 보이던 문제의 해결)
            if (opts.merge) {
                const savedIds = opts.existingOrderIds || new Set();
                // 수집 취소했던 건은 재등록으로 의뢰서 id가 바뀌어도 parsedCaseId로 잡아 제외 (2026-08-05)
                const canceled = canceledParsedCaseIdsRef.current;
                const parsedIdOf = new Map(uniqueOrders.map(o => [o.id, o.parsedCaseId || null]));
                // (2026-08-31 차단③) 자동 이어쓰기 병합은 ordersForItems(수집 행 제외)로 만든 newItems를
                // 쓰므로 웹 의뢰서만 자동 추가된다 — 수집 행 추가는 버튼(includeCollected)에서만.
                // (2026-10-23 1단계) 자동 초안 전용 거름: onlyCaseIds = 이 케이스만 담기([이번 초안에 넣기]),
                //   skipIds = 살아 있는 발행본에 이미 있는 id(이중 청구 방지). 버튼 [담기]는 둘 다 없음 → 기존 동작 그대로
                const caseIdOfItem = (n) => {
                    const oid = String(n.orderId || '');
                    return parsedIdOf.get(n.orderId) || n.parsedCaseId || (oid.startsWith('case:') ? oid.slice(5) : '') || '';
                };
                const onlyCaseIds = opts.onlyCaseIds instanceof Set ? opts.onlyCaseIds : null;
                const skipIds = opts.skipIds instanceof Set ? opts.skipIds : null;
                const inScope = (n) => !onlyCaseIds || onlyCaseIds.has(caseIdOfItem(n));
                const alreadyBilled = (n) => !!skipIds && (skipIds.has(n.orderId) || skipIds.has(caseIdOfItem(n)) || (n.parsedCaseId && skipIds.has(n.parsedCaseId)));
                const added = newItems.filter(n => n.orderId && !savedIds.has(n.orderId)
                    && !(parsedIdOf.get(n.orderId) && canceled.has(parsedIdOf.get(n.orderId)))
                    && inScope(n) && !alreadyBilled(n));
                // 이번에 화면에 존재하게 된 의뢰서 전부를 "본 것"으로 기록 — 이후 삭제해도 재로드에 안 살아남
                // (onlyCaseIds 거름이면 대상 케이스만 기록 — 나머지는 다음 [담기]에 그대로 잡히게)
                newItems.forEach(n => { if (n.orderId && inScope(n)) seenOrderIdsRef.current.add(n.orderId); });
                if (opts.result) { opts.result.added = added.length; opts.result.skippedBilled = newItems.filter(n => inScope(n) && alreadyBilled(n)).length; }
                // 수집 행(agent_auto·case:) 개수 — 버튼 안내 문구용
                const collectedOrderIds = new Set(uniqueOrders
                    .filter(o => o.source === 'agent_auto' || String(o.id || '').startsWith('case:'))
                    .map(o => o.id));
                if (added.length > 0) {
                    setItems(prev => {
                        const have = new Set(prev.map(i => i.orderId).filter(Boolean));
                        const fresh = added.filter(a => !have.has(a.orderId));
                        // (2026-08-31 대표 지시) 뒤에 뭉쳐 붙이지 않고 전체 날짜순으로 재정렬 —
                        // 수집분·웹 의뢰서가 출처 무관 섞여 나열된다 (그룹·금값 행은 sortItems가 묶음 유지)
                        return fresh.length ? sortItems([...prev, ...fresh]) : prev;
                    });
                    if (includeCollected) {
                        const nCollected = added.filter(a => collectedOrderIds.has(a.orderId)).length;
                        const nWeb = added.length - nCollected;
                        setMergeNotice(`수집분 ${nCollected}건 추가됨${nWeb > 0 ? ` (새 의뢰서 ${nWeb}건 포함)` : ''} — 필요 없으면 행에서 삭제하세요.`);
                        // (2026-08-31 검수 P1-2) 버튼으로 담은 수집분은 재조회(자동 경로)로 복귀하지 않는다 —
                        // 기간 칩 재조회 전 확인창이 뜨도록 손본 흔적으로 기록 (소리 없는 소실 방지)
                        dirtyRef.current = true;
                    } else {
                        setMergeNotice(`이 기간의 새 의뢰서 ${added.length}건을 아래에 추가했습니다. 필요 없으면 행에서 삭제하세요.`);
                    }
                } else if (includeCollected) {
                    setMergeNotice('이 기간에 새로 추가할 수집분이 없습니다. (이미 담겨 있거나 수집된 건이 없음)');
                }
                // 의뢰서 확인 모달용 원본은 합집합으로 유지
                setSearchedOrders(prev => {
                    const have = new Set(prev.map(o => o.id));
                    return [...prev, ...uniqueOrders.filter(o => !have.has(o.id))];
                });
                if (opts.result) opts.result.ok = true;
                return;
            }

            // 최종 정렬 (현재 정렬 기준 사용)
            const sortedNewItems = sortItems(newItems);

            // 새 조회 = 항목 전체 교체 — "봤던 의뢰서" 기억도 이번 결과로 재설정 (2026-08-05)
            seenOrderIdsRef.current = new Set(newItems.map(n => n.orderId).filter(Boolean));

            setSearchedOrders(uniqueOrders);   // 의뢰서 확인 모달용 원본 보관
            setItems(sortedNewItems);
            setTotalDiscount(0);
            setDiscountType('amount');
            dirtyRef.current = false;   // 새 조회로 내용 재설정 — 손본 흔적 초기화 (2026-08-13 P0-1)

        } catch (error) {
            console.error('의뢰서 조회 실패:', error);
            if (opts.result) opts.result.ok = false;
            alert('의뢰서 조회에 실패했습니다.');
        } finally {
            setLoading(false);
            setSearchTick(t => t + 1);   // 자동 초안 대기 신호 (2026-10-23)
        }
    };

    // Excel 다운로드
    const downloadExcel = () => {
        if (items.length === 0) {
            alert('다운로드할 데이터가 없습니다.');
            return;
        }

        const selectedClinicName = isManualMode ? (manualClinicName || '업체') : (clinics.find(c => c.id === selectedClinic)?.name || '업체');

        let csv = '작성일,마감일,환자명,치아정보,개수,개당가격,합산가격\n';

        items.forEach(item => {
            // 연도 포함(YYYY-MM-DD) — M-D로 내보내면 재업로드 시 올해로 잘못 파싱됨
            const date = formatDateForInput(item.createdAt) || '-';
            const dueDate = formatDateForInput(item.dueDate) || '-';
            const patientName = (item.patientName || '-').replace(/"/g, '""');
            const toothInfo = (item.toothInfo || '-').replace(/,/g, ';'); // CSV 구분자 충돌 방지
            const toothCount = item.toothCount || 0;
            const unitPrice = item.unitPrice || 0;
            const totalPrice = toothCount * unitPrice;

            csv += `${date},${dueDate},"${patientName}","${toothInfo}",${toothCount},${unitPrice},${totalPrice}\n`;
        });

        const { subtotal, discountAmount, total, totalTeeth } = calculateTotal();
        csv += `\n`;
        csv += `소계,,,,,${totalTeeth}개,"${subtotal}"\n`;
        if (discountAmount > 0) {
            csv += `할인,,,,,,"-${discountAmount}"\n`;
        }
        csv += `최종합계,,,,,${totalTeeth}개,"${total}"\n`;

        const blob = new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');
        const url = URL.createObjectURL(blob);
        link.setAttribute('href', url);
        link.setAttribute('download', `거래명세서_${selectedClinicName}_${startDate}_${endDate}.csv`);
        link.style.visibility = 'hidden';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    // Excel 업로드 (다운로드한 양식을 다시 불러오기) — .csv / .xlsx 모두 지원
    const fileInputRef = useRef(null);

    const handleExcelUpload = (e) => {
        const file = e.target.files?.[0];
        if (!file) return;

        const reader = new FileReader();
        reader.onload = (ev) => {
            try {
                const wb = XLSX.read(new Uint8Array(ev.target.result), { type: 'array' });
                const ws = wb.Sheets[wb.SheetNames[0]];
                const rows = XLSX.utils.sheet_to_json(ws, { header: 1, raw: false, defval: '' });

                const currentYear = new Date().getFullYear();
                const parseMD = (str) => {
                    if (str == null) return null;
                    const s = String(str).trim();
                    if (!s || s === '-') return null;
                    const md = s.match(/^(\d{1,2})[-.\/](\d{1,2})$/);
                    if (md) {
                        const d = new Date(currentYear, parseInt(md[1], 10) - 1, parseInt(md[2], 10));
                        return isNaN(d.getTime()) ? null : d;
                    }
                    const full = new Date(s);
                    return isNaN(full.getTime()) ? null : full;
                };

                const parsed = [];
                let headerFound = false;
                for (const row of rows) {
                    const cols = (row || []).map(c => (c == null ? '' : String(c)));
                    const first = (cols[0] || '').trim();

                    if (!headerFound) {
                        // 헤더 행(작성일,마감일,환자명,...) 식별 후 그 다음 행부터 데이터로 처리
                        if (first.includes('작성일') || cols.some(c => c.includes('환자명'))) {
                            headerFound = true;
                        }
                        continue;
                    }

                    // 요약 행(소계/할인/최종합계) 스킵
                    if (['소계', '할인', '최종합계', '합계'].includes(first)) continue;

                    const createdAt = parseMD(cols[0]) || new Date();
                    const dueDate = parseMD(cols[1]);
                    let patientName = (cols[2] || '').trim();
                    if (patientName === '-') patientName = '';
                    let toothInfo = (cols[3] || '').trim();
                    if (toothInfo === '-') toothInfo = '';
                    toothInfo = toothInfo.replace(/;/g, ','); // 다운로드 시 치환된 구분자(;) 복원
                    const countCol = parseInt((cols[4] || '').replace(/[^\d]/g, ''), 10);
                    const toothCount = (!isNaN(countCol) && countCol > 0) ? countCol : parseToothCount(toothInfo);
                    const unitPrice = parseInt((cols[5] || '').replace(/[^\d]/g, ''), 10) || 0;

                    // 완전히 빈 행은 스킵
                    if (!patientName && !toothInfo && !unitPrice && !toothCount) continue;

                    parsed.push({
                        id: `upload-${Date.now()}-${parsed.length}-${Math.random()}`,
                        orderId: null,
                        createdAt,
                        dueDate,
                        patientName,
                        toothInfo,
                        toothCount,
                        // 내보내기 시 환자명에 붙는 [리메이크]/[수리] 라벨로 플래그 복원 (2026-09-04 수리 추가)
                        isRemake: patientName.includes('[리메이크]') || patientName.includes('[수리]'),
                        remakeKind: patientName.includes('[수리]') ? 'repair' : '',
                        remakeReason: '',
                        unitPrice,
                        _sortTimestamp: getTimestamp(createdAt)
                    });
                }

                if (parsed.length === 0) {
                    alert('불러올 항목을 찾지 못했습니다.\n다운로드한 양식(작성일, 마감일, 환자명, 치아정보, 개수, 개당가격, 합산가격)인지 확인해주세요.');
                    return;
                }

                if (items.length > 0 && !window.confirm(`기존 ${items.length}건의 항목을 업로드한 ${parsed.length}건으로 교체하시겠습니까?`)) {
                    return;
                }

                setItems(sortItems(parsed));
                setIsEditMode(true);
                alert(`${parsed.length}건의 항목을 불러왔습니다.\n내용을 확인 후 단가표 적용 또는 저장하세요.`);
            } catch (err) {
                console.error('엑셀 업로드 실패:', err);
                alert('파일을 읽는 중 오류가 발생했습니다.\n다운로드한 Excel(.csv 또는 .xlsx) 파일인지 확인해주세요.');
            }
        };
        reader.readAsArrayBuffer(file);
        e.target.value = ''; // 같은 파일 다시 선택 가능하도록 초기화
    };

    const formatDate = (timestamp) => {
        if (!timestamp) return '-';
        let date;
        if (timestamp.seconds !== undefined) {
            date = new Date(timestamp.seconds * 1000);
        } else if (typeof timestamp.toDate === 'function') {
            date = timestamp.toDate();
        } else {
            date = new Date(timestamp);
        }
        return `${date.getMonth() + 1}-${date.getDate()}`;
    };

    // 날짜를 YYYY-MM-DD 형식으로 변환 (input type="date"용)
    const formatDateForInput = (timestamp) => {
        if (!timestamp) return '';
        let date;
        if (timestamp.seconds !== undefined) {
            date = new Date(timestamp.seconds * 1000);
        } else if (typeof timestamp.toDate === 'function') {
            date = timestamp.toDate();
        } else {
            date = new Date(timestamp);
        }
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
    };

    // 정렬 함수 (현재 sortBy 기준) — 2026-08-31 대표 지시 "수집별·의뢰서별로 나뉘지 않게":
    // 출처(웹/수집) 무관 전체 날짜순. 행이 아니라 "의뢰서 그룹" 단위로 정렬한다 —
    // 그룹 = 연속된 같은 orderId(있으면) 또는 rowSpan 그룹키(환자명+작성일+마감일) 블록.
    // 금값 파생 행(goldForItemId)은 부모와 같은 그룹키를 복제하므로 그룹째 붙어 다닌다(해체 금지).
    // 날짜 없는 그룹은 맨 뒤. 같은 날짜끼리는 원래 순서 보존.
    // 2026-09-01 대표 지적: 수기(직접 입력) 행을 맨 뒤에 두던 규칙 폐지 — 날짜가 있으면 수기도 같은 날짜순에 섞는다.
    const sortItems = (itemsToSort, criterion) => {
        const key = criterion || sortBy;
        const keyOf = (it) => `${it.patientName}-${formatDate(it.createdAt)}-${formatDate(it.dueDate)}-${formatDate(it.designDate)}`;
        const sameGroup = (a, b) => (a.orderId && b.orderId) ? a.orderId === b.orderId : keyOf(a) === keyOf(b);
        // 연속 블록으로 그룹 나누기 (표의 rowSpan 그룹핑과 같은 연속-키 규칙)
        const groups = [];
        itemsToSort.forEach(it => {
            const g = groups[groups.length - 1];
            if (g && sameGroup(g[0], it)) g.push(it);
            else groups.push([it]);
        });
        const decorated = groups.map((g, i) => {
            const f = g[0];   // 그룹 날짜 = 첫 행 기준 (웹 의뢰서 createdAt · 수집분 fileMtime 유래 — buildItemsFromOrders와 동일)
            // 10-31: '작성일순'은 디자인 완성일 우선(수집분은 작성일 칸이 비어 있다)
            const val = key === 'createdAt' ? (f.designDate || f.createdAt) : f[key];
            const missing = key === 'dueDate' ? !f.dueDate : !getTimestamp(val);
            const ts = missing ? (getTimestamp(f.designDate || f.createdAt) || 0) : getTimestamp(val);
            return { g, i, missing, ts };
        });
        decorated.sort((a, b) => {
            // 수기(직접 입력)도 날짜가 있으면 같은 날짜순에 섞는다 (2026-09-01 대표 지적 — 종전 '수기 맨 뒤' 폐지)
            if (a.missing !== b.missing) return a.missing ? 1 : -1;  // 날짜 없는 그룹(막 추가한 빈 행 등) — 맨 뒤
            return (a.ts - b.ts) || (a.i - b.i);                     // 오름차순 · 동률은 원래 순서
        });
        return decorated.flatMap(d => d.g);
    };

    // 정렬 기준 변경
    const handleSortByChange = (newSortBy) => {
        setSortBy(newSortBy);
        setItems(sortItems(items, newSortBy));
    };

    // 같은 환자 묶음(표의 rowSpan 그룹과 동일한 키: 환자명+작성일+마감일, 연속 행) id 집합
    // — 날짜 입력칸은 묶음의 첫 행에만 있어서, 첫 행만 바꾸면 나머지 행과 키가 어긋나 묶음이 쪼개진다 (2026-08-05 대표 요청 수정)
    const idsInSameGroup = (itemId) => {
        const keyOf = (it) => `${it.patientName}-${formatDate(it.createdAt)}-${formatDate(it.dueDate)}-${formatDate(it.designDate)}`;
        const idx = items.findIndex(i => i.id === itemId);
        if (idx < 0) return new Set([itemId]);
        const key = keyOf(items[idx]);
        const ids = new Set([items[idx].id]);
        for (let i = idx - 1; i >= 0 && keyOf(items[i]) === key; i--) ids.add(items[i].id);
        for (let i = idx + 1; i < items.length && keyOf(items[i]) === key; i++) ids.add(items[i].id);
        return ids;
    };

    // 작성일 변경 핸들러 — 같은 환자 묶음 전체에 적용 (묶음 분리 방지), 편집 중에는 위치 고정
    const handleCreatedAtChange = (itemId, newDate) => {
        if (!newDate) return;
        const date = newDate instanceof Date ? newDate : new Date(newDate);
        const ids = idsInSameGroup(itemId);
        setItems(items.map(item =>
            ids.has(item.id) ? {
                ...item,
                createdAt: date,
                _sortTimestamp: date.getTime()
            } : item
        ));
    };

    // 마감일 변경 핸들러 — 같은 환자 묶음 전체에 적용 (묶음 분리 방지)
    const handleDueDateChange = (itemId, newDate) => {
        const date = newDate ? (newDate instanceof Date ? newDate : new Date(newDate)) : null;
        const ids = idsInSameGroup(itemId);
        setItems(items.map(item =>
            ids.has(item.id) ? { ...item, dueDate: date } : item
        ));
    };

    // 수정 완료 핸들러 - 현재 정렬 기준으로 정렬
    // 날짜순 재정렬 (항상 편집 가능 상태 유지)
    const handleFinishEdit = () => {
        setItems(sortItems(items));
    };

    const handleUnitPriceChange = (itemId, newPrice) => {
        const unitPrice = Number(newPrice) || 0;
        if (unitPrice < 0) return; // 음수 방지

        markDirty();
        setItems(items.map(item =>
            // 사용자가 직접 입력한 단가 — 저장/발행 시 이 거래처 단가표에 학습 제안
            // P1-2 (2026-08-20): 치식 없는 항목(수량 0)에 단가를 넣으면 수량 1 자동 보정 — 0원 고착 방지.
            //   사용자가 수량을 직접 만진 항목(countManual)은 건드리지 않는다.
            item.id === itemId
                ? {
                    ...item, unitPrice, priceSource: 'manual',
                    ...(unitPrice > 0 && !(item.toothCount > 0) && !item.countManual ? { toothCount: 1 } : {}),
                }
                : item
        ));
    };

    // ── 골드(금값) 파생 행 핸들러 ──
    // [+금]: 부모 품목 행 바로 아래 금값 행 삽입. 시세는 최근 입력값(localStorage) 프리필.
    // 외주(design_outsource) 명세서에서는 버튼 자체를 숨긴다(기획서 확정 — CAD 디자인 용역에 금값 줄 금지).
    const handleAddGoldRow = (baseItem) => {
        markDirty();
        const linkId = baseItem.goldLineId || `gl-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
        const rate = loadLastGoldRate();
        const goldRow = {
            id: `gold-${Date.now()}-${Math.random()}`,
            itemType: 'gold',
            goldForItemId: linkId,       // 부모 행의 goldLineId를 가리킨다 (저장·재로드에도 유지되는 연결 토큰)
            goldWeight: 0,
            goldPricePerGram: rate,
            orderId: null,               // 파생 행은 의뢰서 연결 없음 — 수집·누락 감지·이중청구 집계에 안 잡힌다
            parsedCaseId: null,
            source: 'manual',
            // 환자 묶음(rowSpan 그룹 키 = 환자명+작성일+마감일)에 붙도록 부모와 동일하게 복제
            createdAt: baseItem.createdAt,
            designDate: baseItem.designDate ?? null,   // 디자인 완성일(10-31) — 묶음 키·정렬
            dueDate: baseItem.dueDate ?? null,
            patientName: baseItem.patientName,
            toothInfo: goldLineLabel(0, rate),
            toothCount: 1,               // 개수1 × 단가(금값) — 기존 합계 로직 무수정 통과
            countManual: true,
            isRemake: false,
            remakeReason: '',
            unitPrice: goldLineAmount(0, rate),
            _sortTimestamp: baseItem._sortTimestamp || getTimestamp(baseItem.createdAt),
        };
        const baseIndex = items.findIndex(i => i.id === baseItem.id);
        if (baseIndex < 0) return;
        const next = items.map(i => (i.id === baseItem.id ? { ...i, goldLineId: linkId } : i));
        next.splice(baseIndex + 1, 0, goldRow);
        setItems(next);
    };

    // 중량(g, 소수 1자리)·시세(원/g) 수정 → 금액(중량×시세)·표기 즉시 재계산
    const handleGoldFieldChange = (itemId, field, rawValue) => {
        markDirty();
        let v = Number(rawValue) || 0;
        if (v < 0) v = 0;
        v = field === 'goldWeight' ? Math.round(v * 10) / 10 : Math.round(v);
        if (field === 'goldPricePerGram' && v > 0) saveLastGoldRate(v);
        setItems(items.map(i => {
            if (i.id !== itemId) return i;
            const w = field === 'goldWeight' ? v : (i.goldWeight || 0);
            const r = field === 'goldPricePerGram' ? v : (i.goldPricePerGram || 0);
            // priceSource는 건드리지 않는다 — 금값 행이 단가 학습(learnPricesToPriceList) 후보에 섞이지 않도록
            return { ...i, [field]: v, toothInfo: goldLineLabel(w, r), unitPrice: goldLineAmount(w, r), toothCount: 1 };
        }));
    };

    const handleToothInfoChange = (itemId, newToothInfo) => {
        markDirty();
        setItems(items.map(item => {
            if (item.id === itemId) {
                // 단가표 항목이 포함되면 단가 자동 입력 (직접 입력한 단가는 유지)
                // (2026-08-13 P2-4) findPriceMatch 인자 정상 전달 — 종전엔 serviceName·material을
                //   빠뜨려 치아정보(toothInfo)만으로 매칭돼 자동단가가 거의 안 잡혔다.
                const match = findPriceMatch(priceList, newToothInfo, item.serviceName, item.material);
                // (2026-08-13 P2-4) 리메이크 항목은 자동단가로 덮어쓰지 않는다 — 무상/유상은 사람이 결정.
                const nextUnit = (match && !item.unitPrice && !item.isRemake) ? match.unitPrice : item.unitPrice;
                // P1-2 (2026-08-20): 치식이 안 잡히는 품목명(덴처·교합장치 등)은 수량 1로 —
                // 내용이 있는데 수량 0이면 단가를 넣어도 합계가 0원에 고착되던 문제 해소.
                const parsedCount = parseToothCount(newToothInfo);
                const autoCount = parsedCount > 0 ? parsedCount : (newToothInfo.trim() ? 1 : 0);
                return {
                    ...item,
                    toothInfo: newToothInfo,
                    // 사용자가 개수를 직접 입력한 항목은 자동 계산으로 덮어쓰지 않음
                    toothCount: item.countManual ? item.toothCount : autoCount,
                    unitPrice: nextUnit,
                };
            }
            return item;
        }));
    };

    const handleToothCountChange = (itemId, newCount) => {
        const count = Number(newCount) || 0;
        if (count < 0) return; // 음수 방지

        markDirty();
        setItems(items.map(item =>
            // 직접 입력했음을 표시해 이후 치아정보 수정 시 덮어쓰기 방지
            item.id === itemId ? { ...item, toothCount: count, countManual: true } : item
        ));
    };

    const handleDeleteItem = (itemId) => {
        if (window.confirm('이 항목을 삭제하시겠습니까?')) {
            markDirty();
            // 부모 품목 행을 지우면 연결된 금값 파생 행도 동반 삭제 (goldLineId ↔ goldForItemId)
            const target = items.find(i => i.id === itemId);
            setItems(items.filter(item =>
                item.id !== itemId &&
                !(target?.goldLineId && item.itemType === 'gold' && item.goldForItemId === target.goldLineId)
            ));
        }
    };

    // 같은 환자에게 치아정보 항목 추가
    const handleAddToothItem = (baseItem) => {
        const newItem = {
            id: `sub-${Date.now()}-${Math.random()}`,
            orderId: baseItem.orderId,
            createdAt: baseItem.createdAt,
            designDate: baseItem.designDate ?? null,   // 디자인 완성일(10-31) — 묶음 키·정렬
            dueDate: baseItem.dueDate,
            patientName: baseItem.patientName,
            toothInfo: '',
            toothCount: 0,
            isRemake: baseItem.isRemake,
            remakeReason: baseItem.remakeReason,
            // 같은 환자 줄 추가 — 리메이크 원 건 연결도 따라간다 (2026-10-24 ④)
            ...(baseItem.remakeKind ? { remakeKind: baseItem.remakeKind } : {}),
            ...(baseItem.remakeOfCaseId ? { remakeOfCaseId: baseItem.remakeOfCaseId } : {}),
            ...(baseItem.remakeOfOrderId ? { remakeOfOrderId: baseItem.remakeOfOrderId } : {}),
            ...(baseItem.remakeOf ? { remakeOf: baseItem.remakeOf } : {}),
            ...(baseItem.remakeLiability ? { remakeLiability: baseItem.remakeLiability } : {}),
            unitPrice: 0,
            _sortTimestamp: baseItem._sortTimestamp || getTimestamp(baseItem.createdAt)
        };

        // 같은 환자 그룹 바로 뒤에 추가
        const baseIndex = items.findIndex(item => item.id === baseItem.id);
        const newItems = [...items];

        // 같은 환자의 마지막 항목 찾기
        let insertIndex = baseIndex + 1;
        const patientKey = `${baseItem.patientName}-${formatDate(baseItem.createdAt)}-${formatDate(baseItem.dueDate)}-${formatDate(baseItem.designDate)}`;
        while (insertIndex < newItems.length) {
            const currentKey = `${newItems[insertIndex].patientName}-${formatDate(newItems[insertIndex].createdAt)}-${formatDate(newItems[insertIndex].dueDate)}-${formatDate(newItems[insertIndex].designDate)}`;
            if (currentKey !== patientKey) break;
            insertIndex++;
        }

        newItems.splice(insertIndex, 0, newItem);
        markDirty();
        setItems(newItems);
        setIsEditMode(true);
    };

    const handleAddItem = () => {
        markDirty();
        const newItem = {
            id: `manual-${Date.now()}-${Math.random()}`,
            orderId: null,
            source: 'manual',
            createdAt: new Date(),
            dueDate: null,
            patientName: '',
            toothInfo: '',
            toothCount: 0,
            isRemake: false,
            remakeReason: '',
            unitPrice: 0,
            _sortTimestamp: Date.now()
        };
        setItems([...items, newItem]);
        setPatientSearch('');   // 검색 중이면 새 행(빈 환자명)이 안 보여 혼란 — 전체 표시로 복귀
        setIsEditMode(true);
        // 새 행의 환자명 입력으로 자동 포커스
        setTimeout(() => {
            const patients = document.querySelectorAll('input[data-nav="patient"]');
            patients[patients.length - 1]?.focus();
        }, 50);
    };

    // 스프레드시트식 키보드 이동: Enter로 다음 칸, 마지막 칸에서 Enter면 새 행 추가
    // 방향키: ↑↓ = 같은 열 위/아래 행, ←→ = 커서가 칸 끝/처음일 때 옆 칸 (2026-07-19)
    // 단가 셀 전용: Enter → 다음 "빈 단가" 셀로 점프(자동 매칭된 셀 스킵), Ctrl+D → 윗줄 단가 복사
    const handleGridKeyDown = (e) => {
        const target = e.target;
        if (target.tagName !== 'INPUT' || !target.dataset.nav) return;

        const digitsOf = (el) => (el.value || '').replace(/[^0-9]/g, '');

        // 방향키 셀 이동
        if (['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight'].includes(e.key) && !e.ctrlKey && !e.metaKey && !e.shiftKey) {
            const inputs = Array.from(document.querySelectorAll('input[data-nav]'));
            const idx = inputs.indexOf(target);
            if (idx < 0) return;

            // number 타입은 selectionStart가 null — 항상 경계로 취급 (스피너 증감 대신 행 이동)
            let atStart = true, atEnd = true;
            try {
                if (target.selectionStart != null) {
                    const len = (target.value || '').length;
                    atStart = target.selectionStart === 0 && target.selectionEnd === 0;
                    atEnd = target.selectionStart === len && target.selectionEnd === len;
                }
            } catch { /* number input 등 — 경계 취급 */ }

            let next = null;
            if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
                // 같은 열(data-nav 동일)에서 위/아래 행으로
                const sameCol = inputs.filter(el => el.dataset.nav === target.dataset.nav);
                const colIdx = sameCol.indexOf(target);
                next = e.key === 'ArrowDown' ? sameCol[colIdx + 1] : sameCol[colIdx - 1];
            } else if (e.key === 'ArrowRight' && atEnd) {
                next = inputs[idx + 1];
            } else if (e.key === 'ArrowLeft' && atStart) {
                next = inputs[idx - 1];
            }
            if (next) {
                e.preventDefault();
                next.focus();
                next.select?.();
            }
            return;
        }

        // Ctrl/Cmd+D: 단가 셀에서 윗줄 단가 복사
        if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'd') {
            if (target.dataset.nav === 'price' && target.dataset.itemId) {
                e.preventDefault();
                const prices = Array.from(document.querySelectorAll('input[data-nav="price"]'));
                const pIdx = prices.indexOf(target);
                if (pIdx > 0) handleUnitPriceChange(target.dataset.itemId, digitsOf(prices[pIdx - 1]));
            }
            return;
        }

        if (e.key !== 'Enter' || e.ctrlKey || e.metaKey) return;
        e.preventDefault();

        // 단가 셀에서 Enter: 남은 빈 단가 셀만 순회 — 단가표로 채워진 셀은 건너뜀
        if (target.dataset.nav === 'price') {
            const prices = Array.from(document.querySelectorAll('input[data-nav="price"]'));
            const pIdx = prices.indexOf(target);
            const isEmpty = (el) => Number(digitsOf(el) || 0) === 0;
            const next = prices.slice(pIdx + 1).find(isEmpty) || prices.slice(0, pIdx).find(isEmpty);
            if (next) {
                next.focus();
                next.select?.();
                return;
            }
            // 빈 단가가 더 없으면 아래 기본 이동으로
        }

        const inputs = Array.from(document.querySelectorAll('input[data-nav]'));
        const idx = inputs.indexOf(target);
        if (idx >= 0 && idx < inputs.length - 1) {
            inputs[idx + 1].focus();
            inputs[idx + 1].select?.();
        } else {
            handleAddItem();
        }
    };

    // 전역 단축키: Ctrl/Cmd+S 임시저장, Ctrl/Cmd+Enter 발행(확인 후) — 모달 열림/저장 중엔 무시
    const shortcutRef = useRef({});
    shortcutRef.current = {
        canRun: !saving && !showPriceListModal && items.length > 0,
        save: () => isEditingPublished ? handleSaveEdit() : handleSaveDraft(),
        // 발행본 수정 중에는 재발행 단축키 비활성. 확인창은 handlePublish 내부 것 하나만 사용
        publish: () => { if (!isEditingPublished) handlePublish(); },
    };
    useEffect(() => {
        const onKey = (e) => {
            if (!(e.ctrlKey || e.metaKey)) return;
            const { canRun, save, publish } = shortcutRef.current;
            if (e.key.toLowerCase() === 's') {
                e.preventDefault();            // 브라우저 저장 다이얼로그 차단 (모달 중에도)
                if (canRun) save();
            } else if (e.key === 'Enter') {
                e.preventDefault();
                if (canRun) publish();
            }
        };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, []);

    const calculateTotal = () => {
        // 각 항목의 합산가격(개수 × 개당가격)을 모두 더함
        const subtotal = items.reduce((sum, item) => {
            const itemTotal = (item.toothCount || 0) * (item.unitPrice || 0);
            return sum + itemTotal;
        }, 0);
        // 금값 파생 행의 개수 1은 치아가 아님 — 총 개수·개당 평균 표기에서 제외 (금액 합계에는 그대로 포함)
        const totalTeeth = items.reduce((sum, item) => sum + (item.itemType === 'gold' ? 0 : (item.toothCount || 0)), 0);

        let discountAmount = 0;
        if (discountType === 'percent') {
            discountAmount = Math.round(subtotal * totalDiscount / 100);
        } else {
            discountAmount = Number(totalDiscount) || 0;
        }

        const total = subtotal - discountAmount;

        return { subtotal, discountAmount, total, totalTeeth };
    };

    // 임시저장 (발송 안 됨)
    // 공용 payload 빌더 — 임시저장·발행본 수정이 같은 데이터를 쓰도록
    const buildStatementPayload = async () => {
        const senderDoc = await getDoc(doc(db, 'users', auth.currentUser.uid));
        const senderData = senderDoc.exists() ? senderDoc.data() : {};
        // 계좌는 paymentProfiles(비공개)에서 — users 노출 제거 (2026-08-06 보안 P1②)
        const senderBank = await fetchDomesticBank(user?.companyId || auth.currentUser.uid, senderData);

        let receiverName = manualClinicName.trim();
        let receiverId = null;
        if (!isManualMode) {
            const clinic = clinics.find(c => c.id === selectedClinic);
            const isEmailPartner = selectedClinic.startsWith('email_') || clinic?.source === 'email';
            // 비회원(에이전트 수집) 치과 — 실제 uid가 아니므로 users 조회 없이 이름 부분으로 폴백
            const isAgentPartner = selectedClinic.startsWith('agent_') || clinic?.source === 'agent';

            if (isEmailPartner || isAgentPartner) {
                receiverId = selectedClinic;
                receiverName = clinic?.name || selectedClinic.replace(/^(email_|agent_)/, '');
            } else {
                receiverId = selectedClinic;
                try {
                    const receiverDoc = await getDoc(doc(db, 'users', selectedClinic));
                    const receiverData = receiverDoc.exists() ? receiverDoc.data() : {};
                    receiverName = receiverData.businessName || receiverData.name || clinic?.name || '';
                } catch (e) {
                    receiverName = clinic?.name || '';
                }
            }
        }

        const { subtotal, discountAmount, total, totalTeeth } = calculateTotal();
        const statementNumber = savedStatementNumber || generateStatementNumber();
        // CAD 디자인 외주 도장 — 상대가 외주 파트너(기공소)면 kind 태그를 찍는다(서버 수수료 엔진이 이걸로 4.5% 집계, 통합 기획 2·4장).
        // 서버 accept가 만든 draft를 이어쓰는 경우 이미 designOutsourceRequestId가 문서에 있고, merge라 유지된다.
        const outsourcePartner = !isManualMode ? clinics.find(c => c.id === selectedClinic && c.source === 'design_outsource') : null;
        const outsourceTag = outsourcePartner ? {
            kind: 'design_outsource',
            counterpartyType: 'lab',
            commissionSource: 'design_outsource',
            ...(outsourcePartner.designOutsourceRequestId ? { designOutsourceRequestId: outsourcePartner.designOutsourceRequestId } : {}),
        } : {};
        // 법무 P0 — 외주 명세서의 표시 품목명(toothInfo)에 "CAD 디자인" 접두 강제(완제품 보철물명 단독 금지, 의료기사법 방어).
        // 매칭 키(serviceName)는 손대지 않는다 → 단가 자동완성(findPriceMatch/canonKey)은 접두 없는 원문으로 그대로 동작.
        const itemsForSave = outsourcePartner
            ? items.map(it => ({ ...it, toothInfo: withCadDesignPrefix(it.toothInfo) }))
            : items;
        return {
            receiverId,
            statementData: {
                statementNumber,
                ...outsourceTag,
                fromUserId: user?.companyId || auth.currentUser.uid,
                fromUserName: senderData.businessName || senderData.name || '',
                supplierInfo: {
                    businessName: senderData.businessName || '',
                    businessNumber: senderData.businessNumber || '',
                    representativeName: senderData.representativeName || '',
                    address: senderData.address || '',
                    phone: senderData.businessPhone || senderData.phone || '',
                    bankAccount: senderBank || null,
                },
                toUserId: receiverId || '',
                toUserName: receiverName,
                isManual: isManualMode,
                startDate: startDate ? new Date(startDate) : new Date(),
                endDate: endDate ? new Date(endDate) : new Date(),
                dateBasis,
                items: serializeStatementItems(withDesignState(withRemakeOf(itemsForSave))),   // 공용 직렬화 — 외주면 CAD 디자인 접두 적용본 (버그 수정 2026-08-20: 원본 items를 넣어 임시저장·발행본 수정 경로에서 접두 누락되던 죽은 변수)
                // 이어쓰기 병합용 기억 (2026-08-05): 한 번이라도 품었던 의뢰서 id + 수집 취소한 수집건 id
                // — 재로드 때 "지운 행"이 새 수집분으로 둔갑해 부활하던 버그 방지
                seenOrderIds: Array.from(new Set([
                    ...seenOrderIdsRef.current,
                    ...items.map(i => i.orderId).filter(Boolean),
                ])),
                canceledParsedCaseIds: Array.from(canceledParsedCaseIdsRef.current),
                subtotal,
                totalDiscount: totalDiscount,
                discountType: discountType,
                discountAmount: discountAmount,
                totalAmount: total,
                totalTeeth: totalTeeth,
                notes,
                ...(correctsId ? { correctsId } : {}),   // 정정 발행 원본 (2026-10-23)
                createdAt: new Date(),
                createdBy: auth.currentUser.uid,
                status: 'draft'
            },
        };
    };

    // 자동 저장 표시용
    const [lastAutoSavedAt, setLastAutoSavedAt] = useState(null);

    // silent=true: 자동 저장 (알림·단가 학습 없이 조용히)
    // ── 케이스 통일 2단계: 명세서에 들어간 케이스에 statementId 역기록 + imported 전환 ──
    // 자동등록(복사 주문)이 멈춘 뒤에는 이 기록이 "명세서에 반영됨"의 정본이다 (기획서 2-4절 의미 이관).
    // 실패해도 저장·발행을 막지 않는다(케이스는 pending으로 남아 다음 조회에 다시 잡힐 뿐 — 이중청구 아님).
    // opts.takeOverFrom: 정정 발행 시 원본 id — 원본은 곧 '정정됨'이 되므로 그 연결은 새 발행본으로 옮긴다
    const markCasesInStatement = async (stmtId, itemsArr, opts = {}) => {
        if (!stmtId) return;
        try {
            const ids = [...new Set((itemsArr || [])
                .map(i => i.parsedCaseId)
                .filter(Boolean)
                .map(v => (String(v).startsWith('case:') ? String(v).slice(5) : String(v))))];
            // (2026-10-23 1단계 P1-2) 케이스가 이미 '살아 있는 발행본'을 가리키면 덮어쓰지 않는다 —
            //   정정 초안 자동저장 등이 발행본의 연결을 빼앗아, 발행본 삭제 때 되돌림이 빗나가는 것 방지
            const liveCache = new Map();   // statementId → 살아 있는 발행본인가
            const isLiveIssuedId = async (sid) => {
                if (liveCache.has(sid)) return liveCache.get(sid);
                let live = false;
                try {
                    const ss = await getDoc(doc(db, 'transactionStatements', sid));
                    live = ss.exists() && ['confirmed', 'published'].includes(ss.data().status) && ss.data().deletedBySender !== true;
                } catch (e) {
                    console.warn('케이스 연결 명세서 확인 실패(덮어쓰지 않음):', sid, e?.code || e?.message);
                    live = true;   // 확인 못 하면 안전한 쪽 — 기존 연결 유지
                }
                liveCache.set(sid, live);
                return live;
            };
            for (const cid of ids) {
                try {
                    const cs = await getDoc(doc(db, 'parsedCases', cid));
                    const prevSid = cs.exists() ? cs.data().statementId : null;
                    if (prevSid && prevSid !== stmtId && prevSid !== opts.takeOverFrom && await isLiveIssuedId(prevSid)) {
                        console.log('케이스 연결 유지(발행본에 묶임):', cid, '→', prevSid);   // id만
                        continue;
                    }
                    await updateDoc(doc(db, 'parsedCases', cid), { statementId: stmtId, status: 'imported', importedAt: new Date() });
                } catch (e) {
                    console.warn('케이스 statementId 기록 실패:', cid, e?.code || e?.message);   // 환자명 미출력
                }
            }
        } catch (e) { console.warn('케이스 statementId 기록 실패:', e?.message); }
    };

    // (2026-10-24 재점검 P1-2) 저장·발행 뒤: 이 문서에 묶였는데(statementId) 지금 줄에는 없는 케이스를 되돌린다.
    //   줄 삭제 뒤 저장·발행하면 그 케이스가 'imported' 로 영구 잔존하던 경로 차단. 실패해도 저장은 유지 — 기록만(id·사유).
    const restoreDroppedCases = async (stmtId, itemsArr) => {
        if (!stmtId) return;
        try {
            await restoreCasesDroppedFromStatement(stmtId, user?.companyId || auth.currentUser.uid, itemsArr);
        } catch (e) {
            console.warn('줄에서 빠진 케이스 되돌림 실패:', stmtId, e?.code || e?.message);
        }
    };

    // 저장 in-flight 가드 (2026-08-26): 45초 자동저장·수동 저장·발행이 겹치면
    // currentStatementId 반영 전에 addDoc이 두 번 돌아 draft가 이중 생성되던 문제 방지
    const saveInFlightRef = useRef(false);

    // extra(2026-10-23): 자동 초안이 함께 저장할 필드(closingDraft 표식). 반환 = 저장된 문서 id | null(저장 안 됨)
    const handleSaveDraft = async (silent = false, extra = null) => {
        if (saveInFlightRef.current) {
            if (!silent) alert('저장이 진행 중입니다. 잠시 후 다시 시도해주세요.');
            return null;
        }
        if (items.length === 0) {
            if (!silent) alert('항목이 없습니다.');
            return null;
        }
        const { total } = calculateTotal();
        if (total < 0) {
            if (!silent) alert('할인 금액이 총액보다 클 수 없습니다.');
            return null;
        }

        saveInFlightRef.current = true;
        try {
            setSaving(true);
            const { statementData: baseData } = await buildStatementPayload();
            const statementData = extra ? { ...baseData, ...extra } : baseData;

            let savedId = currentStatementId;
            if (currentStatementId) {
                // 기존 임시저장 업데이트 (중복 생성 방지)
                const { createdAt, createdBy, ...updateData } = statementData;
                // (2026-10-23 rules 계약) 발행자·종류·작성일·작성자는 바꿀 수 없다 — 옛 문서 fromUserId가 uid/companyId로 달라도 자동저장이 거부되지 않게 제외
                delete updateData.fromUserId;
                delete updateData.kind;
                // 정정이 아니면 옛 correctsId를 지운다 — 엉뚱한 원본을 '정정됨' 처리하지 않게
                updateData.correctsId = correctsId || deleteField();
                await setDoc(doc(db, 'transactionStatements', currentStatementId), { ...updateData, updatedAt: new Date() }, { merge: true });
            } else {
                const ref = await addDoc(collection(db, 'transactionStatements'), statementData);
                savedId = ref.id;
                setCurrentStatementId(ref.id);
                setSavedStatementNumber(statementData.statementNumber);
            }
            await markCasesInStatement(savedId, items);   // 케이스 통일 2단계 — 역기록
            await restoreDroppedCases(savedId, items);    // 줄에서 빠진 케이스 → 수집 대기 (P1-2)
            // 초안 저장도 명세서 공용 캐시(받을 돈·카드 ①이 같이 쓰는 원천)를 바꾼다 — 목록으로 돌아가면 '작성 중 명세서에만'·초안 금액이 새 값으로 (P1-4)
            invalidateReceivables(user?.companyId || auth.currentUser.uid);
            invalidateSummaryCache(user?.companyId || auth.currentUser.uid);
            await saveRemakeNotes(savedId, statementData.items);   // 리메이크 책임 별도 기록 (실패해도 저장은 유지, 화면 알림)

            if (silent) {
                setLastAutoSavedAt(new Date());
            } else {
                alert('임시저장되었습니다!\n(거래처에는 발송되지 않았습니다)\n이 화면에서 계속 수정할 수 있습니다.');
                await learnPricesToPriceList();   // 수동 입력 단가 → 이 거래처 단가표 학습 제안
            }
            return savedId;
        } catch (error) {
            console.error('임시저장 실패:', error);
            if (!silent) alert('임시저장에 실패했습니다: ' + error.message);
            return null;
        } finally {
            saveInFlightRef.current = false;
            setSaving(false);
        }
    };

    // 발행본 수정 저장 — 상태 유지 + 정산 재검토 전환 + 치과 재확인 알림 (편집 화면 단일화)
    const handleSaveEdit = async () => {
        if (saveInFlightRef.current) { alert('저장이 진행 중입니다. 잠시 후 다시 시도해주세요.'); return; }
        if (items.length === 0) { alert('항목이 없습니다.'); return; }
        const { total } = calculateTotal();
        if (total < 0) { alert('할인 금액이 총액보다 클 수 없습니다.'); return; }
        // 정산 완료본 수정 차단 (2026-10-23 대표 확정) — 저장 직전 최신 문서로도 다시 검사(아래)
        if (loadedSettlementStatus === 'settled') {
            alert('치과가 정산을 확인한 명세서는 고칠 수 없습니다. 명세서 화면의 [고쳐서 다시 발행]을 이용해주세요.');
            return;
        }
        // (2026-08-13 검토자 P1-5) 세금계산서가 이미 발행된 명세서는 금액을 바꾸면 실제 발행액과 어긋난다.
        // 금액(총액)이 로드 시점과 달라졌으면 저장을 막고 계산서를 먼저 취소하도록 안내한다.
        if (loadedTaxInvoiceStatus === 'issued' && loadedTaxInvoiceId && total !== (originalTotalRef.current || 0)) {
            alert(`세금계산서가 이미 발행되어 금액을 바꾸면 실제 발행액과 달라집니다.\n\n발행액 ${(originalTotalRef.current || 0).toLocaleString()}원 → 수정액 ${total.toLocaleString()}원\n\n[세금계산서] 메뉴에서 계산서를 먼저 취소한 뒤 금액을 수정해주세요. (금액 외 항목만 바꾸려면 총액을 원래대로 맞춰주세요)`);
            return;
        }
        if (!window.confirm('수정 내용을 저장할까요?\n거래처에 재확인 요청 알림이 발송됩니다.')) return;

        saveInFlightRef.current = true;
        try {
            setSaving(true);
            // 저장 직전 최신 문서 — 편집 중에 치과가 정산을 확인했거나 계산서가 발행됐을 수 있다 (2026-10-23, 검토자 P2)
            const ref = doc(db, 'transactionStatements', currentStatementId);
            const freshSnap = await getDoc(ref);
            const fresh = freshSnap.exists() ? freshSnap.data() : {};
            if (fresh.settlementStatus === 'settled') {
                setLoadedSettlementStatus('settled');
                alert('치과가 정산을 확인한 명세서는 고칠 수 없습니다. 명세서 화면의 [고쳐서 다시 발행]을 이용해주세요.');
                return;
            }
            const issuedTotal = fresh.totalAmount ?? (originalTotalRef.current || 0);
            if (fresh.taxInvoiceStatus === 'issued' && total !== issuedTotal) {
                alert(`세금계산서가 이미 발행되어 금액을 바꾸면 실제 발행액과 달라집니다.\n\n발행액 ${Number(issuedTotal || 0).toLocaleString()}원 → 수정액 ${total.toLocaleString()}원\n\n[세금계산서] 메뉴에서 계산서를 먼저 취소한 뒤 금액을 수정해주세요. (금액 외 항목만 바꾸려면 총액을 원래대로 맞춰주세요)`);
                return;
            }
            const { statementData, receiverId } = await buildStatementPayload();
            const { createdAt, createdBy, status, ...updateData } = statementData;   // 발행 상태 유지

            // 정산 상태 재검토로 전환 + 수정 이력 노트
            const patch = { ...updateData, updatedAt: new Date() };
            // 기간 정보가 없으면(구 문서 등) 기존 값 유지 — 오늘 날짜로 덮어쓰지 않음
            if (!startDate) delete patch.startDate;
            if (!endDate) delete patch.endDate;
            // (2026-10-23 rules 계약) 발행본은 받는 곳·보낸 곳·종류를 바꿀 수 없다 — 상호를 다시 읽어 넣으면 '권한 없음'으로 거부되므로 제외
            delete patch.fromUserId;
            delete patch.toUserId;
            delete patch.toUserName;
            delete patch.kind;
            delete patch.createdAt;
            delete patch.createdBy;
            patch.correctsId = correctsId || deleteField();   // 정정이 아니면 옛 correctsId 제거 (2026-10-23)
            // 발행본 수정은 항상 정산 재검토로 — 정산 완료 흔적(settledAt 등)도 함께 초기화 (View 저장 경로와 동일 스키마)
            patch.settlementStatus = 'pending';
            patch.settledAt = null;
            patch.settledBy = null;
            patch.settledByName = null;
            patch.settlementNotes = [
                ...(fresh.settlementNotes || []),
                { type: 'system', text: '발행자가 명세서를 수정했습니다. 내용을 다시 확인해주세요.', at: new Date(), by: auth.currentUser.uid },
            ];
            await setDoc(ref, patch, { merge: true });
            await markCasesInStatement(currentStatementId, items);   // 케이스 통일 2단계 — 역기록
            await restoreDroppedCases(currentStatementId, items);    // 줄에서 빠진 케이스 → 수집 대기 (P1-2)

            // (2026-10-24 재점검 P1-1) 비회원 공유 링크 스냅샷은 옛 금액을 그대로 보여 준다 — 옛 공유 문서를 지운다(rules delete 허용).
            //   새 링크는 명세서 화면의 [공유 링크 복사]가 다시 만든다. 실패하면 사용자에게도 알린다(링크가 옛 금액으로 계속 열릴 수 있음).
            let shareDeleteFailed = false;
            if (fresh.shareToken) {
                try {
                    await deleteDoc(doc(db, 'statementShares', fresh.shareToken));
                } catch (e) {
                    console.warn('옛 공유 스냅샷 삭제 실패:', currentStatementId, e?.code || e?.message);
                    shareDeleteFailed = true;
                }
            }
            const isNonMemberReceiver = isManualMode || !receiverId || receiverId.startsWith('email_') || receiverId.startsWith('agent_');

            if (receiverId && !receiverId.startsWith('email_') && !receiverId.startsWith('agent_')) { // 비회원(수집) 치과 유령 알림 방지
                try {
                    await createNotification({
                        recipientId: receiverId,
                        type: 'statement_updated',
                        title: '거래명세서 수정',
                        message: `${statementData.fromUserName || '거래처'}에서 거래명세서를 수정했습니다.${statementData.statementNumber ? ` (${statementData.statementNumber})` : ''} 내용을 다시 확인해주세요.`,
                        link: '/transactions',
                    });
                } catch (e) { console.error('수정 알림 실패:', e); }
            }

            invalidateReceivables(user?.companyId || auth.currentUser.uid);   // 발행본 금액 수정 → 받을 돈·카드 캐시 (재점검 P2-b)
            invalidateSummaryCache(user?.companyId || auth.currentUser.uid);
            alert(isNonMemberReceiver
                ? `수정 내용이 저장되었습니다.\n비회원 치과에는 공유 링크를 다시 보내 주세요.${shareDeleteFailed ? '\n(옛 공유 링크를 지우지 못했습니다 — 옛 금액으로 열릴 수 있으니 명세서 화면에서 링크를 다시 만들어 주세요)' : ''}`
                : `수정 내용이 저장되었습니다.\n거래처에 재확인 요청을 보냈습니다.${fresh.shareToken ? '\n비회원 치과에는 공유 링크를 다시 보내 주세요.' : ''}`);
            await learnPricesToPriceList();
            onBack && onBack();
        } catch (error) {
            console.error('수정 저장 실패:', error);
            alert('저장에 실패했습니다: ' + error.message);
        } finally {
            saveInFlightRef.current = false;
            setSaving(false);
        }
    };

    // 자동 임시저장 — 45초마다, 작성 중(draft)일 때만 (발행본 수정은 명시적 저장만)
    const autoSaveRef = useRef(null);
    autoSaveRef.current = () => {
        if (isEditingPublished || saving || loading || saveInFlightRef.current) return;   // 발행 확인창이 떠 있는 동안에도 건너뜀
        if (autoStage && autoStage !== 'done' && autoStage !== 'error') return;   // 자동 초안 진행 중엔 그 흐름이 저장한다 (2026-10-23)
        if (items.length === 0) return;
        if (!selectedClinic) return;
        handleSaveDraft(true);
    };
    useEffect(() => {
        const t = setInterval(() => autoSaveRef.current && autoSaveRef.current(), 45000);
        return () => clearInterval(t);
    }, []);

    // 발행 (저장 + 발송)
    // (2026-10-23 P1) 이중 실행 방지: 확인창·중복 조회 await 동안 버튼/Ctrl+Enter/자동저장이 끼어들지 못하게
    // 맨 앞에서 잠그고, 확인창 취소·검증 실패를 포함한 모든 종료에서 finally로 해제한다.
    const handlePublish = async () => {
        if (saveInFlightRef.current) { alert('저장이 진행 중입니다. 잠시 후 다시 시도해주세요.'); return; }
        saveInFlightRef.current = true;
        setSaving(true);
        try {
            await runPublish();
        } finally {
            saveInFlightRef.current = false;
            setSaving(false);
        }
    };
    const runPublish = async () => {
        if (items.length === 0) {
            alert('항목이 없습니다.');
            return;
        }

        const { subtotal, discountAmount, total, totalTeeth } = calculateTotal();

        if (total < 0) {
            alert('할인 금액이 총액보다 클 수 없습니다.');
            return;
        }

        // 발행 확인 (2026-08-13 P0-3) — 거래처명 + 세금계산서 연결 고지
        const targetName = isManualMode
            ? (manualClinicName.trim() || '거래처')
            : (clinics.find(c => c.id === selectedClinic)?.name || '거래처');
        // ── 발행 중복 경고 (2026-08-18 대표 확정 — 라인플란트 7월분 이중 발행 재발 방지) ──
        // 같은 거래처·겹치는 기간의 발행본이 이미 있으면 발행 전에 알린다. 차단은 아니다 —
        // 기간을 나눠 두 번 발행하는 정당한 경우도 있으므로 사람이 판단한다. 실패해도 발행은 계속.
        try {
            const uidX = user?.companyId || auth.currentUser.uid;
            // (2026-08-26 성능) 전량 스캔 → 최근 6개월 createdAt 범위 (usedOrderMap과 동일 제한)
            const sixMonthsAgoX = new Date(Date.now() - 183 * 24 * 3600 * 1000);
            const exSnap = await getDocs(query(collection(db, 'transactionStatements'),
                where('fromUserId', '==', uidX),
                where('createdAt', '>=', sixMonthsAgoX),
                orderBy('createdAt', 'desc')));
            const sMs = startDate ? new Date(startDate + 'T00:00:00').getTime() : null;
            const eMs = endDate ? new Date(endDate + 'T23:59:59').getTime() : null;
            const normX = (s) => (s || '').replace(/\s|\(.*?\)/g, '').replace(/치과의원|치과병원|치과$/g, '').toLowerCase();
            const dup = exSnap.docs.map(d => ({ id: d.id, ...d.data() })).filter(s =>
                s.id !== currentStatementId && s.id !== correctsId && s.status && s.status !== 'draft' &&
                ((selectedClinic && s.toUserId === selectedClinic) || (targetName && normX(s.toUserName) === normX(targetName))) &&
                (!sMs || !eMs ||
                    ((getTimestamp(s.startDate) || 0) <= eMs && (getTimestamp(s.endDate) || Infinity) >= sMs)));
            if (dup.length > 0) {
                const lines = dup.slice(0, 3).map(s => `· ${s.statementNumber || '(번호 없음)'} — ${Number(s.totalAmount || 0).toLocaleString()}원`).join('\n');
                const go = window.confirm(
                    `⚠️ 같은 거래처·겹치는 기간의 발행본이 이미 ${dup.length}건 있습니다.\n${lines}\n\n` +
                    `같은 기간을 두 번 발행하면 치과가 중복 정산할 수 있습니다.\n` +
                    `잘못된 기존 장이 있다면 그 명세서를 열어 먼저 삭제하세요. (발행 7일이 지나 삭제가 안 되면 덴탈클러스터에 문의)\n\n` +
                    `그래도 새로 발행할까요?`);
                if (!go) return;
            }
        } catch (e) { console.warn('발행 중복 확인 실패(발행은 계속):', e?.message || e); }

        // (2026-09-09 대표 승인) 발행 전 재청구·리메이크 예상 경고 — 차단은 아니다(유상 재제작·분할 발행은 정당). 사람이 판단.
        try {
            const priorHits = items.filter(i => i.itemType !== 'gold' && priorBilledOf(i));
            const suspectHits = items.filter(i => remakeSuspectSet.has(i.id));
            if (priorHits.length > 0 || suspectHits.length > 0) {
                const ex = (arr) => arr.slice(0, 3).map(i => `· ${i.patientName} ${i.toothInfo}`).join('\n');
                const msg =
                    (priorHits.length > 0
                        ? `⚠️ 이 거래처의 이전 명세서에 같은 환자·치식이 있는 항목 ${priorHits.length}건\n${ex(priorHits)}\n(지난달에 이미 청구한 건이 다시 잡혔을 수 있습니다)\n\n` : '') +
                    (suspectHits.length > 0
                        ? `⚠️ 이 명세서 안에 같은 환자·치식이 두 번 이상 있는 항목 ${suspectHits.length}건 — 리메이크 예상\n${ex(suspectHits)}\n(재제작이면 한쪽은 0원인지 확인)\n\n` : '') +
                    `이대로 발행할까요?`;
                if (!window.confirm(msg)) return;
            }
        } catch (e) { console.warn('재청구·리메이크 예상 확인 실패(발행은 계속):', e?.message || e); }

        // P1-3 (2026-08-20 대표 확정): 발행 확인창에 단가 0원 항목 수 병기 — 차단은 안 함(무상 관행 존중).
        // 표 위 경고 배너(zeroCount)와 같은 기준: 무상 재제작(needsPriceCheck)은 의도적 0원이라 제외.
        const zeroPriceCount = items.filter(i => (i.unitPrice || 0) === 0 && !i.needsPriceCheck).length;
        const zeroPriceLine = zeroPriceCount > 0
            ? `\n\n⚠️ 단가 0원 항목 ${zeroPriceCount}건 포함(무상 재제작 제외) — 표의 주황 행을 확인하세요.`
            : '';
        // 전월 미수 (2026-10-23 1단계 §4-5) — [명세서에 적기]를 켠 경우에만 발행 시점에 다시 계산해 고정. 합계(totalAmount)에는 더하지 않는다
        let carryOverField = null;
        // rules: carryOver 는 대표 본인(uid == fromUserId)만 쓸 수 있다 — 직원 계정이면 키 자체를 넣지 않는다
        const isOwnerWriter = auth.currentUser?.uid === (user?.companyId || auth.currentUser?.uid);
        if (carryOverAllowed && includeCarryOver && isOwnerWriter) {
            try {
                // 발행 직전만 새로 읽기(force). 이번 명세서·정정 원본 제외, 귀속일(기간 종료일)보다 뒤 명세서 제외 (P1-3)
                const co = await computeCarryOver(carryLabId, selectedPartnerKey, new Date(),
                    [currentStatementId, correctsId].filter(Boolean), { force: true, attributionDate: endDate || null });
                const amt = Math.round(Number(co?.amount) || 0);
                if (amt > 0) {
                    carryOverField = { amount: amt, asOf: co.asOf, computedAt: new Date(), include: true };
                } else if (!window.confirm('다시 계산해 보니 전월 미수가 없습니다(0원 이하). 전월 미수 없이 발행할까요?')) {
                    return;
                }
            } catch (e) {
                console.error('전월 미수 계산 실패:', e?.code || e?.message);
                if (!window.confirm('전월 미수를 계산하지 못했습니다. 전월 미수 없이 발행할까요?\n(취소하면 발행하지 않습니다)')) return;
            }
        }
        const carryOverLine = carryOverField
            ? `\n\n전월 미수 ${carryOverField.amount.toLocaleString()}원(${carryOverField.asOf} 현재)을 합계 아래 별도 상자로 함께 적습니다. 합계에는 더하지 않습니다.`
            : '';
        // 받는 곳이 덴클 회원이 아니면(직접 입력·수집 치과) 자동 전달이 안 된다 — 발행 완료 alert 와 같은 판정 (2026-10-24 재점검 UX)
        const isNonMemberTarget = isManualMode || !selectedClinic || String(selectedClinic).startsWith('agent_') || String(selectedClinic).startsWith('email_');
        const confirmMsg = (isNonMemberTarget
            ? `${targetName} 명세서를 발행합니다.\n\n덴클 회원이 아니어서 자동 전달은 안 됩니다. 발행 뒤 [공유 링크 복사]로 카톡·문자로 보내거나 인쇄해 전달해 주세요.`
            : `${targetName}에 명세서를 보냅니다.\n보내면 치과에 바로 전달되고, 치과가 정산 확인하면 [계산서 바로 발행]을 쓸 수 있습니다.`) + zeroPriceLine + carryOverLine;
        if (!window.confirm(confirmMsg)) {
            return;
        }

        try {
            const senderDoc = await getDoc(doc(db, 'users', auth.currentUser.uid));
            const senderData = senderDoc.exists() ? senderDoc.data() : {};
            // 계좌는 paymentProfiles(비공개)에서 — users 노출 제거 (2026-08-06 보안 P1②)
            const senderBank = await fetchDomesticBank(user?.companyId || auth.currentUser.uid, senderData);

            let receiverName = manualClinicName.trim();
            let receiverId = null;
            if (!isManualMode) {
                const clinic = clinics.find(c => c.id === selectedClinic);
                const isEmailPartner = selectedClinic.startsWith('email_') || clinic?.source === 'email';
                // 비회원(에이전트 수집) 치과 — 실제 uid가 아니므로 users 조회 없이 이름 부분으로 폴백
                const isAgentPartner = selectedClinic.startsWith('agent_') || clinic?.source === 'agent';

                if (isEmailPartner || isAgentPartner) {
                    receiverId = selectedClinic;
                    receiverName = clinic?.name || selectedClinic.replace(/^(email_|agent_)/, '');
                } else {
                    receiverId = selectedClinic;
                    try {
                        const receiverDoc = await getDoc(doc(db, 'users', selectedClinic));
                        const receiverData = receiverDoc.exists() ? receiverDoc.data() : {};
                        receiverName = receiverData.businessName || receiverData.name || clinic?.name || '';
                    } catch (e) {
                        receiverName = clinic?.name || '';
                    }
                }
            }

            const statementNumber = savedStatementNumber || generateStatementNumber();
            const senderName = senderData.businessName || senderData.name || '';
            // CAD 디자인 외주 도장 + 표시 품목명 접두 — 발행 경로에도 동일 적용(kind 태그 누락 방지, 법무 P0)
            const outsourcePartner = !isManualMode ? clinics.find(c => c.id === selectedClinic && c.source === 'design_outsource') : null;
            const outsourceTag = outsourcePartner ? {
                kind: 'design_outsource',
                counterpartyType: 'lab',
                commissionSource: 'design_outsource',
                ...(outsourcePartner.designOutsourceRequestId ? { designOutsourceRequestId: outsourcePartner.designOutsourceRequestId } : {}),
            } : {};
            const itemsForSave = outsourcePartner
                ? items.map(it => ({ ...it, toothInfo: withCadDesignPrefix(it.toothInfo) }))   // serviceName(매칭 키)은 원문 유지
                : items;
            const statementData = {
                statementNumber,
                ...outsourceTag,
                fromUserId: user?.companyId || auth.currentUser.uid,
                fromUserName: senderName,
                supplierInfo: {
                    businessName: senderData.businessName || '',
                    businessNumber: senderData.businessNumber || '',
                    representativeName: senderData.representativeName || '',
                    address: senderData.address || '',
                    phone: senderData.businessPhone || senderData.phone || '',
                    bankAccount: senderBank || null,
                },
                toUserId: receiverId || '',
                toUserName: receiverName,
                isManual: isManualMode,
                startDate: startDate ? new Date(startDate) : new Date(),
                endDate: endDate ? new Date(endDate) : new Date(),
                dateBasis,
                items: serializeStatementItems(withDesignState(withRemakeOf(itemsForSave))),   // 공용 직렬화 (utils/statementItems.js)
                subtotal,
                totalDiscount: totalDiscount,
                discountType: discountType,
                discountAmount: discountAmount,
                totalAmount: total,
                totalTeeth: totalTeeth,
                notes,
                createdAt: new Date(),
                createdBy: auth.currentUser.uid,
                publishedAt: new Date(),
                ...(correctsId ? { correctsId } : {}),   // 정정 발행 원본 (2026-10-23)
                ...(carryOverField ? { carryOver: carryOverField } : {}),   // 전월 미수 고정값 (2026-10-23 1단계) — totalAmount와 별개
                status: 'confirmed',
                settlementStatus: 'pending'
            };

            let publishedId = currentStatementId;
            if (currentStatementId) {
                // 임시저장 → 발행 전환 (새 문서를 만들지 않고 같은 문서 갱신)
                const { createdAt, createdBy, ...updateData } = statementData;
                // (2026-10-23 rules 계약) 발행자·종류·작성일·작성자 고정 — 바꾸려 하면 발행이 '권한 없음'으로 거부된다
                delete updateData.fromUserId;
                delete updateData.kind;
                updateData.correctsId = correctsId || deleteField();   // 정정이 아니면 옛 correctsId 제거
                await setDoc(doc(db, 'transactionStatements', currentStatementId), { ...updateData, updatedAt: new Date() }, { merge: true });
            } else if (outsourcePartner) {
                // 외주(design_outsource)는 confirmed 직생성이 rules에서 금지된다(서버전용 필드 스텔스 우회 차단, 보안 5차).
                // draft로 먼저 만든 뒤 update로 confirmed 전이 → 확정감사·집계 트리거를 정상 통과.
                const { status: _st, publishedAt: _pub, settlementStatus: _ss, ...draftData } = statementData;
                const ref = await addDoc(collection(db, 'transactionStatements'), { ...draftData, status: 'draft' });
                publishedId = ref.id;
                await setDoc(ref, {
                    status: 'confirmed',
                    publishedAt: new Date(),
                    settlementStatus: 'pending',
                    updatedAt: new Date(),
                }, { merge: true });
            } else {
                const ref = await addDoc(collection(db, 'transactionStatements'), statementData);
                publishedId = ref.id;
            }
            invalidateReceivables(user?.companyId || auth.currentUser.uid);   // 받을 돈 캐시 — 발행으로 바뀜 (P1-4)
            invalidateSummaryCache(user?.companyId || auth.currentUser.uid);
            await markCasesInStatement(publishedId, items, { takeOverFrom: correctsId || null });   // 케이스 통일 2단계 — 역기록 (정정이면 원본 연결을 넘겨받음)
            await restoreDroppedCases(publishedId, items);   // 초안에서 지운 줄의 케이스 → 수집 대기 (P1-2)
            await saveRemakeNotes(publishedId, statementData.items);   // 리메이크 책임 별도 기록 (실패해도 발행은 유지, 화면 알림)

            // 정정 발행 (2026-10-23): 원본에 '정정됨' 표시 — rules 계약상 이 키 3개만 보낸다
            if (correctsId) {
                // 실패하면 짧게 쉬고 1회 자동 재시도 — 그래도 실패하면 원본 화면의 [정정됨 표시 다시 시도]로 안내
                const markOriginal = () => updateDoc(doc(db, 'transactionStatements', correctsId), {
                    deletedBySender: true,
                    deletedBySenderAt: serverTimestamp(),
                    correctedTo: publishedId,
                });
                try {
                    await markOriginal();
                } catch (e) {
                    console.error('원본 정정 표시 실패(1회 재시도):', e);
                    try {
                        await new Promise(r => setTimeout(r, 1500));
                        await markOriginal();
                    } catch (e2) {
                        console.error('원본 정정 표시 재시도 실패:', e2);
                        // 첫 시도가 실제로는 반영됐을 수 있다(응답만 유실) — 이미 표시돼 있으면 경고하지 않는다
                        let already = false;
                        try {
                            const o = await getDoc(doc(db, 'transactionStatements', correctsId));
                            already = o.exists() && o.data().correctedTo === publishedId;
                        } catch (e3) { console.warn('원본 정정 표시 확인 실패:', e3?.code || e3?.message); }
                        if (!already) alert("새 명세서는 발행됐지만 원본에 '정정됨' 표시를 못 했습니다. 원본 명세서를 열면 [정정됨 표시 다시 시도] 버튼이 있습니다 — 꼭 눌러주세요(안 하면 두 장이 모두 청구됩니다).");
                    }
                }
                // 원본에 묶였다가 정정본에서 뺀 케이스 → 수집 대기 (재점검 P2-a). 원본이 '정정됨'으로 바뀐 뒤에 돌려야 원본으로 재연결되지 않는다
                await restoreDroppedCases(correctsId, items);
                // 원본 공유 스냅샷 삭제 — 링크로 열람 중인 치과에 옛 금액이 남지 않게 (실패해도 발행은 완료)
                try {
                    const origSnap = await getDoc(doc(db, 'transactionStatements', correctsId));
                    const origToken = origSnap.exists() ? origSnap.data().shareToken : null;
                    if (origToken) await deleteDoc(doc(db, 'statementShares', origToken));
                } catch (e) {
                    console.warn('원본 공유 스냅샷 삭제 실패:', e);
                }
            }

            // 덴클 회원 거래처만 알림 전송 (email_ 거래처는 계정이 없어 유령 알림이 됨)
            if (receiverId && !receiverId.startsWith('email_') && !receiverId.startsWith('agent_')) { // 비회원(수집) 치과 유령 알림 방지
                try {
                    await createNotification(correctsId ? {
                        recipientId: receiverId,
                        type: 'statement_corrected',
                        title: '정정 거래명세서',
                        message: `${senderName}에서 정정 명세서를 보냈습니다 (원본 ${correctsNumber || '번호 없음'} → 새 ${statementNumber}). 다시 확인해주세요.`,
                        link: '/transactions',
                    } : {
                        recipientId: receiverId,
                        type: 'statement_received',
                        title: '거래명세서 수신',
                        message: `${senderName}에서 거래명세서를 발행했습니다. (${statementNumber})`,
                        link: '/transactions',
                    });
                } catch (notifError) {
                    console.error('알림 전송 실패:', notifError);
                }
            }

            await learnPricesToPriceList();   // 화면을 떠나기 전에 단가 학습 제안

            // 미가입/수집 거래처(agent_·email_)는 알림 전달이 안 됨 — 발송 문구 대신 인쇄·PDF 안내
            const isUndeliverableReceiver = receiverId && (receiverId.startsWith('agent_') || receiverId.startsWith('email_'));
            alert((isManualMode || isUndeliverableReceiver)
                ? '발행 완료. 명세서 화면 ⋯ 메뉴의 [공유 링크 복사]로 치과에 보내거나, 인쇄해 전달해 주세요.'
                : '거래명세서가 발행되었습니다!\n\n거래처에게 발송되었습니다.');
            onBack();
        } catch (error) {
            console.error('발행 실패:', error);
            alert('발행에 실패했습니다: ' + error.message);
        }
    };

    const handlePrint = () => {
        // 검색 필터가 걸린 채 인쇄하면 일부 환자만 찍힘 — 인쇄 전 전체 표시로 복귀
        if (patientSearch) {
            setPatientSearch('');
            setTimeout(() => window.print(), 60);
            return;
        }
        window.print();
    };

    const formatCurrency = (amount) => {
        return new Intl.NumberFormat('ko-KR').format(amount);
    };

    // ── 거래명세서 1단계 (2026-10-23): 청구 마감일·받을 돈 1회 로드 (대표만) ──
    useEffect(() => {
        if (!isLabOwner || !carryLabId) return;
        let alive = true;
        getLabBilling(carryLabId)
            .then(b => { if (alive) setLabBilling(b); })
            .catch(e => console.warn('청구 마감일 읽기 실패:', e?.code || e?.message));
        loadReceivables(carryLabId).then(r => {
            if (!alive) return;
            if (r?.error) { console.warn('받을 돈 계산 실패:', r.error); return; }
            setRecvByPartner(r?.byPartner || {});
        });
        return () => { alive = false; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [carryLabId, isLabOwner]);

    // 전월 미수 미리 계산 — 거래처가 바뀔 때만 (체크는 기본 꺼짐으로 되돌림). 발행 때 다시 계산해 고정한다
    useEffect(() => {
        setCarryOverPreview(null);
        setIncludeCarryOver(false);
        if (!isLabOwner || !selectedPartnerKey || isEditingPublished) return;
        let alive = true;
        // 미리보기는 원장 캐시 사용(거래처 바꿀 때마다 새로 읽지 않음). 이번 명세서·정정 원본 제외, 귀속일 = 기간 종료일
        computeCarryOver(carryLabId, selectedPartnerKey, new Date(), [currentStatementId, correctsId].filter(Boolean),
            { attributionDate: endDate || null })
            .then(co => { if (alive) setCarryOverPreview({ amount: Math.round(Number(co?.amount) || 0), asOf: co?.asOf || '' }); })
            .catch(e => console.warn('전월 미수 미리 계산 실패:', e?.code || e?.message));
        return () => { alive = false; };
        // endDate 도 의존성에 — 기간을 바꾸면 귀속일 기준이 달라져 미리보기도 다시 계산 (2026-10-24 재점검 P2-9)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedPartnerKey, isEditingPublished, isLabOwner, correctsId, endDate]);

    // ── 자동 초안 (2026-10-23 1단계 §3-3 · §2-3) ──
    // 순서: 거래처 찾기 → 첫 조회(웹 의뢰서) 끝 → [담기]와 같은 경로(loadCollectedIntoItems)로 수집분 병합 → 바로 임시저장.
    // 발행은 하지 않는다. 각 단계 실패는 화면 안내줄로 알린다(조용한 실패 금지).
    const autoCollectFrom = autoDraft ? (autoDraft.collectFrom || autoDraft.from) : '';
    const autoCollectTo = autoDraft ? (autoDraft.collectTo || autoDraft.to) : '';
    const autoWho = autoDraft ? (autoDraft.partnerName || '이 치과') : '';
    const autoStageRef = useRef(autoStage);
    autoStageRef.current = autoStage;
    // 0) 거래처 id 없이 넘어온 경우 — 목록의 거래처 키로 찾는다(비회원 치과 = 이름 키)
    useEffect(() => {
        if (!autoDraft || autoStage !== 'waitSearch' || statementId || selectedClinic || clinics.length === 0) return;
        const hit = clinics.find(c => c.source !== 'design_outsource' && ledgerPartnerKeyOf(c.id, c.name) === autoDraft.partnerKey);
        if (hit) { setSelectedClinic(hit.id); return; }
        setAutoStage('error');
        setAutoMsg({ kind: 'error', text: `${autoWho}를 거래처 목록에서 찾지 못했습니다. 거래처를 직접 고른 뒤 [담기]를 눌러 주세요.` });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [clinics, autoStage]);
    // 진행 안내 + 첫 조회가 30초 안에 안 끝나면 멈추고 알린다
    useEffect(() => {
        if (!autoDraft || autoStage !== 'waitSearch') return;
        setAutoMsg({ kind: 'info', text: `${autoWho} ${autoDraft.label || ''} 초안을 만드는 중입니다… 화면을 닫지 마세요` });
        const t = setTimeout(() => {
            if (autoStageRef.current !== 'waitSearch') return;
            console.warn('자동 초안: 첫 조회 대기 시간 초과');
            setAutoStage('error');
            setAutoMsg({ kind: 'error', text: '자동으로 담지 못했습니다. 기간을 확인하고 수집분 줄의 [담기]를 직접 눌러 주세요.' });
        }, 30000);
        return () => clearTimeout(t);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [autoStage]);
    // 1) 첫 조회 끝 + 기존 명세서 사용 이력 로드 끝 → 수집분 담기
    //   - 살아 있는 발행본에 이미 있는 id는 담지 않는다(skipIds, 새 초안이면 첫 조회의 웹 의뢰서 줄도 거름)
    //   - [이번 초안에 넣기](unbilled)는 그 행의 caseIds만 담는다(그 달 전체 [담기] 아님). 새 초안이면 첫 조회 줄을 비우고 시작
    const autoResultRef = useRef(null);
    useEffect(() => {
        if (autoStage !== 'waitSearch' || searchTick < 1 || loading || !selectedClinic || clinics.length === 0 || usedLoaded === null) return;
        if (usedLoaded === 'error') {
            setAutoStage('error');
            setAutoMsg({ kind: 'error', text: '이미 발행된 명세서를 확인하지 못해 자동으로 담지 않았습니다. 새로고침 후 다시 시도하거나 직접 담아 주세요.' });
            return;
        }
        setAutoStage('collecting');
        (async () => {
            try {
                const isBilled = (it) => {
                    const oid = String(it.orderId || '');
                    return liveBilledIds.has(oid) || (it.parsedCaseId && liveBilledIds.has(String(it.parsedCaseId).replace(/^case:/, '')))
                        || (oid.startsWith('case:') && liveBilledIds.has(oid.slice(5)));
                };
                const onlyCaseIds = autoDraft.mode === 'unbilled' && Array.isArray(autoDraft.caseIds) && autoDraft.caseIds.length > 0
                    ? new Set(autoDraft.caseIds.map(x => String(x).replace(/^case:/, ''))) : null;
                if (!statementId) {
                    if (onlyCaseIds) {
                        // 새 초안 = 이 작업만. 첫 조회로 들어온 줄은 비우고 '본 것' 기억도 지운다(다음 [담기]에 정상으로 잡히게)
                        setItems([]);
                        seenOrderIdsRef.current = new Set();
                    } else {
                        setItems(prev => prev.filter(it => !isBilled(it)));
                    }
                }
                const res = await loadCollectedIntoItems(autoCollectFrom, autoCollectTo, {
                    skipIds: liveBilledIds,
                    ...(onlyCaseIds ? { onlyCaseIds } : {}),
                });
                autoResultRef.current = res;
                if (!res?.ok) {
                    console.warn('자동 초안 담기 실패(조회 결과 없음)');
                    setAutoStage('error');
                    setAutoMsg({ kind: 'error', text: '수집분을 담지 못했습니다. 초안은 저장하지 않았습니다 — 수집분 줄의 [담기]를 직접 눌러 주세요.' });
                    return;
                }
                if (onlyCaseIds && res.added === 0) {
                    setAutoStage('error');
                    setAutoMsg({ kind: 'error', text: res.skippedBilled > 0
                        ? '이 작업은 이미 발행된 명세서에 들어 있어 넣지 않았습니다.'
                        : '이 작업을 담지 못했습니다(이미 이 초안에 있거나 수집 대기 상태가 아님). 초안은 저장하지 않았습니다.' });
                    return;
                }
                setAutoStage('save');
            } catch (e) {
                console.error('자동 초안 담기 실패:', e?.code || e?.message);
                setAutoStage('error');
                setAutoMsg({ kind: 'error', text: '수집분을 담지 못했습니다. 수집분 줄의 [담기]를 직접 눌러 주세요.' });
            }
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [autoStage, searchTick, loading, selectedClinic, clinics.length, usedLoaded]);
    // 2) 담기 반영된 화면 → 즉시 임시저장 (마감 초안이면 closingDraft 표식)
    useEffect(() => {
        if (autoStage !== 'save') return;
        if (items.length === 0) {
            setAutoStage('done');
            setAutoMsg({ kind: 'info', text: `${autoWho} ${autoCollectFrom} ~ ${autoCollectTo}에 담을 작업이 없어 초안을 만들지 않았습니다.` });
            return;
        }
        setAutoStage('saving');
        (async () => {
            // 자동저장 등 다른 저장이 진행 중이면 잠깐 기다린다(최대 10초)
            for (let i = 0; i < 20 && saveInFlightRef.current; i++) await new Promise(r => setTimeout(r, 500));
            const extra = autoDraft.mode === 'closing' ? {
                closingDraft: {
                    partnerKey: autoDraft.partnerKey, from: autoDraft.from, to: autoDraft.to,
                    closeDay: autoDraft.closeDay ?? null, createdAt: new Date(),
                },
            } : null;
            const savedId = await handleSaveDraft(true, extra);
            if (savedId) {
                console.log('자동 초안 저장:', savedId, '항목', items.length);   // id·건수만
                invalidateSummaryCache(carryLabId);   // 목록 카드 ①(초안에만)·② 다시 계산
                setAutoStage('done');
                const skipped = autoResultRef.current?.skippedBilled || 0;
                setAutoMsg({ kind: 'ok', text: (autoDraft.mode === 'closing'
                    ? `${autoWho} ${autoDraft.label || ''} 초안이 만들어졌습니다. 확인 후 [발행하기]를 누르세요.`
                    : `${autoWho} 초안에 담고 임시저장했습니다. 확인 후 [발행하기]를 누르세요.`)
                    + (skipped > 0 ? ` (이미 발행된 명세서에 있는 ${skipped}건은 넣지 않았습니다)` : '') });
            } else {
                setAutoStage('error');
                setAutoMsg({ kind: 'error', text: '초안을 저장하지 못했습니다. 아래 [임시저장]을 눌러 다시 시도해 주세요.' });
            }
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [autoStage]);

    const { subtotal, discountAmount, total, totalTeeth } = calculateTotal();

    return (
        <div style={styles.container}>
            {/* 에이전트 연결 상태 — 작성 전에 수집 누락 가능성을 먼저 알림 (2026-07-30 대표 지시) */}
            <AgentStatusBanner user={user} style={{ marginBottom: 12 }} />
            <style>
                {`
                    /* DatePicker 입력 필드 스타일 */
                    .ts-date-input {
                        width: 100%;
                        padding: 8px 10px;
                        border: 1px solid #cbd5e1;
                        border-radius: 6px;
                        font-size: 13px;
                        background: #fff;
                        color: #0f172a;
                        outline: none;
                        font-family: inherit;
                    }
                    .ts-date-input:focus {
                        border-color: #6366f1;
                    }
                    .ts-cell-date {
                        width: 90px;
                        padding: 4px 6px;
                        border: 1px solid #cbd5e1;
                        border-radius: 4px;
                        font-size: 12px;
                        background: #fff;
                        color: #0f172a;
                        outline: none;
                        text-align: center;
                        font-family: inherit;
                    }
                    .ts-cell-date:focus {
                        border-color: #6366f1;
                    }
                    .react-datepicker-wrapper {
                        display: inline-block;
                    }

                    @media print {
                        .print-only { display: inline !important; }
                        @page {
                            size: A4;
                            margin: 10mm;
                        }
                        html, body {
                            font-size: 10px !important;
                            -webkit-print-color-adjust: exact !important;
                            print-color-adjust: exact !important;
                        }
                        body * {
                            visibility: hidden;
                        }
                        .print-area, .print-area * {
                            visibility: visible;
                        }
                        .print-area {
                            position: absolute;
                            left: 0;
                            top: 0;
                            width: 100%;
                            padding: 5mm !important;
                            font-size: 9px !important;
                        }
                        .print-area h1, .print-area h3 {
                            font-size: 12px !important;
                            margin: 5px 0 !important;
                        }
                        .print-area table {
                            font-size: 8px !important;
                            width: 100% !important;
                        }
                        .print-area th, .print-area td {
                            padding: 0px 2px !important;
                            font-size: 8px !important;
                            line-height: 0.8 !important;
                        }
                        .print-area th {
                            font-size: 7px !important;
                        }
                        /* 환자명 열 화면 확대(170px)와 무관하게 인쇄는 기존 폭 유지 (2026-08-05) */
                        .print-area th.ts-col-patient {
                            width: 100px !important;
                        }
                        .print-area input, .print-area select, .print-area textarea {
                            border: none !important;
                            background: transparent !important;
                            font-size: 8px !important;
                            padding: 0 !important;
                        }
                        .no-print {
                            display: none !important;
                        }
                        /* 총액 섹션 압축 */
                        .print-area > div {
                            padding: 8px !important;
                            margin-bottom: 8px !important;
                        }
                        /* 페이지 나눔 방지 */
                        .print-area table {
                            page-break-inside: auto;
                        }
                        .print-area tr {
                            page-break-inside: avoid;
                            page-break-after: auto;
                        }
                    }
                `}
            </style>

            <div className="no-print" style={styles.header}>
                <h1 style={styles.title}>거래명세서 작성</h1>
                <button onClick={onBack} style={styles.backButton}>
                    <X size={16} />
                    닫기
                </button>
            </div>

            <FlowBar
                steps={['작성 중', '발행됨', '정산 완료']}   // 10-23 대표 결정 3단계 (보기 화면과 같음)
                current={isEditingPublished ? 1 : 0}
                tone={isEditingPublished ? 'warn' : 'pri'}
                message={isEditingPublished
                    ? '발행된 명세서를 수정하고 있습니다.'
                    : currentStatementId ? '작성 중인 명세서를 이어서 편집하고 있습니다.' : '거래처와 기간을 선택하면 내역이 자동으로 채워집니다.'}
                small={isEditingPublished
                    ? '[수정 내용 저장]을 누르면 거래처에 재확인 요청이 발송됩니다'
                    : '발행 전에는 거래처에 보이지 않습니다 · 45초마다 자동 저장 · Ctrl+S 저장, Ctrl+Enter 발행'}
            />

            {/* 정정 발행 안내 (2026-10-23) */}
            {correctsId && (
                <div className="no-print" style={{ background: '#FFF7ED', border: '1px solid #FED7AA', borderRadius: 10, padding: '10px 14px', marginBottom: 12, fontSize: 13, color: '#9A3412', fontWeight: 700 }}>
                    정정 명세서 작성 중 — 원본 {correctsNumber || '(번호 확인 중)'} (발행하면 원본은 '정정됨'으로 바뀝니다)
                </div>
            )}
            {/* 단가표 읽기 실패 (2026-10-23 P1) — 빈 단가표로 학습해 기존 단가표를 덮어쓰지 않게 학습도 멈춘다 */}
            {priceListLoadFailed && (
                <div className="no-print" style={{ background: '#FEF2F2', border: '1px solid #FECACA', borderRadius: 10, padding: '10px 14px', marginBottom: 12, fontSize: 13, color: '#B91C1C', fontWeight: 700 }}>
                    단가표를 불러오지 못했습니다. 새로고침 후 다시 열어주세요 — 이 상태에서는 단가 학습을 하지 않습니다.
                </div>
            )}

            {/* 이어쓰기 병합 안내 (2026-07-30) */}
            {mergeNotice && (
                <div className="no-print" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, background: '#EFF6FF', border: '1px solid #BFDBFE', borderRadius: 10, padding: '10px 14px', marginBottom: 12, fontSize: 13, color: '#1D4ED8', fontWeight: 700 }}>
                    <span>{mergeNotice}</span>
                    <button onClick={() => setMergeNotice('')} style={{ border: 'none', background: 'transparent', color: '#1D4ED8', cursor: 'pointer', fontWeight: 800 }}>닫기</button>
                </div>
            )}

            {/* 자동 초안 진행·결과 안내 (2026-10-23 1단계) — 발행은 사람이 누른다 */}
            {autoMsg && (
                <div className="no-print" style={{
                    display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', borderRadius: 10, padding: '10px 14px', marginBottom: 12, fontSize: 13, fontWeight: 700,
                    background: autoMsg.kind === 'error' ? '#FEF2F2' : autoMsg.kind === 'ok' ? '#F0FDF4' : '#EEF2FF',
                    border: `1px solid ${autoMsg.kind === 'error' ? '#FECACA' : autoMsg.kind === 'ok' ? '#BBF7D0' : '#C7D2FE'}`,
                    color: autoMsg.kind === 'error' ? '#B91C1C' : autoMsg.kind === 'ok' ? '#166534' : '#3730A3',
                }}>
                    <span style={{ flex: 1, minWidth: 200 }}>{autoMsg.text}</span>
                    {autoMsg.kind !== 'info' && autoDraft?.mode === 'closing' && (
                        <button onClick={onBack}
                            style={{ padding: '5px 12px', fontSize: 12.5, fontWeight: 700, border: '1px solid #CBD5E1', borderRadius: 7, background: '#fff', color: '#334155', cursor: 'pointer' }}>
                            {'목록으로 (다음 치과)'}
                        </button>
                    )}
                    {autoMsg.kind !== 'info' && (
                        <button onClick={() => setAutoMsg(null)} style={{ border: 'none', background: 'transparent', color: 'inherit', cursor: 'pointer', fontWeight: 800 }}>{'닫기'}</button>
                    )}
                </div>
            )}

            {/* 검색 조건 — 단계형 리디자인 (2026-07-19): 거래처 → 기간 칩(즉시 조회) → 도구 */}
            <div className="no-print" style={{ background: '#fff', border: '1px solid #E2E8F0', borderRadius: 14, padding: '16px 18px', marginBottom: 16 }}>
                <div style={{ display: 'flex', gap: 18, flexWrap: 'wrap', alignItems: 'flex-start' }}>
                    {/* ① 거래처 */}
                    <div style={{ flex: '0 1 260px', minWidth: 220 }}>
                        <div style={{ fontSize: 12.5, fontWeight: 800, color: '#334155', marginBottom: 6 }}>
                            <span style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 18, height: 18, borderRadius: '50%', background: '#6366F1', color: '#fff', fontSize: 11, marginRight: 6 }}>1</span>
                            거래처
                        </div>
                        <select
                            value={selectedClinic}
                            onChange={(e) => {
                                if (!confirmClinicSwitch(e.target.value)) return;   // 취소 = 이전 거래처 유지
                                setSelectedClinic(e.target.value);
                                if (e.target.value !== '__manual__') setManualClinicName('');
                            }}
                            disabled={isEditingPublished || !!correctsId}   // 발행본·정정 작성 중은 거래처 변경 불가 (2026-10-23 rules 계약 — 정정본은 원본과 같은 거래처여야 '정정됨' 표시가 통과)
                            style={{ ...styles.select, width: '100%', padding: '10px 12px', fontSize: 14 }}
                        >
                            <option value="">거래처를 선택하세요</option>
                            {/* 묶음 구분 (2026-07-30): 덴클로 연결된 거래처와, 파일에서 이름만 읽어온 미가입 치과를
                                같은 목록에 섞으면 연결된 것처럼 보인다 — 세 묶음으로 분리 */}
                            {(() => {
                                const linked = clinics.filter(c => c.source !== 'agent' && c.source !== 'email' && c.source !== 'design_outsource');
                                const mailed = clinics.filter(c => c.source === 'email');
                                const unlinked = clinics.filter(c => c.source === 'agent');
                                const outsource = clinics.filter(c => c.source === 'design_outsource');   // CAD 디자인 외주 기공소
                                return (<>
                                    {linked.length > 0 && (
                                        <optgroup label="덴클로 연결된 거래처">
                                            {linked.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                                        </optgroup>
                                    )}
                                    {outsource.length > 0 && (
                                        <optgroup label="CAD 디자인 외주 (기공소)">
                                            {outsource.map(c => <option key={c.id} value={c.id}>{c.name} (CAD 외주)</option>)}
                                        </optgroup>
                                    )}
                                    {mailed.length > 0 && (
                                        <optgroup label="이메일로 받은 치과">
                                            {mailed.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                                        </optgroup>
                                    )}
                                    {unlinked.length > 0 && (
                                        <optgroup label="파일에서 인식된 치과 (덴클 미가입)">
                                            {unlinked.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                                        </optgroup>
                                    )}
                                </>);
                            })()}
                            <option value="__manual__">직접 입력</option>
                        </select>
                        {/* 청구 마감일 · 받을 돈 한 줄 (2026-10-23 1단계 §5-2) — 대표만. 받을 돈은 PIN 구역 화면 안에서만 */}
                        {isLabOwner && selectedPartnerKey && (selectedClosingDay || selectedRecv) && (
                            <div style={{ marginTop: 6, fontSize: 12, color: '#475569', fontWeight: 600 }}>
                                {selectedClosingDay
                                    ? `청구 마감 ${selectedClosingDay === 'end' ? '말일' : `${selectedClosingDay}일`}`
                                    : '청구 마감 안 씀'}
                                {selectedRecv && (selectedRecv.balance > 0
                                    ? ` · 받을 돈 ${formatCurrency(selectedRecv.balance)}원`
                                    : selectedRecv.balance < 0
                                        ? ` · 받을 돈 없음 · ${formatCurrency(-selectedRecv.balance)}원 더 받음`
                                        : ' · 받을 돈 0원')}
                            </div>
                        )}
                        {/* CAD 디자인 외주 기공소 선택 시 — 외주 명세서임을 명시 */}
                        {selectedOutsourcePartner && (
                            <div style={{ marginTop: 6, padding: '8px 10px', background: '#EEF2FF', border: '1px solid #C7D2FE', borderRadius: 8, fontSize: 11.5, color: '#3730A3', lineHeight: 1.5 }}>
                                <b>CAD 디자인 외주</b> 명세서입니다. 세금계산서·정산은 일반 거래명세서와 똑같이 이어집니다.
                            </div>
                        )}
                        {/* 미가입 치과 선택 시 — 무엇이 다른지 그 자리에서 알려준다 */}
                        {isAgentClinicSelected && (
                            <div style={{ marginTop: 6, padding: '8px 10px', background: '#FFF7ED', border: '1px solid #FED7AA', borderRadius: 8, fontSize: 11.5, color: '#9A3412', lineHeight: 1.5 }}>
                                덴클에 가입하지 않은 치과입니다. 파일에 적힌 이름으로만 작성되며,
                                <b> 저장된 단가표가 없어 금액은 직접 입력</b>해야 합니다. 발행해도 치과에는 전달되지 않습니다(인쇄·PDF로 전달).
                            </div>
                        )}
                        {selectedClinic === '__manual__' ? (
                            <input
                                type="text"
                                value={manualClinicName}
                                onChange={(e) => setManualClinicName(e.target.value)}
                                onKeyDown={(e) => { if (e.key === 'Enter' && manualClinicName.trim()) handleSearch(); }}
                                placeholder="거래처(치과)명을 입력하고 Enter"
                                style={{ ...styles.input, marginTop: 8 }}
                                autoFocus
                            />
                        ) : isEditingPublished ? (
                            <div style={{ marginTop: 6, fontSize: 11.5, color: '#94A3B8' }}>발행된 명세서는 거래처를 바꿀 수 없습니다</div>
                        ) : correctsId ? (
                            <div style={{ marginTop: 6, fontSize: 11.5, color: '#94A3B8' }}>정정 명세서는 원본과 같은 거래처로만 발행됩니다. 거래처가 잘못됐다면 이 화면을 나가 새 명세서를 작성해주세요.</div>
                        ) : (
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 6, flexWrap: 'wrap' }}>
                                <span style={{ fontSize: 11.5, color: '#94A3B8' }}>목록에 없으면 →</span>
                                <button type="button" onClick={() => { if (!confirmClinicSwitch('__manual__')) return; setSelectedClinic('__manual__'); setManualClinicName(''); }}
                                    style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 12, fontWeight: 700, color: '#4338CA', background: '#EEF2FF', border: '1px solid #C7D2FE', borderRadius: 7, padding: '5px 10px', cursor: 'pointer' }}>
                                    <Plus size={13} /> 거래처 직접 추가
                                </button>
                            </div>
                        )}
                    </div>

                    {/* ② 기간 — 칩 클릭 즉시 조회 */}
                    {!isManualMode && (
                        <div style={{ flex: '1 1 400px', minWidth: 300 }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6, flexWrap: 'wrap' }}>
                                <span style={{ fontSize: 12.5, fontWeight: 800, color: '#334155' }}>
                                    <span style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 18, height: 18, borderRadius: '50%', background: '#6366F1', color: '#fff', fontSize: 11, marginRight: 6 }}>2</span>
                                    기간 <span style={{ fontWeight: 500, color: '#94A3B8' }}>누르면 바로 조회됩니다</span>
                                </span>
                                {/* 10-31 대표: 작성일/납기일 고르기 없앰 — 늘 디자인 완성일 기준(CAD 파일 날짜, 웹 의뢰서는 짝이 맞는 수집분 날짜·없으면 작성일) */}
                                <span style={{ marginLeft: 'auto', padding: '5px 12px', fontSize: 12, fontWeight: 700, borderRadius: 8, background: '#EEF2FF', color: '#4338CA' }}
                                    title="CAD에서 디자인을 마친 날(파일 날짜) 기준으로 불러옵니다. 웹 의뢰서는 같은 환자·치식의 CAD 파일 날짜, 없으면 의뢰서 작성일">
                                    {'디자인 완성일 기준'}
                                </span>
                            </div>
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                                {[
                                    // 청구 마감 기간 칩 (2026-10-23 1단계) — 거래처에 청구 마감일이 있을 때만
                                    ...(selectedClosingPeriod ? [{
                                        key: 'closing',
                                        label: `${selectedClosingPeriod.label} ${Number(selectedClosingPeriod.from.slice(5, 7))}/${Number(selectedClosingPeriod.from.slice(8))}~${Number(selectedClosingPeriod.to.slice(5, 7))}/${Number(selectedClosingPeriod.to.slice(8))}`,
                                        fn: () => ({ start: selectedClosingPeriod.from, end: selectedClosingPeriod.to }),
                                    }] : []),
                                    { key: 'thisMonth', label: '이번 달', fn: getThisMonth },
                                    { key: 'lastMonth', label: '지난 달', fn: getLastMonth },
                                    { key: 'thisWeek', label: '이번 주', fn: getThisWeek, more: true },
                                    { key: 'today', label: '오늘', fn: getToday, more: true },
                                    { key: 'last7', label: '최근 7일', fn: getLast7Days, more: true },
                                    { key: 'last30', label: '최근 30일', fn: getLast30Days, more: true },
                                // 'more' 칩은 '더 보기'를 펼쳤거나 지금 선택된 칩일 때만 노출 (선택 상태가 안 보이는 일 방지)
                                ].filter(p => !p.more || moreChipsOpen || activePreset === p.key).map(p => (
                                    <button key={p.key} type="button"
                                        onClick={() => {
                                            // 발행본 수정 중 재조회는 저장된 항목·할인을 통째로 교체 — 반드시 확인
                                            if (isEditingPublished && items.length > 0 &&
                                                !window.confirm('기간을 변경해 다시 조회하면 발행본의 현재 항목·할인 내용이 재조회 결과로 교체됩니다.\n계속할까요?')) return;
                                            // (2026-08-13 P0-1) 손본 내용이 있으면 재조회 전 확인 — 단가 수정·삭제·추가가 사라짐
                                            if (!isEditingPublished && dirtyRef.current &&
                                                !window.confirm('작성 중인 내용이 있습니다. 다시 불러오면 손보신 단가·삭제·추가가 사라집니다. 계속할까요?')) return;
                                            const period = p.fn();
                                            setActivePreset(p.key);
                                            handleQuickDateSelect(period.start, period.end);
                                            if (selectedClinic && selectedClinic !== '__manual__') handleSearch(period.start, period.end);
                                        }}
                                        style={{ padding: '8px 14px', fontSize: 13, fontWeight: 700, borderRadius: 20, cursor: 'pointer',
                                            border: activePreset === p.key ? '1.5px solid #6366F1' : '1px solid #E2E8F0',
                                            background: activePreset === p.key ? '#EEF2FF' : '#fff',
                                            color: activePreset === p.key ? '#4338CA' : '#475569' }}>
                                        {p.label}
                                    </button>
                                ))}
                                <button type="button"
                                    onClick={() => setActivePreset('custom')}
                                    style={{ padding: '8px 14px', fontSize: 13, fontWeight: 700, borderRadius: 20, cursor: 'pointer',
                                        border: activePreset === 'custom' ? '1.5px solid #6366F1' : '1px dashed #CBD5E1',
                                        background: activePreset === 'custom' ? '#EEF2FF' : '#fff',
                                        color: activePreset === 'custom' ? '#4338CA' : '#64748B' }}>
                                    직접 지정
                                </button>
                                {/* 더 보기 — 이번 주·오늘·최근 7일·최근 30일 펼치기/접기 (2026-10-23 §5-2) */}
                                <button type="button"
                                    onClick={() => setMoreChipsOpen(o => !o)}
                                    aria-expanded={moreChipsOpen}
                                    style={{ padding: '8px 14px', fontSize: 13, fontWeight: 700, borderRadius: 20, cursor: 'pointer',
                                        border: '1px dashed #CBD5E1', background: '#fff', color: '#64748B' }}>
                                    {moreChipsOpen ? '접기 ▴' : '더 보기 ▾'}
                                </button>
                                {/* (2026-08-31 대표 확정 시안) 우상단 [📥 수집분 불러오기] 토글 버튼·인라인 패널은
                                    기간 줄 아래 '수집분 한 줄 바'로 흡수 — 로직(collectPanelOpen·loadCollectedIntoItems)은 재사용 */}
                            </div>
                            {/* 직접 지정 시에만 날짜 입력 노출 */}
                            {activePreset === 'custom' && (
                                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 10, flexWrap: 'wrap' }}>
                                    <DatePicker
                                        selected={startDate ? new Date(startDate) : null}
                                        onChange={(d) => setStartDate(d ? formatDateHelper(d) : '')}
                                        locale={ko}
                                        dateFormat="yyyy-MM-dd"
                                        placeholderText="시작일"
                                        className="ts-date-input"
                                    />
                                    <span style={{ color: '#94A3B8' }}>~</span>
                                    <DatePicker
                                        selected={endDate ? new Date(endDate) : null}
                                        onChange={(d) => setEndDate(d ? formatDateHelper(d) : '')}
                                        locale={ko}
                                        dateFormat="yyyy-MM-dd"
                                        placeholderText="종료일"
                                        className="ts-date-input"
                                    />
                                    <button
                                        onClick={() => { if (confirmDiscardIfDirty()) handleSearch(); }}
                                        disabled={loading}
                                        style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '9px 18px', fontSize: 13.5, fontWeight: 700, border: 'none', borderRadius: 8, cursor: 'pointer', background: '#6366F1', color: '#fff' }}
                                    >
                                        <Search size={15} />
                                        {loading ? '조회 중...' : '조회'}
                                    </button>
                                </div>
                            )}
                        </div>
                    )}
                </div>

                {/* ── 수집분 한 줄 바 (2026-08-31 대표 확정 시안: 입구 1개·정보 1줄) ──
                    기존 우상단 토글 버튼 + 노란 안내줄 + 하단 수집 현황 정보줄을 이 한 줄로 통합.
                    수집 0건 치과면 줄 자체 숨김 · 외주 거래처 전체 미노출 */}
                {!isManualMode && !selectedOutsourcePartner && selectedClinic && collectSummary
                    && (collectSummary.total + collectSummary.excludedRows.length) > 0 && (() => {
                    // 미가입 치과(agent_/email_) = 자동 합산 유지 대상 — 안내만, 담기 버튼 숨김
                    const isUnregistered = String(selectedClinic).startsWith('agent_') || String(selectedClinic).startsWith('email_');
                    const excludedN = collectSummary.excludedRows.length;
                    const totalN = collectSummary.total;                                   // 기간 내 수집(대기+담김+제외)
                    const notIn = isUnregistered ? 0 : pendingCollectCount;               // 아직 화면에 없는 대기 건
                    const inN = Math.max(0, totalN - excludedN - notIn);                  // 명세서에 담긴(담길) 건
                    const allIn = notIn === 0;
                    return (
                        <div className="no-print" style={{ marginTop: 12 }}>
                            <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 10,
                                background: allIn ? '#F0FDF4' : '#EEF2FF',
                                border: `1px solid ${allIn ? '#BBF7D0' : '#C7D2FE'}`,
                                borderRadius: 10, padding: '9px 14px', fontSize: 13.5,
                                color: allIn ? '#166534' : '#3730A3' }}>
                                {isUnregistered ? (
                                    <span>📥 수집분 <b>{totalN}건</b> 자동 반영됨</span>
                                ) : allIn ? (
                                    <span>📥 수집분 <b>{totalN}건</b> — 전부 담김 ✓</span>
                                ) : (
                                    <span>📥 수집분 — 이 기간 <b>{totalN}건</b> · 명세서에 <b>{inN}건</b> 담김 · 아직 없는 <b>{notIn}건</b></span>
                                )}
                                {!isUnregistered && notIn > 0 && (
                                    <button type="button" onClick={() => loadCollectedIntoItems(startDate, endDate)} disabled={collectLoading}
                                        style={{ padding: '6px 14px', fontSize: 12.5, fontWeight: 800, border: 'none', borderRadius: 8, cursor: 'pointer', background: '#4F46E5', color: '#fff', opacity: collectLoading ? 0.6 : 1 }}>
                                        {collectLoading ? '담는 중...' : `${notIn}건 담기`}
                                    </button>
                                )}
                                {!isUnregistered && (
                                    <button type="button"
                                        onClick={() => {
                                            setCollectPanelOpen(v => {
                                                if (!v) { setCollectFrom(startDate); setCollectTo(endDate); }   // 기본 = 현재 조회 기간
                                                return !v;
                                            });
                                        }}
                                        style={{ padding: '6px 12px', fontSize: 12.5, fontWeight: 700, borderRadius: 8, cursor: 'pointer',
                                            border: '1px solid #C7D2FE', background: collectPanelOpen ? '#E0E7FF' : '#fff', color: '#4338CA' }}>
                                        다른 기간에서 담기 {collectPanelOpen ? '▴' : '▾'}
                                    </button>
                                )}
                                {excludedN > 0 && (
                                    <button type="button" onClick={() => setShowExcluded(v => !v)}
                                        style={{ background: 'none', border: 'none', color: '#4F46E5', fontWeight: 700, fontSize: 12.5, cursor: 'pointer', textDecoration: 'underline', textUnderlineOffset: 3 }}>
                                        {showExcluded ? `제외 ${excludedN}건 접기 ▴` : `제외 ${excludedN}건 보기 ▾`}
                                    </button>
                                )}
                            </div>
                            {/* [다른 기간에서 담기] 인라인 펼침 — 기간 지정 후 즉시 합산 (미리보기 없음, 대표 확정) */}
                            {collectPanelOpen && !isUnregistered && (
                                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 6, flexWrap: 'wrap', background: '#F5F7FF', border: '1px solid #C7D2FE', borderRadius: 10, padding: '9px 12px' }}>
                                    <span style={{ fontSize: 12.5, fontWeight: 800, color: '#3730A3' }}>수집분 기간</span>
                                    <input type="date" value={collectFrom} max={collectTo || undefined}
                                        onChange={e => setCollectFrom(e.target.value)}
                                        style={{ padding: '6px 8px', fontSize: 12.5, border: '1px solid #C7D2FE', borderRadius: 8, color: '#334155', background: '#fff' }} />
                                    <span style={{ color: '#94A3B8' }}>~</span>
                                    <input type="date" value={collectTo} min={collectFrom || undefined}
                                        onChange={e => setCollectTo(e.target.value)}
                                        style={{ padding: '6px 8px', fontSize: 12.5, border: '1px solid #C7D2FE', borderRadius: 8, color: '#334155', background: '#fff' }} />
                                    <button type="button" onClick={() => loadCollectedIntoItems()} disabled={collectLoading}
                                        style={{ padding: '7px 16px', fontSize: 13, fontWeight: 800, border: 'none', borderRadius: 8, cursor: 'pointer', background: '#4F46E5', color: '#fff', opacity: collectLoading ? 0.6 : 1 }}>
                                        {collectLoading ? '담는 중...' : '담기'}
                                    </button>
                                    <span style={{ fontSize: 11.5, color: '#64748B' }}>파일 작업일 기준 · 이미 담긴 건은 다시 넣지 않습니다</span>
                                </div>
                            )}
                            {/* 제외된 수집 건 목록 — [복원] 후 [담기]로 재포함 */}
                            {showExcluded && excludedN > 0 && (
                                <div style={{ border: '1px dashed #FDE68A', background: '#FFFBEB', borderRadius: 10, padding: '9px 14px', marginTop: 6 }}>
                                    <div style={{ fontSize: 12.5, color: '#B45309', fontWeight: 700, marginBottom: 4 }}>제외된 수집 건 — [복원] 후 [담기]를 누르면 항목에 다시 들어옵니다</div>
                                    {collectSummary.excludedRows.map(r => (
                                        <div key={r.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '5px 0', borderTop: '1px solid #FDE68A', fontSize: 13 }}>
                                            <span style={{ flex: 1 }}>{displayPatientName(r).name || '환자미상'} · {(r.teeth || []).length > 0 ? `#${(r.teeth || []).join(',')}` : ''} {r.indications?.[0] || ''}</span>
                                            <span style={{ color: '#94A3B8', fontSize: 12 }}>{(r.fileMtime || '').slice(5, 10).replace('-', '.')}</span>
                                            <button onClick={() => restoreParsedCase(r)}
                                                style={{ padding: '3px 10px', border: '1px solid #C7D2FE', background: '#fff', color: '#4F46E5', borderRadius: 7, fontSize: 12, fontWeight: 700, cursor: 'pointer' }}>
                                                복원
                                            </button>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    );
                })()}

                {/* 하단 도구 줄 */}
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 14, paddingTop: 12, borderTop: '1px solid #F1F5F9', flexWrap: 'wrap' }}>
                    {isManualMode ? (
                        <button
                            onClick={handleSearch}
                            disabled={loading}
                            style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '10px 22px', fontSize: 14, fontWeight: 700, border: 'none', borderRadius: 10, cursor: 'pointer', background: '#6366F1', color: '#fff' }}
                        >
                            <Search size={16} /> 작성 시작
                        </button>
                    ) : (
                        loading && <span style={{ fontSize: 13, color: '#6366F1', fontWeight: 700 }}>조회 중...</span>
                    )}
                    <div style={{ flex: 1 }} />
                    <button
                        onClick={openPriceListModal}
                        style={styles.priceListButton}
                    >
                        단가표 관리
                        {priceList.length > 0 && (
                            <span style={styles.priceListBadge}>
                                {priceList.length}
                            </span>
                        )}
                    </button>
                    <button
                        onClick={() => fileInputRef.current?.click()}
                        style={styles.uploadButton}
                        title="다운로드한 Excel(.csv/.xlsx) 양식을 불러옵니다"
                    >
                        <Upload size={16} />
                        Excel 업로드
                    </button>
                    <input
                        ref={fileInputRef}
                        type="file"
                        accept=".csv,.xlsx,.xls"
                        onChange={handleExcelUpload}
                        style={{ display: 'none' }}
                    />
                </div>
            </div>

            {/* 에이전트 자동 수집 항목 — 검수 박스 제거: 이제 [조회] 시 자동 등록되어 거래 내역에 바로 표시 (2026-07) */}
            {false && parsedCases.length > 0 && (() => {
                const dateChosen = !!(parsedFrom && parsedTo);
                const visibleParsed = !dateChosen ? [] : parsedCases.filter(r => {
                    const d = (r.fileMtime || '').slice(0, 10);
                    return d >= parsedFrom && d <= parsedTo;
                });
                return (
                <div className="no-print" style={{ background: '#fff', border: '2px solid #6366F1', borderRadius: 12, padding: '16px 18px', marginBottom: 16 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8, marginBottom: 10 }}>
                        <div style={{ fontWeight: 800, fontSize: 15, color: '#3730A3' }}>
                            에이전트 자동 수집 항목 {dateChosen ? `${visibleParsed.length}건` : `(전체 ${parsedCases.length}건 대기)`}
                            <span style={{ fontSize: 12, fontWeight: 600, color: '#6366F1', marginLeft: 8 }}>
                                {dateChosen ? 'CAD 파일에서 자동 인식 — 확인 후 의뢰서로 등록하세요' : '작업 기간을 먼저 선택하세요'}
                            </span>
                        </div>
                        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                            <input type="date" value={parsedFrom} max={parsedTo || undefined} onChange={e => setParsedFrom(e.target.value)}
                                style={{ padding: '6px 8px', fontSize: 12.5, border: `1px solid ${parsedFrom ? '#E5E7EB' : '#A5B4FC'}`, borderRadius: 8, color: '#334155', background: parsedFrom ? '#fff' : '#EEF2FF' }} title="작업일 시작" />
                            <span style={{ color: '#94A3B8', fontSize: 12 }}>~</span>
                            <input type="date" value={parsedTo} min={parsedFrom || undefined} onChange={e => setParsedTo(e.target.value)}
                                style={{ padding: '6px 8px', fontSize: 12.5, border: `1px solid ${parsedTo ? '#E5E7EB' : '#A5B4FC'}`, borderRadius: 8, color: '#334155', background: parsedTo ? '#fff' : '#EEF2FF' }} title="작업일 끝" />
                            {(parsedFrom || parsedTo) && (
                                <button onClick={() => { setParsedFrom(''); setParsedTo(''); }} style={{ padding: '6px 10px', fontSize: 12, border: '1px solid #E5E7EB', background: '#fff', borderRadius: 8, cursor: 'pointer', color: '#64748B' }}>날짜 초기화</button>
                            )}
                            {dateChosen && <>
                                <button onClick={() => setParsedChecked(Object.fromEntries(visibleParsed.map(r => [r.id, true])))} style={{ padding: '7px 12px', fontSize: 12.5, border: '1px solid #E5E7EB', background: '#fff', borderRadius: 8, cursor: 'pointer' }}>표시된 항목 전체 선택</button>
                                <button onClick={() => importParsedCases(visibleParsed)} style={{ padding: '7px 14px', fontSize: 13, fontWeight: 700, border: 'none', background: '#6366F1', color: '#fff', borderRadius: 8, cursor: 'pointer' }}>선택 항목 의뢰서로 등록</button>
                            </>}
                        </div>
                    </div>
                    {!dateChosen ? (
                        <div style={{ padding: '18px 14px', textAlign: 'center', color: '#6366F1', fontSize: 13.5, background: '#F5F7FF', borderRadius: 8 }}>
                            📅 위에서 <b>시작일과 종료일</b>을 선택하면 그 기간에 작업된 자동 수집 항목만 표시됩니다.
                        </div>
                    ) : (
                    <div style={{ overflowX: 'auto' }}>
                        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                            <thead><tr style={{ background: '#F8FAFC', color: '#64748B', fontSize: 12 }}>
                                <th style={{ padding: '8px 10px', textAlign: 'left' }}></th>
                                <th style={{ padding: '8px 10px', textAlign: 'left' }}>일자</th>
                                <th style={{ padding: '8px 10px', textAlign: 'left' }}>환자</th>
                                <th style={{ padding: '8px 10px', textAlign: 'left' }}>치식</th>
                                <th style={{ padding: '8px 10px', textAlign: 'left' }}>종류/재료</th>
                                <th style={{ padding: '8px 10px', textAlign: 'left' }}>출처</th>
                                <th style={{ padding: '8px 10px' }}></th>
                            </tr></thead>
                            <tbody>
                                {visibleParsed.map(r => (
                                    <tr key={r.id} style={{ borderTop: '1px solid #F1F5F9' }}>
                                        <td style={{ padding: '7px 10px' }}><input type="checkbox" checked={!!parsedChecked[r.id]} onChange={e => setParsedChecked(prev => ({ ...prev, [r.id]: e.target.checked }))} /></td>
                                        <td style={{ padding: '7px 10px', whiteSpace: 'nowrap', color: '#64748B' }}>{(r.fileMtime || '').slice(0, 10)}</td>
                                        <td style={{ padding: '7px 10px', fontWeight: 600 }}>
                                            {r.patientName || '환자미상'}
                                            {r.isRemake && <span style={{ marginLeft: 6, fontSize: 11, fontWeight: 700, background: r.remakeKind === 'repair' ? '#ECFEFF' : '#FEF2F2', color: r.remakeKind === 'repair' ? '#0E7490' : '#DC2626', padding: '2px 8px', borderRadius: 6 }}>{r.remakeKind === 'repair' ? '수리' : '리메이크'}</span>}
                                        </td>
                                        <td style={{ padding: '7px 10px' }}>{(r.teeth || []).join(', ') || '-'}</td>
                                        <td style={{ padding: '7px 10px', color: '#475569' }}>{[(r.indications || [])[0], r.material].filter(Boolean).join(' / ') || '-'}</td>
                                        <td style={{ padding: '7px 10px' }}><span style={{ fontSize: 11, fontWeight: 700, background: '#EEF2FF', color: '#6366F1', padding: '2px 8px', borderRadius: 6 }}>{r.sourceType}</span></td>
                                        <td style={{ padding: '7px 10px', textAlign: 'right' }}><button onClick={() => excludeParsedCase(r.id)} style={{ fontSize: 12, border: 'none', background: 'none', color: '#94A3B8', cursor: 'pointer' }}>제외</button></td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                    )}
                </div>
                );
            })()}

            {/* 테이블 */}
            {/* (2026-08-31 대표 확정 시안) 아래 노란 안내줄은 기간 줄 아래 '수집분 한 줄 바'로 흡수 — 렌더만 정지, 주석 보존 */}
            {false && selectedClinic && !isManualMode && !selectedOutsourcePartner && pendingCollectCount > 0 && (
                <div className="no-print" style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 10, background: '#FFFBEB', border: '1px solid #FDE68A', borderRadius: 10, padding: '9px 14px', margin: '0 0 10px', fontSize: 13.5, color: '#92400E' }}>
                    <span>📥 이 치과의 수집분 <b>{pendingCollectCount}건</b>이 아직 명세서에 없습니다.</span>
                    <button type="button" onClick={() => loadCollectedIntoItems(startDate, endDate)} disabled={collectLoading}
                        style={{ padding: '5px 14px', fontSize: 12.5, fontWeight: 800, border: '1px solid #C7D2FE', background: '#EEF2FF', color: '#4338CA', borderRadius: 8, cursor: 'pointer', opacity: collectLoading ? 0.6 : 1 }}>
                        {collectLoading ? '불러오는 중...' : '불러오기'}
                    </button>
                </div>
            )}
            {/* 수집 현황 요약줄 (2026-07-30 수집함 통합) — 2026-08-31 '수집분 한 줄 바'(기간 줄 아래)로 흡수, 렌더만 정지·주석 보존 */}
            {false && collectSummary && collectSummary.total + collectSummary.excludedRows.length > 0 && (
                <div className="no-print" style={{ margin: '0 0 10px' }}>
                    <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 10, background: '#EEF2FF', border: '1px solid #C7D2FE', borderRadius: 10, padding: '9px 14px', fontSize: 13.5 }}>
                        <span>📁 이 기간 수집 <b>{collectSummary.total}건</b> — 명세서에 포함 <b>{collectSummary.included}</b> · 제외됨 <b>{collectSummary.excludedRows.length}</b></span>
                        {collectSummary.excludedRows.length > 0 && (
                            <button onClick={() => setShowExcluded(v => !v)}
                                style={{ background: 'none', border: 'none', color: '#4F46E5', fontWeight: 700, fontSize: 12.5, cursor: 'pointer', textDecoration: 'underline', textUnderlineOffset: 3 }}>
                                {showExcluded ? '제외된 건 접기 ▴' : '제외된 건 보기 ▾'}
                            </button>
                        )}
                    </div>
                    {showExcluded && (
                        <div style={{ border: '1px dashed #FDE68A', background: '#FFFBEB', borderRadius: 10, padding: '9px 14px', marginTop: 6 }}>
                            <div style={{ fontSize: 12.5, color: '#B45309', fontWeight: 700, marginBottom: 4 }}>제외된 수집 건 — [복원] 후 [수집분 불러오기]를 누르면 항목에 다시 들어옵니다</div>
                            {collectSummary.excludedRows.map(r => (
                                <div key={r.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '5px 0', borderTop: '1px solid #FDE68A', fontSize: 13 }}>
                                    <span style={{ flex: 1 }}>{displayPatientName(r).name || '환자미상'} · {(r.teeth || []).length > 0 ? `#${(r.teeth || []).join(',')}` : ''} {r.indications?.[0] || ''}</span>
                                    <span style={{ color: '#94A3B8', fontSize: 12 }}>{(r.fileMtime || '').slice(5, 10).replace('-', '.')}</span>
                                    <button onClick={() => restoreParsedCase(r)}
                                        style={{ padding: '3px 10px', border: '1px solid #C7D2FE', background: '#fff', color: '#4F46E5', borderRadius: 7, fontSize: 12, fontWeight: 700, cursor: 'pointer' }}>
                                        복원
                                    </button>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            )}
            {items.length > 0 && (
                <>
                    <div className="print-area" style={styles.tableSection}>
                        <div className="no-print" style={styles.tableSectionHeader}>
                            <div style={{display: 'flex', alignItems: 'center', gap: '12px'}}>
                                <h3 style={styles.tableSectionTitle}>
                                    거래 내역 ({items.length}건 / 총 {totalTeeth}개)
                                </h3>
                                {/* 종이 의뢰서 대조 진행 (2026-07-30 사장 요청) — 환자 단위로 센다.
                                    체크박스가 환자당 하나(첫 행)라서 항목 수가 아닌 환자 수 기준 */}
                                {(() => {
                                    // 환자 그룹 첫 항목 id만 모아 분모로 사용 (표의 rowSpan 그룹핑과 같은 키)
                                    const firstIds = [];
                                    let prevKey = null;
                                    items.forEach(it => {
                                        const k = `${it.patientName}-${formatDate(it.createdAt)}-${formatDate(it.dueDate)}-${formatDate(it.designDate)}`;
                                        if (k !== prevKey) { firstIds.push(it.id); prevKey = k; }
                                    });
                                    const done = firstIds.filter(id => verifiedPatients[id]).length;
                                    const total = firstIds.length;
                                    if (total === 0) return null;
                                    const complete = done === total;
                                    return (
                                        <span className="no-print" style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 12.5, fontWeight: 800, padding: '3px 10px', borderRadius: 7,
                                            background: complete ? '#DCFCE7' : '#F0FDF4', color: complete ? '#15803D' : '#166534', border: `1px solid ${complete ? '#86EFAC' : '#BBF7D0'}` }}
                                            title="종이 의뢰서와 대조한 환자 수 (저장되지 않음)">
                                            ✓ {done}명 / {total}명 대조{complete ? ' 완료' : ''}
                                        </span>
                                    );
                                })()}
                                {items.some(it => it.source === 'auto') && (
                                    <span className="no-print" style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 12, color: '#6D28D9', background: '#F5F3FF', border: '1px solid #DDD6FE', padding: '3px 9px', borderRadius: 6 }}>
                                        <span style={{ width: 10, height: 10, background: '#7C3AED', borderRadius: 2, display: 'inline-block' }} /> 에이전트 자동 수집분
                                    </span>
                                )}
                                <div style={styles.sortToggle}>
                                    <button
                                        onClick={() => handleSortByChange('createdAt')}
                                        style={sortBy === 'createdAt' ? styles.sortButtonActive : styles.sortButtonInactive}
                                        title="디자인 완성일(CAD 파일 날짜) 순 — 수집분은 작성일 칸이 비어 있어 디자인 완성일로 정렬합니다(10-31)"
                                    >
                                        디자인 완성일순
                                    </button>
                                    <button
                                        onClick={() => handleSortByChange('dueDate')}
                                        style={sortBy === 'dueDate' ? styles.sortButtonActive : styles.sortButtonInactive}
                                    >
                                        납기일순
                                    </button>
                                </div>
                            </div>
                            <div style={{display: 'flex', gap: '8px'}}>
                                <button onClick={applyPriceList} style={styles.applyPriceListButton}>
                                    단가표 적용
                                </button>
                                <button onClick={downloadExcel} style={styles.downloadButton}>
                                    <Download size={14} />
                                    Excel 다운로드
                                </button>
                                <button onClick={handleAddItem} style={styles.addItemButton}>
                                    <Plus size={14} />
                                    항목 추가
                                </button>
                                <button onClick={handleFinishEdit} style={styles.editAllButton} title="작성일 순서로 다시 정렬">
                                    날짜순 정렬
                                </button>
                            </div>
                        </div>

                        {/* 단가 미입력 경고 */}
                        {(() => {
                            // 무상 재제작(needsPriceCheck)은 의도적 0원 — 미입력 경고에서 제외(무상확인 배지와 이중 표기 방지)
                            const zeroCount = items.filter(i => (i.unitPrice || 0) === 0 && !i.needsPriceCheck).length;
                            return zeroCount > 0 ? (
                                <div className="no-print" style={{ display: 'flex', alignItems: 'center', gap: 8, background: '#FFF7ED', border: '1px solid #FDBA74', borderRadius: 8, padding: '9px 14px', margin: '0 0 10px', fontSize: 13, color: '#9A3412', fontWeight: 700 }}>
                                    단가 미입력 {zeroCount}건 — 주황색 행입니다. [단가표 적용]을 누르거나 개당가격을 직접 입력하세요.
                                </div>
                            ) : null;
                        })()}

                        {/* 디자인 전 건수 요약 (2026-09-06 대표 지시) — 0건이면 표시하지 않는다 */}
                        {(() => {
                            const pendingCount = items.filter(i => (resolveDesign(i) || {}).state === 'pending').length;
                            return pendingCount > 0 ? (
                                <div className="no-print" style={{ display: 'flex', alignItems: 'center', gap: 8, background: '#FFF7ED', border: '1px solid #FED7AA', borderRadius: 8, padding: '7px 14px', margin: '0 0 10px', fontSize: 13, color: '#C2410C', fontWeight: 700 }}>
                                    {`디자인 전 ${pendingCount}건`}
                                    <span style={{ fontWeight: 500, color: '#9A3412' }}>— 아직 디자인이 끝나지 않은 케이스가 명세서에 들어 있습니다. 행의 배지로 확인하세요.</span>
                                </div>
                            ) : null;
                        })()}

                        {/* 단가표 자동완성 (치아정보 입력 시) */}
                        <datalist id="ts-price-options">
                            {priceList.map((p, i) => (
                                <option key={i} value={p.serviceName} />
                            ))}
                        </datalist>

                        {/* 환자명 검색 (2026-08-05 대표 요청) — 종이 의뢰서 대조 시 해당 환자만 골라 보기. 화면 필터일 뿐 저장·합계는 전체 */}
                        <div className="no-print" style={{ display: 'flex', alignItems: 'center', gap: 10, margin: '0 0 8px' }}>
                            <div style={{ position: 'relative', display: 'inline-flex', alignItems: 'center' }}>
                                <Search size={14} style={{ position: 'absolute', left: 10, color: '#94A3B8', pointerEvents: 'none' }} />
                                <input
                                    type="text"
                                    value={patientSearch}
                                    onChange={(e) => setPatientSearch(e.target.value)}
                                    placeholder="환자명 검색"
                                    style={{ padding: '7px 30px 7px 30px', border: '1px solid #CBD5E1', borderRadius: 8, fontSize: 13, width: 190, outline: 'none' }}
                                />
                                {patientSearch && (
                                    <button
                                        onClick={() => setPatientSearch('')}
                                        title="검색 지우기"
                                        style={{ position: 'absolute', right: 6, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 18, height: 18, border: 'none', background: '#E2E8F0', color: '#475569', borderRadius: '50%', cursor: 'pointer', fontSize: 12, lineHeight: 1, padding: 0 }}
                                    >
                                        ×
                                    </button>
                                )}
                            </div>
                            {patientSearch.trim() && (() => {
                                const q = patientSearch.replace(/\s/g, '').toLowerCase();
                                const shown = items.filter(it => (it.patientName || '').replace(/\s/g, '').toLowerCase().includes(q)).length;
                                return (
                                    <span style={{ fontSize: 12, color: '#B45309', background: '#FFFBEB', border: '1px solid #FDE68A', borderRadius: 6, padding: '3px 9px', fontWeight: 700 }}>
                                        {`검색 결과 ${shown}건 표시 중 — 검색 중에도 저장·합계는 전체 ${items.length}건 기준`}
                                    </span>
                                );
                            })()}
                        </div>
                        <div className="no-print" style={{ fontSize: 12, color: '#94A3B8', margin: '0 0 6px 2px' }}>
                            ↑↓←→ 방향키·Tab으로 칸 이동 · 단가칸 Enter: 다음 빈 단가로 · Ctrl+D: 윗줄 단가 복사 · Ctrl+S: 임시저장 · Ctrl+Enter: 발행
                        </div>
                        <div style={styles.tableContainer} onKeyDown={handleGridKeyDown}>
                            <table style={styles.table}>
                                <thead>
                                    <tr style={styles.tableHeader}>
                                        <th className="no-print" style={{...styles.th, width: '54px'}}>의뢰서</th>
                                        <th style={{...styles.th, width: '70px'}}>작성일</th>
                                        <th style={{...styles.th, width: '70px'}}>납기일</th>
                                        {/* 환자명 폭 확대 (2026-08-05 대표 요청): '[리메이크]' 표기가 잘려 안 보임 — 인쇄는 아래 print CSS로 기존 폭 유지 */}
                                        <th className="ts-col-patient" style={{...styles.th, width: '170px'}}>환자명</th>
                                        <th style={{...styles.th, width: 'auto', minWidth: '200px'}}>치아정보</th>
                                        <th style={{...styles.th, width: '50px'}}>개수</th>
                                        <th style={{...styles.th, width: '100px'}}>개당가격</th>
                                        <th style={{...styles.th, width: '100px'}}>합산가격</th>
                                        <th className="no-print" style={{...styles.th, width: '60px'}}>작업</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {(() => {
                                        // 환자명 검색 필터 (화면 표시만 — 저장·합계·발행은 전체 items 기준)
                                        const q = patientSearch.replace(/\s/g, '').toLowerCase();
                                        const visibleItems = q
                                            ? items.filter(it => (it.patientName || '').replace(/\s/g, '').toLowerCase().includes(q))
                                            : items;
                                        // 같은 환자의 행들을 그룹화
                                        const groupedItems = [];
                                        let currentPatient = null;
                                        let currentGroup = [];

                                        visibleItems.forEach((item, index) => {
                                            const patientKey = `${item.patientName}-${formatDate(item.createdAt)}-${formatDate(item.dueDate)}-${formatDate(item.designDate)}`;

                                            if (patientKey !== currentPatient) {
                                                if (currentGroup.length > 0) {
                                                    groupedItems.push(currentGroup);
                                                }
                                                currentPatient = patientKey;
                                                currentGroup = [item];
                                            } else {
                                                currentGroup.push(item);
                                            }
                                        });

                                        if (currentGroup.length > 0) {
                                            groupedItems.push(currentGroup);
                                        }

                                        // 렌더링
                                        return groupedItems.map((group, groupIndex) => {
                                            const rows = group.map((item, itemIndex) => {
                                            // 금값 파생 행 — 치아정보·개수·개당가격 칸을 전용 표기로 (중량×시세, 면세)
                                            const isGoldRow = item.itemType === 'gold';
                                            // 이 행에 이미 연결된 금값 행이 있으면 [+금] 버튼 숨김 (부모당 금값 1행)
                                            const hasGoldChild = !isGoldRow && !!item.goldLineId &&
                                                items.some(g => g.itemType === 'gold' && g.goldForItemId === item.goldLineId);
                                            return (
                                                <tr key={item.id} style={{ ...styles.tableRow,
                                                    // 환자 묶음 구분은 그룹 사이 간격 띠(tr no-print)가 담당 — 아래 배경 규칙만 유지
                                                    // 단가 0원(미매칭)이 최우선 주황 — 수집분(연보라)이 덮으면 배너 문구("주황색 행")와 어긋남 (2026-09-01 검증 P2).
                                                    // 수집분 0원 행은 주황 배경 + 보라 인셋을 병행해 출처 구분도 유지. (금값 행은 연노랑, 무상 재제작은 0원 판정 제외)
                                                    ...(isGoldRow ? { background: '#FFFBEB' }
                                                        : ((item.unitPrice || 0) === 0 && !item.needsPriceCheck)
                                                            ? { background: '#FFF7ED', ...(item.source === 'auto' ? { boxShadow: 'inset 3px 0 0 #7C3AED' } : {}) }
                                                        : item.source === 'auto' ? { background: '#F5F3FF', boxShadow: 'inset 3px 0 0 #7C3AED' } : {}) }}>
                                                    {/* 의뢰서 확인 - 첫 번째 행에만, 클릭 시 원본 의뢰서 모달 */}
                                                    {itemIndex === 0 && (
                                                        <td className="no-print" style={{ ...styles.td, textAlign: 'center' }} rowSpan={group.length}>
                                                            {item.orderId ? (
                                                                <button
                                                                    onClick={async () => {
                                                                        const ord = searchedOrders.find(o => o.id === item.orderId);
                                                                        if (ord) { setOrderModal(ord); return; }
                                                                        // 조회 목록에 없으면(발행본 편집·재진입 등) orderId로 직접 불러오기
                                                                        try {
                                                                            const snap = await getDoc(doc(db, 'workOrders', item.orderId));
                                                                            if (snap.exists()) setOrderModal({ id: snap.id, ...snap.data() });
                                                                            else alert('원본 의뢰서를 찾을 수 없습니다. (삭제되었거나 접근 권한 없음)');
                                                                        } catch (e) {
                                                                            alert('의뢰서를 불러오지 못했습니다.');
                                                                        }
                                                                    }}
                                                                    title="의뢰서 상세 보기"
                                                                    style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', padding: '5px 7px', border: '1px solid #C7D2FE', background: '#EEF2FF', color: '#4338CA', borderRadius: 7, cursor: 'pointer' }}>
                                                                    <FileText size={14} />
                                                                </button>
                                                            ) : (
                                                                <span style={{ color: '#CBD5E1', fontSize: 12 }}>—</span>
                                                            )}
                                                        </td>
                                                    )}
                                                    {/* 작성일 - 첫 번째 행에만 표시 */}
                                                    {itemIndex === 0 && (
                                                        <td style={styles.td} rowSpan={group.length}>
                                                            {isEditMode ? (
                                                                <DatePicker
                                                                    selected={item.createdAt ? new Date(getTimestamp(item.createdAt)) : null}
                                                                    onChange={(d) => handleCreatedAtChange(item.id, d)}
                                                                    locale={ko}
                                                                    dateFormat="MM-dd"
                                                                    placeholderText="선택"
                                                                    className="ts-cell-date"
                                                                />
                                                            ) : (
                                                                formatDate(item.createdAt)
                                                            )}
                                                        </td>
                                                    )}

                                                    {/* 마감일 - 첫 번째 행에만 표시 */}
                                                    {itemIndex === 0 && (
                                                        <td style={styles.td} rowSpan={group.length}>
                                                            {isEditMode ? (
                                                                <DatePicker
                                                                    selected={item.dueDate ? new Date(getTimestamp(item.dueDate)) : null}
                                                                    onChange={(d) => handleDueDateChange(item.id, d)}
                                                                    locale={ko}
                                                                    dateFormat="MM-dd"
                                                                    placeholderText="선택"
                                                                    className="ts-cell-date"
                                                                    isClearable
                                                                />
                                                            ) : (
                                                                <span style={item.dueDate ? styles.dueDateText : styles.noDueDateText}>
                                                                    {item.dueDate ? formatDate(item.dueDate) : '-'}
                                                                </span>
                                                            )}
                                                        </td>
                                                    )}

                                                    {/* 환자명 - 첫 번째 행에만 표시 */}
                                                    {itemIndex === 0 && (
                                                        <td style={styles.td} rowSpan={group.length}>
                                                            {/* 대조 체크 (2026-07-30 사장 요청) — 종이 의뢰서와 하나씩 맞춰볼 때 쓰는 표시.
                                                                저장되지 않는 화면 전용 표시이며 인쇄물에는 나오지 않는다. */}
                                                            <input
                                                                type="checkbox"
                                                                className="no-print"
                                                                checked={!!verifiedPatients[item.id]}
                                                                onChange={(e) => setVerifiedPatients(prev => ({ ...prev, [item.id]: e.target.checked }))}
                                                                title="종이 의뢰서와 대조 완료 표시 (저장되지 않음)"
                                                                style={{ width: 15, height: 15, marginRight: 6, verticalAlign: 'middle', cursor: 'pointer', accentColor: '#16A34A' }}
                                                            />
                                                            {isEditMode ? (
                                                                <input
                                                                    type="text"
                                                                    data-nav="patient"
                                                                    value={item.patientName}
                                                                    onChange={(e) => {
                                                                        markDirty();
                                                                        // 환자명도 묶음 전체에 적용 — 첫 행만 바꾸면 키가 어긋나 묶음이 쪼개짐 (2026-08-05, 날짜와 동일 수정)
                                                                        const ids = idsInSameGroup(item.id);
                                                                        setItems(items.map(i =>
                                                                            ids.has(i.id) ? {...i, patientName: e.target.value} : i
                                                                        ));
                                                                    }}
                                                                    style={{ ...styles.editInput, width: 'calc(100% - 22px)' }}
                                                                    placeholder="환자명"
                                                                />
                                                            ) : (
                                                                <span style={verifiedPatients[item.id] ? { color: '#94A3B8' } : undefined}>{item.patientName}</span>
                                                            )}
                                                            {item.source === 'auto' && (
                                                                <span className="no-print" style={{ display: 'inline-flex', alignItems: 'center', gap: 5, marginTop: 3 }}>
                                                                    <span style={{ fontSize: 10, fontWeight: 800, background: '#EEF2FF', color: '#4338CA', padding: '1px 7px', borderRadius: 5 }}>자동수집</span>
                                                                    {item.orderId && (
                                                                        <button onClick={() => cancelCollectedItem(item)}
                                                                            title="이 건을 명세서에서 빼고 수집 대기로 되돌립니다 (구 수집함의 되돌리기)"
                                                                            style={{ fontSize: 10, fontWeight: 700, color: '#B45309', background: '#FFFBEB', border: '1px solid #FDE68A', borderRadius: 5, padding: '1px 6px', cursor: 'pointer' }}>
                                                                            수집 취소
                                                                        </button>
                                                                    )}
                                                                </span>
                                                            )}
                                                            {item.source === 'manual' && (
                                                                <span className="no-print" style={{ display: 'inline-block', marginTop: 3, fontSize: 10, fontWeight: 800, background: '#F1F5F9', color: '#64748B', padding: '1px 7px', borderRadius: 5 }}>수기</span>
                                                            )}
                                                            {/* 이미 다른 명세서에 쓰인 건 — 작게만 알린다 (인쇄 제외). 키 = orderId ∪ parsedCaseId (2단계) */}
                                                            {(() => {
                                                                const used = (item.orderId && usedOrderMap[item.orderId])
                                                                    || (item.parsedCaseId && usedOrderMap[item.parsedCaseId]);
                                                                if (used) return (
                                                                    <div className="no-print"
                                                                        title={`이 환자 건은 ${used.number || '다른 명세서'}에 이미 포함돼 있습니다. 중복 청구가 아닌지 확인하세요.`}
                                                                        style={{ marginTop: 2, fontSize: 10, color: '#B45309', lineHeight: 1.3, whiteSpace: 'nowrap' }}>
                                                                        {used.status === 'confirmed' ? '이미 발행됨' : '작성 중 명세서에 있음'}
                                                                        {used.number ? ` · ${used.number}` : ''}
                                                                    </div>
                                                                );
                                                                // (2026-09-09) 문서 id는 다르지만 같은 환자·치식이 이 거래처의 이전 명세서에 있음 (월 넘긴 재청구 방지)
                                                                const prior = priorBilledOf(item);
                                                                return prior ? (
                                                                    <div className="no-print"
                                                                        title={`같은 환자·치식이 ${prior.number || '다른 명세서'}에 이미 있습니다. 지난달에 청구한 건이 다시 잡힌 게 아닌지, 재제작이면 무상인지 확인하세요.`}
                                                                        style={{ marginTop: 2, fontSize: 10, color: '#B45309', lineHeight: 1.3, whiteSpace: 'nowrap' }}>
                                                                        {prior.status === 'confirmed' ? '이전 명세서에 같은 환자·치식' : '작성 중 명세서에 같은 환자·치식'}
                                                                        {prior.number ? ` · ${prior.number}` : ''}
                                                                    </div>
                                                                ) : null;
                                                            })()}
                                                            {/* (2026-09-09 대표 승인) 같은 명세서 안 동일 환자·치식 2회 — 리메이크 표시 없이 두 번 잡힌 건 */}
                                                            {remakeSuspectSet.has(item.id) && (
                                                                <div className="no-print"
                                                                    title="이 명세서 안에 같은 환자·치식이 두 번 이상 있습니다. 재제작이면 한쪽은 무상(0원)인지, 같은 건이 두 번 잡힌 건 아닌지 확인하세요."
                                                                    style={{ display: 'inline-block', marginTop: 2, fontSize: 10, fontWeight: 800, background: '#FEF3C7', color: '#92400E', border: '1px solid #FCD34D', padding: '1px 7px', borderRadius: 5, whiteSpace: 'nowrap' }}>
                                                                    리메이크 예상 · 같은 환자·치식 2회
                                                                </div>
                                                            )}
                                                        </td>
                                                    )}

                                                    {/* 치아정보 (금값 행 = 표기 + 중량·시세 입력) */}
                                                    <td style={styles.td}>
                                                        {isGoldRow ? (
                                                            <>
                                                                {/* 인쇄·화면 공통 표기: "└ 금값 (1.2g × 165,000원/g)" + [면세] — 서면에 중량·시세 근거가 남는다 */}
                                                                <span style={{ ...styles.toothInfoText, color: '#92400E', fontWeight: 600 }}>{'└ '}{item.toothInfo}</span>
                                                                <span style={{ marginLeft: 6, fontSize: 10, fontWeight: 800, background: '#FEF3C7', color: '#92400E', border: '1px solid #FDE68A', padding: '1px 6px', borderRadius: 5, verticalAlign: 'middle' }}>면세</span>
                                                                {isEditMode && (
                                                                    <div className="no-print" style={{ display: 'flex', alignItems: 'center', gap: 4, marginTop: 4, fontSize: 12, color: '#92400E' }}>
                                                                        <span>중량</span>
                                                                        <input
                                                                            type="number"
                                                                            step="0.1"
                                                                            min="0"
                                                                            value={item.goldWeight ?? 0}
                                                                            onChange={(e) => handleGoldFieldChange(item.id, 'goldWeight', e.target.value)}
                                                                            style={{ ...styles.editInput, width: 58, textAlign: 'right' }}
                                                                            title="금 중량 (g, 소수 1자리)"
                                                                        />
                                                                        <span>g ×</span>
                                                                        <span>시세</span>
                                                                        <input
                                                                            type="text"
                                                                            inputMode="numeric"
                                                                            value={item.goldPricePerGram ?? 0}
                                                                            onChange={(e) => handleGoldFieldChange(item.id, 'goldPricePerGram', e.target.value.replace(/[^0-9]/g, ''))}
                                                                            style={{ ...styles.editInput, width: 84, textAlign: 'right' }}
                                                                            title="금 시세 (원/g) — 최근 입력값이 다음에 자동으로 채워집니다"
                                                                        />
                                                                        <span>원/g</span>
                                                                    </div>
                                                                )}
                                                            </>
                                                        ) : isEditMode ? (
                                                            <input
                                                                type="text"
                                                                data-nav="tooth"
                                                                list="ts-price-options"
                                                                value={item.toothInfo}
                                                                onChange={(e) => handleToothInfoChange(item.id, e.target.value)}
                                                                style={{...styles.editInput, width: '100%'}}
                                                                placeholder="#11 크라운 지르코니아 (단가표 자동완성)"
                                                            />
                                                        ) : (
                                                            <span style={styles.toothInfoText}>{item.toothInfo}</span>
                                                        )}
                                                        {/* 수리 배지 (2026-09-04 C-5): toothInfo에 접미를 넣지 않고 remakeKind로만 표시 */}
                                                        {item.remakeKind === 'repair' && (
                                                            <span style={{ marginLeft: 6, fontSize: 11, fontWeight: 700, background: '#ECFEFF', color: '#0E7490', padding: '2px 8px', borderRadius: 6 }}>수리</span>
                                                        )}
                                                        {/* 리메이크 원 건 (2026-10-24 ④): 인쇄에도 나가는 글자 = 날짜·번호만. 책임 구분은 화면 전용(no-print) */}
                                                        {!isGoldRow && item.isRemake && (() => {
                                                            const lbl = remakeOfLabel({ ...item, remakeOf: effectiveRemakeOf(item) });
                                                            return lbl ? <div style={{ marginTop: 2, fontSize: 10.5, color: '#64748B', lineHeight: 1.3 }}>{lbl}</div> : null;
                                                        })()}
                                                        {/* 디자인 완료/전 배지 (2026-09-06 대표 지시) — 화면은 색 칩(툴팁에 공정 원값·갱신 시각), 인쇄는 소형 글자 */}
                                                        {!isGoldRow && <DesignBadge badge={resolveDesign(item)} style={{ marginLeft: 6 }} />}
                                                        {/* 임플란트 힌트 배지 (2026-09-01 대표 승인) — 수집분 크라운 행인데
                                                            같은 환자의 웹 의뢰서에 임플란트 표시가 있으면 단가 확인 유도.
                                                            화면 전용(no-print) · 이미 임플/어버트로 잡힌 행·웹 의뢰서 행에는 표시 안 함 */}
                                                        {!isGoldRow && item.source === 'auto'
                                                            && canonKey(item.serviceName) === 'crown'
                                                            && !/임플|어버트|abut|implant/i.test(`${item.serviceName || ''} ${item.toothInfo || ''}`)
                                                            && implantPatientSet.has((item.patientName || '').replace(/[\s\d\-_.()[\]]/g, '').toLowerCase()) && (
                                                            <span className="no-print"
                                                                title="이 환자의 웹 의뢰서에 임플란트 표시가 있습니다 — 단가를 확인하세요"
                                                                style={{ display: 'inline-block', marginTop: 3, fontSize: 10, fontWeight: 800, background: '#FFFBEB', color: '#B45309', border: '1px solid #FDE68A', padding: '1px 7px', borderRadius: 5, whiteSpace: 'nowrap' }}>
                                                                임플란트일 수 있음
                                                            </span>
                                                        )}
                                                    </td>

                                                    {/* 개수 - 항상 화살표로 조절 가능 (금값 행은 1 고정) */}
                                                    <td style={styles.tdCenter}>
                                                        {isGoldRow ? (
                                                            <span>1</span>
                                                        ) : (
                                                        <input
                                                            type="number"
                                                            data-nav="count"
                                                            value={item.toothCount || 0}
                                                            onChange={(e) => handleToothCountChange(item.id, e.target.value)}
                                                            style={styles.countInput}
                                                            placeholder="0"
                                                            min="0"
                                                        />
                                                        )}
                                                    </td>

                                                    {/* 개당가격 - 화살표 없이 숫자만 입력 (금값 행 = 중량×시세 자동 계산, 직접 입력 불가) */}
                                                    <td style={styles.td}>
                                                        {isGoldRow ? (
                                                            <span style={{ ...styles.totalPriceText, color: '#92400E' }}>{formatCurrency(item.unitPrice || 0)}</span>
                                                        ) : (<>
                                                        <input
                                                            type="text"
                                                            inputMode="numeric"
                                                            data-nav="price"
                                                            data-item-id={item.id}
                                                            value={item.unitPrice || 0}
                                                            onChange={(e) => {
                                                                const value = e.target.value.replace(/[^0-9]/g, '');
                                                                handleUnitPriceChange(item.id, value);
                                                            }}
                                                            style={item.needsPriceCheck && !item.unitPrice
                                                                ? { ...styles.priceInput, borderColor: '#f59e0b', background: '#fffbeb' }
                                                                : styles.priceInput}
                                                            placeholder="0"
                                                            title={item.needsPriceCheck ? '재제작 항목입니다 — 무상이면 0원 그대로, 유상이면 금액을 넣으세요' : ''}
                                                        />
                                                        {/* 재제작은 단가를 자동으로 채우지 않는다 (대표 확정 2026-08-11) —
                                                            무상/유상은 사람이 정한다. 단가표 값이 있으면 참고로만 보여준다. */}
                                                        {item.needsPriceCheck && !item.unitPrice && (
                                                            <div style={{ fontSize: 10.5, color: '#b45309', marginTop: 3, lineHeight: 1.4 }}>
                                                                {'무상 여부 확인'}
                                                                {item.suggestedUnitPrice ? (
                                                                    <button
                                                                        type="button"
                                                                        onClick={() => handleUnitPriceChange(item.id, String(item.suggestedUnitPrice))}
                                                                        style={{ marginLeft: 4, fontSize: 10.5, color: '#b45309', background: 'none', border: 'none', textDecoration: 'underline', cursor: 'pointer', padding: 0 }}
                                                                    >
                                                                        {`유상이면 ${item.suggestedUnitPrice.toLocaleString()}원`}
                                                                    </button>
                                                                ) : null}
                                                                {canLinkRemake && item.isRemake && (
                                                                    <button type="button" className="no-print" onClick={() => setRemakeModalItemId(item.id)}
                                                                        title="원래 만든 건과 다시 만든 건을 나란히 보고 책임을 기록합니다 (기공소 내부 기록)"
                                                                        style={{ marginLeft: 6, fontSize: 10.5, fontWeight: 700, color: '#4338CA', background: '#EEF2FF', border: '1px solid #C7D2FE', borderRadius: 5, padding: '0 6px', cursor: 'pointer' }}>
                                                                        {'처음 만든 것 보기'}
                                                                    </button>
                                                                )}
                                                            </div>
                                                        )}
                                                        {/* 단가를 이미 정한 리메이크 줄도 원 건 보기는 열 수 있다 (2026-10-24 ④) */}
                                                        {canLinkRemake && item.isRemake && !(item.needsPriceCheck && !item.unitPrice) && (
                                                            <div className="no-print" style={{ marginTop: 3, fontSize: 10.5, lineHeight: 1.4 }}>
                                                                <button type="button" onClick={() => setRemakeModalItemId(item.id)}
                                                                    title="원래 만든 건과 다시 만든 건을 나란히 보고 책임을 기록합니다 (기공소 내부 기록)"
                                                                    style={{ fontSize: 10.5, fontWeight: 700, color: '#4338CA', background: '#EEF2FF', border: '1px solid #C7D2FE', borderRadius: 5, padding: '0 6px', cursor: 'pointer' }}>
                                                                    {'처음 만든 것 보기'}
                                                                </button>
                                                                {item.remakeLiability && (
                                                                    <span style={{ marginLeft: 5, color: '#64748B' }}>{`책임: ${({ lab: '기공소', clinic: '치과 요청·변경', shared: '같이', unknown: '모름' })[item.remakeLiability] || ''}`}</span>
                                                                )}
                                                            </div>
                                                        )}
                                                        </>)}
                                                    </td>

                                                    {/* 합산가격 (개수 × 개당가격) */}
                                                    <td style={styles.tdRight}>
                                                        <span style={styles.totalPriceText}>
                                                            {formatCurrency((item.toothCount || 0) * (item.unitPrice || 0))}원
                                                        </span>
                                                    </td>

                                                    {/* 작업 - 항목 추가 및 삭제 */}
                                                    <td className="no-print" style={styles.td}>
                                                        <div style={styles.actionButtons}>
                                                            {isEditMode && itemIndex === 0 && !isGoldRow && (
                                                                <button
                                                                    onClick={() => handleAddToothItem(item)}
                                                                    style={styles.addToothButton}
                                                                    title="치아정보 추가"
                                                                >
                                                                    <Plus size={14} />
                                                                </button>
                                                            )}
                                                            {/* [+금] — 금값 파생 행 추가 (대표 확정 2026-08-20). 외주 명세서에는 숨김(CAD 디자인 용역에 금값 줄 금지) */}
                                                            {isEditMode && !isGoldRow && !hasGoldChild && !selectedOutsourcePartner && (
                                                                <button
                                                                    onClick={() => handleAddGoldRow(item)}
                                                                    style={{ ...styles.addToothButton, background: '#FEF3C7', color: '#92400E', border: '1px solid #FDE68A', fontSize: 11, fontWeight: 800, padding: '3px 6px' }}
                                                                    title="금값 행 추가 — 중량(g)×시세(원/g)로 자동 계산, 면세 표기"
                                                                >
                                                                    +금
                                                                </button>
                                                            )}
                                                            <button
                                                                onClick={() => handleDeleteItem(item.id)}
                                                                style={styles.deleteButton}
                                                                title={isGoldRow ? '금값 행 삭제' : '삭제'}
                                                            >
                                                                <Trash2 size={14} />
                                                            </button>
                                                        </div>
                                                    </td>
                                                </tr>
                                            );
                                            });
                                            // 환자 묶음 사이 간격 띠 (2026-09-01 대표 지시 "칸 분리가 명확하게") —
                                            // 전 행이 수집분(연보라)일 때 얇은 경계선만으로는 안 보여, 흰 간격 + 굵은 선으로 확실히 띄운다
                                            return (
                                                <React.Fragment key={'grp-' + (group[0]?.id || groupIndex)}>
                                                    {groupIndex > 0 && (
                                                        <tr className="no-print">
                                                            <td colSpan={20} style={{ padding: 0, height: 8, background: '#ffffff', borderTop: '1px solid #e2e8f0', borderBottom: '2.5px solid #94A3B8' }} />
                                                        </tr>
                                                    )}
                                                    {rows}
                                                </React.Fragment>
                                            );
                                        });
                                    })()}
                                </tbody>
                            </table>
                        </div>
                    </div>

                    {/* 합계 */}
                    <div className="print-area" style={styles.totalSection}>
                        <div style={styles.totalRow}>
                            <span style={styles.totalLabel}>소계:</span>
                            <span style={styles.totalValue}>{formatCurrency(subtotal)}원</span>
                        </div>

                        <div style={styles.discountRow}>
                            <span style={styles.totalLabel}>총 할인:</span>
                            <div style={styles.discountInputGroup}>
                                <input
                                    type="number"
                                    value={totalDiscount}
                                    onChange={(e) => { markDirty(); setTotalDiscount(Number(e.target.value) || 0); }}
                                    style={styles.discountInputLarge}
                                    placeholder="0"
                                    min="0"
                                />
                                <select
                                    value={discountType}
                                    onChange={(e) => { markDirty(); setDiscountType(e.target.value); }}
                                    style={styles.discountTypeSelectLarge}
                                >
                                    <option value="amount">원</option>
                                    <option value="percent">%</option>
                                </select>
                            </div>
                        </div>

                        {discountAmount > 0 && (
                            <div style={styles.totalRow}>
                                <span style={styles.totalLabel}>할인 금액:</span>
                                <span style={styles.discountValue}>-{formatCurrency(discountAmount)}원</span>
                            </div>
                        )}

                        <div style={styles.totalRowFinal}>
                            <div>
                                <div style={styles.totalLabelFinal}>최종 합계</div>
                                <div style={styles.totalTeethInfo}>총 {totalTeeth}개</div>
                            </div>
                            <div style={styles.totalValueWrapper}>
                                <div style={styles.totalValueFinal}>{formatCurrency(total)}원</div>
                                {totalTeeth > 0 && (
                                    <div style={styles.avgPerTooth}>
                                        {/* 분자도 금값 제외 — 금값 포함 시 개당 평균이 부풀려짐 (검수 P2-2) */}
                                        {/* Number(i.count) → Number(i.toothCount) — count 필드는 없어서 항상 0원이던 버그 (2026-08-26) */}
                                        개당 평균: {formatCurrency(Math.round(items.filter(i => i.itemType !== 'gold').reduce((s2, i) => s2 + (Number(i.toothCount) || 0) * (Number(i.unitPrice) || 0), 0) / totalTeeth))}원
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* 전월 미수 (2026-10-23 1단계 §4-5 · 계약 §1) — 기본 꺼짐, 0원 이하면 숨김. 켜면 발행 때 다시 계산해 합계 아래 별도 상자로 고정.
                        합계(totalAmount)에는 더하지 않는다. 작성 화면 조작 줄이라 인쇄 제외 */}
                    {carryOverAllowed && (
                        <div className="no-print" style={{ margin: '10px 0 0', padding: '10px 14px', border: '1px solid #E2E8F0', borderRadius: 10, background: '#F8FAFC', fontSize: 13, color: '#334155' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                                <span style={{ fontWeight: 700 }}>{`전월 미수 ${formatCurrency(carryOverPreview.amount)}원`}</span>
                                <span style={{ fontSize: 11.5, color: '#94A3B8' }}>{`${carryOverPreview.asOf} 현재 · 입금 기록 기준`}</span>
                                <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, cursor: 'pointer', marginLeft: 'auto', fontWeight: 600 }}>
                                    <input type="checkbox" checked={includeCarryOver} onChange={(e) => setIncludeCarryOver(e.target.checked)}
                                        style={{ width: 15, height: 15, accentColor: '#6366F1' }} />
                                    {'명세서에 적기'}
                                </label>
                            </div>
                            {includeCarryOver && isNonMemberPartner && (
                                <div style={{ marginTop: 6, fontSize: 12, color: '#B45309', fontWeight: 600 }}>
                                    {'덴클 회원이 아닌 치과입니다 — 입금을 빠짐없이 기록했는지 확인한 뒤 적어 주세요. 이미 낸 치과에 미수로 찍힐 수 있습니다.'}
                                </div>
                            )}
                            {includeCarryOver && (
                                <div style={{ marginTop: 4, fontSize: 11.5, color: '#64748B' }}>
                                    {'합계에는 더하지 않고, 합계 아래 별도 상자로 적습니다. 금액은 발행할 때 다시 계산합니다.'}
                                </div>
                            )}
                        </div>
                    )}

                    {/* 메모 */}
                    <div className="print-area" style={styles.notesSection}>
                        <label style={styles.notesLabel}>메모/특이사항</label>
                        <textarea
                            value={notes}
                            onChange={(e) => setNotes(e.target.value)}
                            style={styles.notesTextarea}
                            placeholder="거래명세서에 추가할 메모를 입력하세요..."
                        />
                    </div>

                    {/* 액션 버튼 — 주 버튼 1개 원칙: 작성 중=발행하기 / 발행본 수정=수정 저장 */}
                    <div className="no-print" style={{ ...styles.actions, alignItems: 'center' }}>
                        {lastAutoSavedAt && !isEditingPublished && (
                            <span style={{ fontSize: 12, color: '#94A3B8', marginRight: 'auto' }}>
                                자동 저장됨 {`${String(lastAutoSavedAt.getHours()).padStart(2, '0')}:${String(lastAutoSavedAt.getMinutes()).padStart(2, '0')}`}
                            </span>
                        )}
                        <button onClick={handlePrint} style={styles.printButton}>
                            <Printer size={16} />
                            인쇄
                        </button>
                        {isEditingPublished ? (
                            <button
                                onClick={handleSaveEdit}
                                title="수정 내용 저장: 발행 상태는 유지되며, 거래처에 재확인 요청 알림이 발송됩니다"
                                style={{ ...styles.publishButton, background: '#D97706' }}
                                disabled={saving}
                            >
                                <Save size={16} />
                                {saving ? '저장 중...' : '수정 내용 저장'}
                            </button>
                        ) : (
                            <>
                                <button
                                    onClick={() => handleSaveDraft()}
                                    title="임시저장: 작성 중인 내용만 저장됩니다 (거래처에 발송 안 됨) · 45초마다 자동 저장됩니다"
                                    style={styles.saveDraftButton}
                                    disabled={saving}
                                >
                                    <Save size={16} />
                                    {saving ? '저장 중...' : '임시저장'}
                                </button>
                                <button
                                    onClick={handlePublish}
                                    title="발행하기: 저장 후 거래처에 즉시 발송됩니다"
                                    style={styles.publishButton}
                                    disabled={saving}
                                >
                                    <Send size={16} />
                                    {saving ? '발행 중...' : '발행하기'}
                                </button>
                            </>
                        )}
                    </div>

                    {/* 안내 메시지 — 상시 설명 3줄은 각 버튼의 title 툴팁으로 옮김 (2026-10-23 §5-2, 내용 동일) */}
                </>
            )}

            {/* 검색 결과 없음 */}
            {!loading && items.length === 0 && selectedClinic && !isManualMode && startDate && endDate && (
                <div style={styles.emptyState}>
                    <div style={styles.emptyIcon}>📋</div>
                    <p style={styles.emptyText}>해당 기간에 의뢰서가 없습니다.</p>
                    <p style={styles.emptySubtext}>다른 날짜 범위를 선택하거나, 직접 항목을 추가해서 작성할 수 있습니다.</p>
                    {/* 지난달 프리셋 제안 (2026-08-26) — 월초 마감 작성 시 흔한 상황 */}
                    <button
                        onClick={() => {
                            const period = getLastMonth();
                            setActivePreset('lastMonth');
                            handleQuickDateSelect(period.start, period.end);
                            handleSearch(period.start, period.end);
                        }}
                        style={{
                            marginTop: 16, marginRight: 8, padding: '10px 20px', display: 'inline-flex', alignItems: 'center', gap: 6,
                            background: '#EEF2FF', color: '#4338CA', border: '1px solid #C7D2FE', borderRadius: 8, fontSize: 14, fontWeight: 700, cursor: 'pointer'
                        }}
                    >
                        <Search size={16} /> 지난달로 조회
                    </button>
                    <button
                        onClick={handleAddItem}
                        style={{
                            marginTop: 16, padding: '10px 20px', display: 'inline-flex', alignItems: 'center', gap: 6,
                            background: '#6366f1', color: '#fff', border: 'none', borderRadius: 8, fontSize: 14, fontWeight: 700, cursor: 'pointer'
                        }}
                    >
                        <Plus size={16} /> 직접 항목 추가하여 작성
                    </button>
                </div>
            )}

            {/* 의뢰서 미리보기 모달 — 2026-07-30 단가표 모달 교체 때 유실됐던 것 복원 (배포 번들 기준 재구성) */}
            {orderModal && (() => {
                const e = orderModal;
                const fmt = (v) => v ? (v.toDate ? v.toDate() : new Date(v)).toLocaleDateString('ko-KR') : '-';
                const rows = e.items && e.items.length ? e.items : [];
                const isAuto = e.source === 'agent_auto';
                return (
                    <div style={styles.modalOverlay} onClick={() => setOrderModal(null)}>
                        <div style={{ background: '#fff', borderRadius: 14, width: '92%', maxWidth: 560, maxHeight: '85vh', overflowY: 'auto', boxShadow: '0 20px 60px rgba(0,0,0,.25)' }} onClick={(ev) => ev.stopPropagation()}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '16px 20px', borderBottom: '1px solid #EEF2F6' }}>
                                <div>
                                    <div style={{ fontSize: 17, fontWeight: 800, color: '#0F172A' }}>
                                        {e.patientName || '환자미상'}
                                        {e.isRemake && <span style={{ marginLeft: 8, fontSize: 11, fontWeight: 700, background: e.remakeKind === 'repair' ? '#ECFEFF' : '#FEF2F2', color: e.remakeKind === 'repair' ? '#0E7490' : '#DC2626', padding: '2px 8px', borderRadius: 6 }}>{e.remakeKind === 'repair' ? '수리' : '리메이크'}</span>}
                                    </div>
                                    <div style={{ fontSize: 12.5, color: '#64748B', marginTop: 3 }}>
                                        {e.fromUserName || '치과'} · 의뢰서 {e.orderNumber || ''}
                                        <span style={{ marginLeft: 8, fontSize: 11, fontWeight: 700, padding: '1px 7px', borderRadius: 5, background: isAuto ? '#F4F1FF' : '#E8F6FD', color: isAuto ? '#7C3AED' : '#0369A1' }}>
                                            {isAuto ? '자동 수집' : '웹/이메일'}
                                        </span>
                                    </div>
                                </div>
                                <button onClick={() => setOrderModal(null)} style={{ border: 'none', background: 'none', cursor: 'pointer', color: '#94A3B8' }}><X size={20} /></button>
                            </div>
                            <div style={{ padding: '16px 20px' }}>
                                <div style={{ display: 'flex', gap: 20, marginBottom: 14, fontSize: 13 }}>
                                    <div><span style={{ color: '#94A3B8' }}>작성일</span> <b>{fmt(e.createdAt)}</b></div>
                                    <div><span style={{ color: '#94A3B8' }}>납기일</span> <b>{fmt(e.dueDate)}</b></div>
                                    {e.chartNumber && <div><span style={{ color: '#94A3B8' }}>차트번호</span> <b>{e.chartNumber}</b></div>}
                                </div>
                                <div style={{ fontSize: 12, fontWeight: 800, color: '#475569', margin: '0 0 8px' }}>치아 / 보철</div>
                                {rows.length ? (
                                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                                        <thead>
                                            <tr style={{ background: '#F8FAFC', color: '#64748B', fontSize: 11.5 }}>
                                                <th style={{ padding: '6px 8px', textAlign: 'left' }}>치식</th>
                                                <th style={{ padding: '6px 8px', textAlign: 'left' }}>보철</th>
                                                <th style={{ padding: '6px 8px', textAlign: 'left' }}>재료</th>
                                                <th style={{ padding: '6px 8px', textAlign: 'left' }}>임플란트</th>
                                                <th style={{ padding: '6px 8px', textAlign: 'left' }}>쉐이드</th>
                                                <th style={{ padding: '6px 8px', textAlign: 'left' }}>구분</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {rows.map((r, i) => (
                                                <tr key={i} style={{ borderTop: '1px solid #F1F5F9' }}>
                                                    <td style={{ padding: '6px 8px', fontWeight: 600 }}>{r.toothNumber || '-'}</td>
                                                    <td style={{ padding: '6px 8px' }}>{r.prosthesis || '-'}</td>
                                                    <td style={{ padding: '6px 8px', color: '#475569' }}>{r.material || '-'}</td>
                                                    <td style={{ padding: '6px 8px' }}>{r.implant
                                                        ? <span style={{ fontSize: 11, fontWeight: 700, background: '#E8F6FD', color: '#0369A1', padding: '1px 7px', borderRadius: 5 }}>{r.implant}</span>
                                                        : <span style={{ color: '#CBD5E1' }}>-</span>}</td>
                                                    <td style={{ padding: '6px 8px', color: '#475569' }}>{r.shade || '-'}</td>
                                                    <td style={{ padding: '6px 8px' }}>{r.isPontic
                                                        ? <span style={{ fontSize: 11, fontWeight: 700, background: '#FEF3C7', color: '#B45309', padding: '1px 7px', borderRadius: 5 }}>폰틱</span>
                                                        : r.isBridge
                                                            ? <span style={{ fontSize: 11, fontWeight: 700, background: '#F3E8FF', color: '#7C3AED', padding: '1px 7px', borderRadius: 5 }}>브릿지</span>
                                                            : <span style={{ color: '#CBD5E1' }}>-</span>}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                ) : (
                                    <div style={{ fontSize: 13, color: '#94A3B8' }}>치아 상세 정보가 없습니다.</div>
                                )}
                                {e.specialNotes && (
                                    <div style={{ marginTop: 14 }}>
                                        <div style={{ fontSize: 12, fontWeight: 800, color: '#475569', marginBottom: 5 }}>특이사항</div>
                                        <div style={{ fontSize: 13, color: '#334155', background: '#F8FAFC', borderRadius: 8, padding: '10px 12px', whiteSpace: 'pre-wrap', lineHeight: 1.6 }}>{e.specialNotes}</div>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                );
            })()}

            {/* 단가표 관리 모달 — 공용 컴포넌트 (2026-07-30 통일) */}
            {showPriceListModal && selectedClinic && (
                <PriceListModal
                    user={user}
                    clinicId={selectedClinic}
                    clinicName={clinics.find(c => c.id === selectedClinic)?.name || ''}
                    suggestedItems={items.map(it => ({ serviceName: it.serviceName, material: it.material }))}
                    onClose={() => setShowPriceListModal(false)}
                    onSaved={(saved) => setPriceList(normalizePriceItems(saved))}
                />
            )}

            {/* 리메이크 책임 기록 저장 실패 알림 (검수 P2-7) */}
            {remakeNoteWarn && (
                <div className="no-print" role="alert" style={{ position: 'fixed', left: '50%', bottom: 24, transform: 'translateX(-50%)', zIndex: 2100, background: '#FEF2F2', color: '#991B1B', border: '1px solid #FECACA', borderRadius: 10, padding: '10px 14px', fontSize: 13, boxShadow: '0 8px 20px rgba(0,0,0,0.12)', display: 'flex', alignItems: 'center', gap: 10, maxWidth: 'calc(100vw - 32px)' }}>
                    <span>{remakeNoteWarn}</span>
                    <button type="button" onClick={() => setRemakeNoteWarn('')} style={{ background: 'none', border: 'none', color: '#991B1B', fontSize: 16, cursor: 'pointer' }} aria-label="닫기">{'×'}</button>
                </div>
            )}

            {/* 리메이크 원 건 연결 창 (2026-10-24 2단계 ④) — 기공소 계정만 */}
            {canLinkRemake && remakeModalItemId && (() => {
                const target = items.find(it => it.id === remakeModalItemId);
                if (!target) return null;
                return (
                    <RemakeLinkModal
                        item={target}
                        origin={remakeOriginOf(target)}
                        statementNumber={savedStatementNumber || ''}
                        findOriginById={(ids) => findRemakeOriginById(target.id, ids)}
                        onPatch={(patch) => patchItem(target.id, patch)}
                        onApplyPrice={(price) => applyRemakePrice(target.id, price)}
                        onClose={() => setRemakeModalItemId(null)}
                    />
                );
            })()}
        </div>
    );
}

const styles = {
    container: {
        maxWidth: '1400px',
        margin: '0 auto',
    },
    header: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '24px',
    },
    title: {
        // 2026-08-18 디자인 통일 — 페이지 제목 계층(PageHeader 20px)과 정합. 화면(no-print) 전용, 인쇄 양식 무관
        margin: 0,
        fontSize: '20px',
        fontWeight: '700',
        color: '#0f172a',
    },
    backButton: {
        display: 'flex',
        alignItems: 'center',
        gap: '6px',
        padding: '8px 16px',
        backgroundColor: '#f1f5f9',
        border: '1px solid #e2e8f0',
        borderRadius: '8px',
        color: '#64748b',
        fontSize: '13px',
        fontWeight: '600',
        cursor: 'pointer',
    },
    searchSection: {
        backgroundColor: '#ffffff',
        borderRadius: '12px',
        padding: '24px',
        border: '1px solid #e2e8f0',
        marginBottom: '24px',
    },
    searchRow: {
        display: 'flex',
        gap: '12px',
        flexWrap: 'wrap',
        alignItems: 'flex-end',
    },
    formGroup: {
        display: 'flex',
        flexDirection: 'column',
        gap: '8px',
        minWidth: '150px',
    },
    label: {
        fontSize: '14px',
        fontWeight: '600',
        color: '#475569',
    },
    select: {
        padding: '10px 12px',
        border: '1px solid #e2e8f0',
        borderRadius: '8px',
        fontSize: '14px',
        backgroundColor: '#ffffff',
        boxSizing: 'border-box',
    },
    input: {
        padding: '10px 12px',
        border: '1px solid #e2e8f0',
        borderRadius: '8px',
        fontSize: '14px',
        boxSizing: 'border-box',
    },
    searchButton: {
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        padding: '10px 24px',
        backgroundColor: '#6366f1',
        color: 'white',
        border: 'none',
        borderRadius: '8px',
        fontSize: '14px',
        fontWeight: '600',
        cursor: 'pointer',
        whiteSpace: 'nowrap',
    },
    quickSelectRow: {
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        flexWrap: 'wrap',
        marginTop: '16px',
        paddingTop: '16px',
        borderTop: '1px solid #e2e8f0',
    },
    quickSelectLabel: {
        fontSize: '13px',
        fontWeight: '600',
        color: '#64748b',
    },
    quickButton: {
        padding: '6px 12px',
        backgroundColor: '#f8fafc',
        color: '#475569',
        border: '1px solid #e2e8f0',
        borderRadius: '6px',
        fontSize: '13px',
        fontWeight: '500',
        cursor: 'pointer',
        transition: 'all 0.2s',
    },
    tableSection: {
        backgroundColor: '#ffffff',
        borderRadius: '12px',
        padding: '24px',
        border: '1px solid #e2e8f0',
        marginBottom: '16px',
    },
    tableSectionHeader: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '16px',
    },
    sortToggle: {
        display: 'flex',
        backgroundColor: '#f1f5f9',
        borderRadius: '8px',
        padding: '2px',
        gap: '2px',
    },
    sortButtonActive: {
        padding: '6px 12px',
        backgroundColor: '#6366f1',
        color: 'white',
        border: 'none',
        borderRadius: '6px',
        fontSize: '12px',
        fontWeight: '600',
        cursor: 'pointer',
    },
    sortButtonInactive: {
        padding: '6px 12px',
        backgroundColor: 'transparent',
        color: '#64748b',
        border: 'none',
        borderRadius: '6px',
        fontSize: '12px',
        fontWeight: '500',
        cursor: 'pointer',
    },
    tableSectionTitle: {
        margin: 0,
        fontSize: '18px',
        fontWeight: '700',
        color: '#0f172a',
    },
    downloadButton: {
        display: 'flex',
        alignItems: 'center',
        gap: '6px',
        padding: '8px 16px',
        backgroundColor: '#059669',
        color: 'white',
        border: 'none',
        borderRadius: '8px',
        fontSize: '13px',
        fontWeight: '600',
        cursor: 'pointer',
    },
    uploadButton: {
        display: 'flex',
        alignItems: 'center',
        gap: '6px',
        padding: '8px 16px',
        backgroundColor: '#0ea5e9',
        color: 'white',
        border: 'none',
        borderRadius: '8px',
        fontSize: '14px',
        fontWeight: '600',
        cursor: 'pointer',
    },
    addItemButton: {
        display: 'flex',
        alignItems: 'center',
        gap: '6px',
        padding: '8px 16px',
        backgroundColor: '#10b981',
        color: 'white',
        border: 'none',
        borderRadius: '8px',
        fontSize: '13px',
        fontWeight: '600',
        cursor: 'pointer',
    },
    editAllButton: {
        display: 'flex',
        alignItems: 'center',
        gap: '6px',
        padding: '8px 16px',
        backgroundColor: '#3b82f6',
        color: 'white',
        border: 'none',
        borderRadius: '8px',
        fontSize: '13px',
        fontWeight: '600',
        cursor: 'pointer',
    },
    saveAllButton: {
        display: 'flex',
        alignItems: 'center',
        gap: '6px',
        padding: '8px 16px',
        backgroundColor: '#f59e0b',
        color: 'white',
        border: 'none',
        borderRadius: '8px',
        fontSize: '13px',
        fontWeight: '600',
        cursor: 'pointer',
    },
    tableContainer: {
        overflowX: 'auto',
        border: '1px solid #e2e8f0',
        borderRadius: '8px',
    },
    table: {
        width: '100%',
        borderCollapse: 'collapse',
        backgroundColor: '#ffffff',
    },
    tableHeader: {
        backgroundColor: '#f8fafc',
    },
    th: {
        padding: '12px',
        textAlign: 'left',
        fontSize: '12px',
        fontWeight: '700',
        color: '#475569',
        borderBottom: '2px solid #e2e8f0',
        whiteSpace: 'nowrap',
    },
    tableRow: {
        borderBottom: '1px solid #f1f5f9',
    },
    td: {
        padding: '12px',
        fontSize: '14px',
        color: '#0f172a',
        verticalAlign: 'middle',
    },
    tdCenter: {
        padding: '12px',
        fontSize: '14px',
        color: '#0f172a',
        textAlign: 'center',
        verticalAlign: 'middle',
    },
    tdRight: {
        padding: '12px',
        fontSize: '14px',
        color: '#0f172a',
        textAlign: 'right',
        verticalAlign: 'middle',
    },
    totalPriceText: {
        fontWeight: '600',
        color: '#6366f1',
    },
    priceInput: {
        width: '100%',
        padding: '6px 8px',
        border: '1px solid #e2e8f0',
        borderRadius: '6px',
        fontSize: '14px',
        boxSizing: 'border-box',
        textAlign: 'right',
    },
    countInput: {
        width: '60px',
        padding: '6px 4px',
        border: '1px solid #e2e8f0',
        borderRadius: '6px',
        fontSize: '14px',
        boxSizing: 'border-box',
        textAlign: 'center',
    },
    actionButtons: {
        display: 'flex',
        gap: '4px',
        justifyContent: 'center',
    },
    addToothButton: {
        padding: '6px',
        backgroundColor: '#dbeafe',
        color: '#2563eb',
        border: 'none',
        borderRadius: '6px',
        cursor: 'pointer',
        display: 'flex',
        alignItems: 'center',
    },
    deleteButton: {
        padding: '6px',
        backgroundColor: '#fee2e2',
        color: '#dc2626',
        border: 'none',
        borderRadius: '6px',
        cursor: 'pointer',
        display: 'flex',
        alignItems: 'center',
    },
    editInput: {
        padding: '4px 6px',
        border: '1px solid #e2e8f0',
        borderRadius: '4px',
        fontSize: '13px',
        boxSizing: 'border-box',
    },
    dateInput: {
        padding: '4px 6px',
        border: '1px solid #e2e8f0',
        borderRadius: '4px',
        fontSize: '12px',
        boxSizing: 'border-box',
        width: '100%',
    },
    toothInfoText: {
        fontSize: '14px',
        color: '#0f172a',
        lineHeight: '1.5',
    },
    toothCountBadge: {
        display: 'inline-block',
        padding: '4px 10px',
        backgroundColor: '#dbeafe',
        color: '#1d4ed8',
        borderRadius: '12px',
        fontSize: '13px',
        fontWeight: '700',
    },
    dueDateText: {
        color: '#0f172a',
        fontWeight: '500',
    },
    noDueDateText: {
        color: '#94a3b8',
        fontStyle: 'italic',
    },
    totalSection: {
        backgroundColor: '#ffffff',
        borderRadius: '12px',
        padding: '24px',
        border: '1px solid #e2e8f0',
        marginBottom: '16px',
    },
    totalRow: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        padding: '8px 0',
        fontSize: '16px',
    },
    discountRow: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        padding: '12px 16px',
        fontSize: '16px',
        backgroundColor: '#fef3c7',
        margin: '8px -24px',
        borderRadius: '8px',
    },
    totalLabel: {
        color: '#64748b',
        fontWeight: '600',
    },
    totalValue: {
        color: '#0f172a',
        fontWeight: '600',
    },
    discountValue: {
        color: '#dc2626',
        fontWeight: '600',
    },
    discountInputGroup: {
        display: 'flex',
        gap: '8px',
        alignItems: 'center',
    },
    discountInputLarge: {
        width: '150px',
        padding: '8px 12px',
        border: '2px solid #f59e0b',
        borderRadius: '8px',
        fontSize: '16px',
        fontWeight: '600',
        boxSizing: 'border-box',
        textAlign: 'right',
    },
    discountTypeSelectLarge: {
        padding: '8px 12px',
        border: '2px solid #f59e0b',
        borderRadius: '8px',
        fontSize: '16px',
        fontWeight: '600',
        backgroundColor: '#ffffff',
        cursor: 'pointer',
        boxSizing: 'border-box',
    },
    totalRowFinal: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'flex-end',
        padding: '20px 0 0 0',
        borderTop: '2px solid #e2e8f0',
        marginTop: '12px',
    },
    totalLabelFinal: {
        fontSize: '20px',
        color: '#0f172a',
        fontWeight: '700',
    },
    totalTeethInfo: {
        fontSize: '14px',
        color: '#64748b',
        marginTop: '4px',
    },
    totalValueWrapper: {
        textAlign: 'right',
    },
    totalValueFinal: {
        fontSize: '28px',
        color: '#6366f1',
        fontWeight: '700',
    },
    avgPerTooth: {
        fontSize: '13px',
        color: '#64748b',
        marginTop: '6px',
    },
    notesSection: {
        backgroundColor: '#ffffff',
        borderRadius: '12px',
        padding: '24px',
        border: '1px solid #e2e8f0',
        marginBottom: '16px',
    },
    notesLabel: {
        display: 'block',
        marginBottom: '12px',
        fontSize: '14px',
        fontWeight: '600',
        color: '#475569',
    },
    notesTextarea: {
        width: '100%',
        padding: '12px',
        border: '1px solid #e2e8f0',
        borderRadius: '8px',
        fontSize: '14px',
        minHeight: '100px',
        resize: 'vertical',
        fontFamily: 'inherit',
        boxSizing: 'border-box',
    },
    actions: {
        display: 'flex',
        gap: '12px',
        justifyContent: 'flex-end',
        marginBottom: '16px',
    },
    printButton: {
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        padding: '12px 24px',
        backgroundColor: '#f1f5f9',
        color: '#475569',
        border: 'none',
        borderRadius: '8px',
        fontSize: '14px',
        fontWeight: '600',
        cursor: 'pointer',
    },
    // (2026-08-13 P0-3) 임시저장 = 보조 버튼(회색 외곽선) — 발행과 위계를 나눠 오발행을 줄인다
    saveDraftButton: {
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        padding: '11px 20px',
        backgroundColor: '#fff',
        color: '#475569',
        border: '1.5px solid #CBD5E1',
        borderRadius: '8px',
        fontSize: '14px',
        fontWeight: '600',
        cursor: 'pointer',
        transition: 'all 0.2s',
    },
    // (2026-08-13 P0-3) 발행 = 주요 버튼(초록 채움) — 더 크고 진하게
    publishButton: {
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        padding: '13px 28px',
        backgroundColor: '#059669',
        color: 'white',
        border: 'none',
        borderRadius: '8px',
        fontSize: '15px',
        fontWeight: '800',
        cursor: 'pointer',
        transition: 'all 0.2s',
        boxShadow: '0 2px 8px rgba(5,150,105,.28)',
    },
    infoBox: {
        backgroundColor: '#f8fafc',
        borderRadius: '12px',
        padding: '16px',
        border: '1px solid #e2e8f0',
        display: 'flex',
        flexDirection: 'column',
        gap: '12px',
    },
    infoItem: {
        display: 'flex',
        alignItems: 'center',
        gap: '10px',
        fontSize: '13px',
        color: '#64748b',
    },
    emptyState: {
        textAlign: 'center',
        padding: '80px 20px',
        backgroundColor: '#ffffff',
        borderRadius: '12px',
        border: '2px dashed #e2e8f0',
    },
    emptyIcon: {
        fontSize: '64px',
        marginBottom: '16px',
    },
    emptyText: {
        fontSize: '18px',
        fontWeight: '600',
        color: '#0f172a',
        marginBottom: '8px',
    },
    emptySubtext: {
        fontSize: '14px',
        color: '#94a3b8',
    },
    priceListButton: {
        display: 'flex',
        alignItems: 'center',
        gap: '6px',
        padding: '10px 16px',
        backgroundColor: '#F5F3FF',
        color: '#7C3AED',
        border: '1px solid #DDD6FE',
        borderRadius: '8px',
        fontSize: '14px',
        fontWeight: '600',
        cursor: 'pointer',
        whiteSpace: 'nowrap',
    },
    priceListBadge: {
        background: '#7C3AED',
        color: 'white',
        borderRadius: '10px',
        padding: '1px 7px',
        fontSize: '11px',
    },
    applyPriceListButton: {
        display: 'flex',
        alignItems: 'center',
        gap: '4px',
        padding: '8px 16px',
        backgroundColor: '#7C3AED',
        color: 'white',
        border: 'none',
        borderRadius: '8px',
        fontSize: '13px',
        fontWeight: '600',
        cursor: 'pointer',
    },
    modalOverlay: {
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0,0,0,0.5)',
        zIndex: 1000,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
    },
    modalContent: {
        backgroundColor: 'white',
        borderRadius: '16px',
        padding: '28px',
        width: '520px',
        maxHeight: '80vh',
        overflowY: 'auto',
    },
    modalHeader: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '20px',
    },
    modalTitle: {
        margin: 0,
        fontSize: '18px',
        fontWeight: '700',
    },
    modalSubtitle: {
        fontSize: '14px',
        fontWeight: '400',
        color: '#64748B',
        marginLeft: '8px',
    },
    modalCloseButton: {
        background: 'none',
        border: 'none',
        cursor: 'pointer',
        color: '#94A3B8',
        padding: '4px',
    },
    priceListTable: {
        width: '100%',
        borderCollapse: 'collapse',
        marginBottom: '16px',
    },
    priceListInput: {
        width: '100%',
        padding: '8px 10px',
        borderRadius: '6px',
        border: '1px solid #E2E8F0',
        fontSize: '14px',
        boxSizing: 'border-box',
    },
    priceListPriceInput: {
        width: '100%',
        padding: '8px 10px',
        borderRadius: '6px',
        border: '1px solid #E2E8F0',
        fontSize: '14px',
        textAlign: 'right',
        boxSizing: 'border-box',
    },
    priceListAddButton: {
        display: 'flex',
        alignItems: 'center',
        gap: '4px',
        width: '100%',
        padding: '10px',
        backgroundColor: '#F8FAFC',
        color: '#64748B',
        border: '1px dashed #CBD5E1',
        borderRadius: '8px',
        cursor: 'pointer',
        fontSize: '13px',
        justifyContent: 'center',
        marginBottom: '20px',
    },
    modalActions: {
        display: 'flex',
        justifyContent: 'flex-end',
        gap: '8px',
    },
    modalCancelButton: {
        padding: '10px 20px',
        backgroundColor: '#F1F5F9',
        color: '#475569',
        border: '1px solid #E2E8F0',
        borderRadius: '8px',
        cursor: 'pointer',
        fontWeight: '600',
        fontSize: '14px',
    },
    modalSaveButton: {
        padding: '10px 20px',
        backgroundColor: '#7C3AED',
        color: 'white',
        border: 'none',
        borderRadius: '8px',
        cursor: 'pointer',
        fontWeight: '600',
        fontSize: '14px',
    },
};

export default CreateTransactionStatement;
